import { z } from 'zod';

/**
 * Canonical, vertical-neutral WorkSite contract.
 * The compatibility barrel still exposes the deployed RailHub WorkSite schema.
 */
export const CoreWorkSiteSchema = z.object({
  id: z.string().uuid(),
  code: z.string(),
  name: z.string(),
  isActive: z.boolean(),
});
export type CoreWorkSite = z.infer<typeof CoreWorkSiteSchema>;

export const CreateCoreWorkSiteSchema = z.object({
  code: z.string().min(1),
  name: z.string().min(1),
});
export type CreateCoreWorkSiteDto = z.infer<typeof CreateCoreWorkSiteSchema>;

export const UpdateCoreWorkSiteSchema = z.object({
  code: z.string().min(1).optional(),
  name: z.string().min(1).optional(),
  isActive: z.boolean().optional(),
});
export type UpdateCoreWorkSiteDto = z.infer<typeof UpdateCoreWorkSiteSchema>;

export const CoreRoleSchema = z.enum(['WORKER', 'SENIOR', 'ADMIN']);
export type CoreRole = z.infer<typeof CoreRoleSchema>;

export const CoreWorkerSchema = z.object({
  id: z.string().uuid(),
  employeeNumber: z.string(),
  name: z.string(),
  role: CoreRoleSchema,
  subdivisionId: z.string().nullable().optional(),
  canIssueWorkOrder: z.boolean(),
  isActive: z.boolean(),
});
export type CoreWorker = z.infer<typeof CoreWorkerSchema>;

export const CreateCoreWorkerSchema = z.object({
  employeeNumber: z.string().min(1),
  name: z.string().min(1),
  role: CoreRoleSchema,
  subdivisionId: z.string().uuid().nullable().optional(),
  password: z.string().min(6, 'Пароль должен быть не менее 6 символов'),
  canIssueWorkOrder: z.boolean().optional(),
});
export type CreateCoreWorkerDto = z.infer<typeof CreateCoreWorkerSchema>;

export const UpdateCoreWorkerSchema = z.object({
  name: z.string().min(1).optional(),
  role: CoreRoleSchema.optional(),
  subdivisionId: z.string().uuid().nullable().optional(),
  canIssueWorkOrder: z.boolean().optional(),
});
export type UpdateCoreWorkerDto = z.infer<typeof UpdateCoreWorkerSchema>;

export const CoreWorkFormSchema = z.enum(['NARYAD', 'RASPORYAZHENIE', 'TEKUSCHAYA']);
export const CoreWorkOrderStatusSchema = z.enum(['ISSUED', 'ADMITTED', 'IN_PROGRESS', 'CLOSED']);
export const CoreElectricalGroupSchema = z.enum(['II', 'III', 'IV', 'V']);
/// Issue #419 (карточка B-057): напряжение электроустановки, на которой ведутся
/// работы. Два значения, потому что норма другого деления не знает: пункты 33,
/// 35, 37 и 38 Правил № 222 задают пороги группы по электробезопасности
/// отдельно для работ выше 1000 В и отдельно для работ до 1000 В.
export const CoreInstallationVoltageClassSchema = z.enum(['ABOVE_1000V', 'UP_TO_1000V']);
export type CoreInstallationVoltageClass = z.infer<typeof CoreInstallationVoltageClassSchema>;
export const CoreWorkOrderMemberRoleSchema = z.enum(['MEMBER', 'OBSERVER']);
// Issue #144/#146: закрытый нормативный перечень видов инструктажа
// (ADR-002 / Правила №253). Объявлен здесь, до первого использования в
// файле (OrganizationClearanceType.briefingType, матрица требований ниже).
export const CoreBriefingTypeSchema = z.enum([
  'INTRODUCTORY',
  'WORKPLACE_INITIAL',
  'REPEATED',
  'TARGET',
  'UNSCHEDULED',
]);

export const CreateCoreWorkOrderMemberSchema = z.object({
  workerId: z.string().uuid(),
  role: CoreWorkOrderMemberRoleSchema.optional(),
});
export type CreateCoreWorkOrderMemberDto = z.infer<typeof CreateCoreWorkOrderMemberSchema>;

/**
 * Графы пункта 5 бланка наряда-допуска (приложение 1 к Правилам № 344) —
 * номер и подстрочный текст, напечатанный в бланке под графой.
 *
 * Общая константа, а не строки в экране: тот же перечень нужен серверу, чтобы
 * отвергнуть графу «5.11», и экрану, чтобы подписать поле теми же словами, что
 * стоят в бумажном бланке. Два списка разошлись бы при первой правке, и
 * человек увидел бы на экране не то, что подписывает.
 */
export const SAFETY_MEASURE_ITEMS = [
  { number: '5.1', title: 'Остановить', hint: 'место остановки, положение' },
  { number: '5.2', title: 'Отключить', hint: 'рубильник, задвижку, магистраль и т.д.' },
  { number: '5.3', title: 'Установить', hint: 'тупики, заглушки, сигнальные лампы и т.д.' },
  {
    number: '5.4',
    title: 'Взять пробу для анализа воздушной среды',
    hint: 'указать места и результат анализа, группу загазованности',
  },
  { number: '5.5', title: 'Оградить', hint: 'зону работ, вывесить плакаты' },
  {
    number: '5.6',
    title: 'Предусмотреть меры безопасности при работе на высоте и в колодцах',
    hint: 'леса, предохранительные пояса, верёвки и т.д.',
  },
  {
    number: '5.7',
    title: 'Предупредить',
    hint: 'машинистов соседних кранов и кранов смежных пролётов с подписью в вахтенном журнале',
  },
  {
    number: '5.8',
    title: 'Предусмотреть меры безопасности у железнодорожных путей',
    hint: 'установить знаки, плакаты, ограждения, тупики и др.',
  },
  { number: '5.9', title: 'Указать маршруты к месту работы', hint: 'при необходимости приложить схему' },
  { number: '5.10', title: 'Дополнительные мероприятия', hint: '' },
] as const;

export const SAFETY_MEASURE_ITEM_NUMBERS = SAFETY_MEASURE_ITEMS.map((i) => i.number);

export const SafetyMeasureItemNumberEnum = z.enum([
  '5.1', '5.2', '5.3', '5.4', '5.5', '5.6', '5.7', '5.8', '5.9', '5.10',
]);
export type SafetyMeasureItemNumber = z.infer<typeof SafetyMeasureItemNumberEnum>;

/**
 * Уровень приоритетности меры контроля — пункт 24 Правил управления
 * профессиональными рисками (приказ МТСЗН РК от 11.09.2020 № 363 в редакции
 * приказа от 28.05.2026 № 211). Issue #329, Э3-2.
 *
 * Порядок значений — порядок пункта 24, от самого действенного к последнему
 * средству: устранить опасность → заменить на менее опасное → изолировать
 * инженерными и коллективными средствами → административные меры → СИЗ.
 *
 * Перечень закрыт самой нормой, поэтому он enum, а не справочник организации:
 * шестого уровня у пункта 24 нет, и редактируемый справочник позволил бы его
 * завести. Тот же довод, по которому enum'ом сделан `BriefingType`
 * (оговорка ADR-002 §4 о нормативных величинах).
 */
export const RiskControlHierarchyLevelSchema = z.enum([
  'HAZARD_ELIMINATION',
  'SUBSTITUTION',
  'ENGINEERING_CONTROLS',
  'ADMINISTRATIVE_CONTROLS',
  'PPE',
]);
export type RiskControlHierarchyLevel = z.infer<typeof RiskControlHierarchyLevelSchema>;

/**
 * Одна заполненная графа пункта 5.
 *
 * Незаполненные графы сюда НЕ попадают: по пункту 5 Правил в них ставится
 * прочерк, а прочерк — это отсутствие строки, а не строка с пустым текстом.
 * Иначе пустая графа требовала бы подписи наравне с заполненной (пункт 7).
 */
export const CreateWorkOrderSafetyMeasureSchema = z.object({
  itemNumber: SafetyMeasureItemNumberEnum,
  content: z.string().trim().min(1).max(2000),
  /// Issue #329 (Э3-2): уровень иерархии меры по пункту 24 Правил № 363.
  /// Необязателен: бланк № 344 уровня не спрашивает, и наряд прежней формы
  /// выписывается как раньше. Не прислан — уровень не назван, а не «СИЗ».
  ///
  /// В канон документа выдачи НЕ входит: перечень мероприятий подписывается,
  /// и новое поле в каноне изменило бы байты уже подписанных документов
  /// (ADR-004 §5).
  hierarchyLevel: RiskControlHierarchyLevelSchema.nullable().optional(),
  /// Назначенный исполнитель. Необязателен: пункт 19 Правил поручает выдающему
  /// определить МЕРЫ, а пункт 21 возлагает их выполнение на допускающего — кто
  /// физически выполнил меру, к моменту выписки известно не всегда.
  executorUserId: z.string().uuid().nullable().optional(),
});
export type CreateWorkOrderSafetyMeasureDto = z.infer<typeof CreateWorkOrderSafetyMeasureSchema>;

/**
 * Одно согласование с обслуживающей организацией — пункт 8 Правил № 344
 * (issue #468, карточка B-034).
 *
 * Пункт 8 требует предварительно согласовать работы вблизи действующих дорог,
 * ЛЭП, газо- и теплопроводов, других коммуникаций, а также ВСЕ земляные работы
 * с организациями, обслуживающими эти объекты; пункт 10 — чтобы согласовавшие
 * подписали наряд до начала работы.
 *
 * Записей бывает несколько: работа у автодороги и у кабеля связи разом — две
 * организации и два согласовавших.
 *
 * Наименование организации свободным текстом, а не ссылкой на справочник:
 * обслуживающая организация чужая, в справочниках дистанции её нет и быть не
 * должно.
 *
 * ПОЧЕМУ СХЕМА НЕ ТРЕБУЕТ НЕПУСТОТЫ И ФОРМАТА ДАТЫ. Отказ схемы — это
 * «Validation failed» с перечнем путей полей, и выдающему он не говорит
 * ничего. Полноту реквизитов и разбор даты проверяет СЕРВИС и отказывает
 * словами (решение владельца 25.09.2026: «отказ сервера словами»). Схема здесь
 * держит только длины — то, что дальше упёрлось бы в тип колонки.
 */
// ─── Issue #469 (карточка B-034, шаг 3): приложение к согласованию ───────────
//
// Пункт 8 Правил № 344: «соответствующие документы (схемы, коммуникации) в
// случае необходимости прилагаются к наряду-допуску». Пункт 300 Правил № 222:
// земляные работы в охранных зонах подземных коммуникаций «начинаются с
// письменного разрешения», и «к разрешению прилагается план (схема) с указанием
// размещения и глубины заложения коммуникаций».

/**
 * Потолок размера одного приложения — 10 МБ (решение владельца 25.09.2026).
 *
 * Файл лежит в самой базе, поэтому потолок не формальность: он и определяет,
 * сколько весит резервная копия. Сканы разрешения и схемы в него укладываются с
 * запасом, а десятимегабайтная фотография — уже не документ, а неудачный выбор
 * камеры.
 */
export const SAFETY_APPROVAL_ATTACHMENT_MAX_BYTES = 10 * 1024 * 1024;

/**
 * Что можно приложить: PDF или изображение — скан письменного разрешения, схема.
 *
 * Перечень закрыт, потому что файл потом ОТКРЫВАЮТ: панель и телефон выбирают
 * способ показа по этому полю, и тип, который нечем открыть, означал бы, что
 * приложено не то, что предъявляют. Тот же перечень держит CHECK в базе — два
 * места, но одно решение, и расхождение между ними падает тестом.
 */
export const SAFETY_APPROVAL_ATTACHMENT_MIME_TYPES = [
  'application/pdf',
  'image/jpeg',
  'image/png',
] as const;
export type SafetyApprovalAttachmentMimeType =
  (typeof SAFETY_APPROVAL_ATTACHMENT_MIME_TYPES)[number];

/** Сколько файлов можно приложить к одному согласованию. */
export const SAFETY_APPROVAL_ATTACHMENT_MAX_COUNT = 5;

/**
 * Потолок ТЕЛА запроса загрузки — 16 МБ. Это не второй предел размера файла, а
 * граница, за которой отказывает транспорт, а не правило.
 *
 * Он заведомо выше десяти мегабайт, и намеренно. Base64 длиннее самих байтов на
 * треть, и если бы тело обрывалось ровно на пределе файла, человек с файлом
 * 10,5 МБ получил бы обрыв соединения вместо ответа «файл больше предела».
 * Отказ по существу обязан прийти СЛОВАМИ, а слова умеет сказать только
 * сервис — значит запрос должен до него доехать.
 *
 * Выше этого — уже не неудачный скан, а злоупотребление, и там обрыв уместен.
 */
export const SAFETY_APPROVAL_ATTACHMENT_REQUEST_MAX_BYTES = 16 * 1024 * 1024;

/**
 * Загрузка файла: тело запроса `POST /api/safety-approval-attachments`.
 *
 * ПОЧЕМУ BASE64, А НЕ MULTIPART. Весь обмен в системе — JSON со схемой, и
 * разбор тела один на все маршруты. Ради одного маршрута заводить второй разбор
 * значило бы завести и второе место, где проверяются размер, тип и права.
 * Плата известна: base64 на треть длиннее самих байтов, и потолок тела запроса
 * поднят ровно настолько (`apps/api/src/common/body-limit.ts`).
 *
 * ПОЧЕМУ ЗАГРУЗКА ОТДЕЛЬНЫМ ШАГОМ, А НЕ ПОЛЕМ В ВЫПИСКЕ. Наряд выписывается
 * одним запросом, и файл в его теле означал бы, что каждая неудачная выписка
 * гонит десять мегабайт заново. Здесь файл загружается один раз, а выписка
 * называет его идентификатором.
 *
 * Длина строки здесь ограничена ТРАНСПОРТНЫМ потолком, а не пределом файла:
 * отказ схемы — это «Validation failed» с путём поля, и выдающему он не говорит
 * ничего. Предел в 10 МБ проверяет СЕРВИС по раскодированным байтам и
 * отказывает словами.
 */
export const UploadSafetyApprovalAttachmentSchema = z.object({
  fileName: z.string().max(255),
  mimeType: z.enum(SAFETY_APPROVAL_ATTACHMENT_MIME_TYPES),
  contentBase64: z.string().max(SAFETY_APPROVAL_ATTACHMENT_REQUEST_MAX_BYTES),
});
export type UploadSafetyApprovalAttachmentDto = z.infer<
  typeof UploadSafetyApprovalAttachmentSchema
>;

/**
 * Приложение, как его видит человек: имя файла, кто и когда приложил,
 * контрольная сумма. Ровно те три вещи, которые назвал владелец.
 *
 * Байтов здесь нет: список карточки наряда их не грузит. Содержимое отдаёт
 * отдельный адрес — `GET /api/safety-approval-attachments/:id/content`.
 */
export const SafetyApprovalAttachmentSchema = z.object({
  id: z.string(),
  fileName: z.string(),
  mimeType: z.string(),
  byteSize: z.number(),
  /** «sha256:<64 шестнадцатеричных>» — с меткой алгоритма, как хеш документа. */
  checksum: z.string(),
  uploadedAt: z.string(),
  uploadedBy: z.object({ userId: z.string(), name: z.string().nullable() }),
  /** Привязано ли приложение к выписанному наряду. `false` — черновик формы. */
  attached: z.boolean(),
});
export type SafetyApprovalAttachment = z.infer<typeof SafetyApprovalAttachmentSchema>;

/** Содержимое одного приложения: те же реквизиты плюс сами байты в base64. */
export const SafetyApprovalAttachmentContentSchema = SafetyApprovalAttachmentSchema.extend({
  contentBase64: z.string(),
});
export type SafetyApprovalAttachmentContent = z.infer<
  typeof SafetyApprovalAttachmentContentSchema
>;

/**
 * Issue #531: отметка «подписано на бумаге» — фото страницы бумажного журнала
 * инструктажа (решение Штаба 29.09.2026). Работник без смартфона расписывается
 * в журнале; инструктирующий фотографирует страницу и отмечает на ней одного
 * или нескольких работников. Файл — по тем же правилам, что приложения к
 * согласованиям: тип закрыт перечнем, предел 10 МБ проверяет сервис.
 *
 * Подписи личным кодом на устройстве мастера здесь нет и быть не должно: это
 * не ЭЦП (решение Штаба).
 */
export const UploadBriefingPaperMarkSchema = UploadSafetyApprovalAttachmentSchema.extend({
  /// Строки бригады (`WorkOrderMember.id`), чьи подписи на этой странице.
  memberIds: z.array(z.string().uuid()).min(1).max(50),
});
export type UploadBriefingPaperMarkDto = z.infer<typeof UploadBriefingPaperMarkSchema>;

export const BriefingPaperMarkSchema = z.object({
  id: z.string(),
  /** Пусто у фото инструктажа без наряда (#543). */
  workOrderId: z.string().nullable(),
  /// День инструктажа, `YYYY-MM-DD`.
  briefingDay: z.string(),
  fileName: z.string(),
  mimeType: z.string(),
  byteSize: z.number(),
  checksum: z.string(),
  uploadedAt: z.string(),
  uploadedBy: z.object({ userId: z.string(), name: z.string().nullable() }),
  memberIds: z.array(z.string()),
  /** Сколько страниц журнала на отметке; нет — одна (отметка наряда, #531). */
  pageCount: z.number().int().min(1).optional(),
});
export type BriefingPaperMark = z.infer<typeof BriefingPaperMarkSchema>;

export const BriefingPaperMarkContentSchema = BriefingPaperMarkSchema.extend({
  contentBase64: z.string(),
});
export type BriefingPaperMarkContent = z.infer<typeof BriefingPaperMarkContentSchema>;

// ─── Журнал регистрации инструктажа (issue #531 → #533) ──────────────────────
// Приложение 17 Правил № 1019 в ред. приказа № 223. Журнал — не отдельный
// ввод, а та же запись, что в наряде: строки строятся из подписанных
// документов проведения, подписей работников и фото бумажного журнала.

/** День журнала — `YYYY-MM-DD` по времени Казахстана, как `briefingDay`. */
const BriefingJournalDaySchema = z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'Дата в формате ГГГГ-ММ-ДД');

export const BriefingJournalQuerySchema = z
  .object({
    from: BriefingJournalDaySchema,
    to: BriefingJournalDaySchema,
    subdivisionId: z.string().uuid().optional(),
  })
  .refine((q) => q.from <= q.to, { message: 'Начало периода позже конца', path: ['from'] })
  .refine((q) => (Date.parse(`${q.to}T00:00:00Z`) - Date.parse(`${q.from}T00:00:00Z`)) / 86_400_000 < 366, {
    message: 'Период журнала — не больше года',
    path: ['to'],
  });
export type BriefingJournalQuery = z.infer<typeof BriefingJournalQuerySchema>;

/**
 * Как поставлена подпись в графе 9 или 10.
 *
 * - `QUALIFIED_ECP` — ЭЦП;
 * - `DEVICE_KEY` — ключ устройства, **без ЭЦП**;
 * - `PAPER_JOURNAL` — подпись на бумаге, в системе фото страницы (графа 10);
 * - `UNSIGNED_MARK` — проведение отмечено без подписи (до #531 и в городском
 *   контуре; графа 9);
 * - `PROXY_WITHDRAWN` — прежняя отметка «за работника», способ отменён
 *   (графа 10).
 */
export const BRIEFING_JOURNAL_SIGNATURE_METHODS = [
  'QUALIFIED_ECP',
  'DEVICE_KEY',
  'PAPER_JOURNAL',
  'UNSIGNED_MARK',
  'PROXY_WITHDRAWN',
] as const;
export const BriefingJournalSignatureMethodSchema = z.enum(BRIEFING_JOURNAL_SIGNATURE_METHODS);
export type BriefingJournalSignatureMethod = z.infer<typeof BriefingJournalSignatureMethodSchema>;

export const BriefingJournalSignatureSchema = z.object({
  method: BriefingJournalSignatureMethodSchema,
  /** Юридически значимая подпись (ЭЦП). Всё прочее выводится «без ЭЦП». */
  qualified: z.boolean(),
  at: z.string().datetime().nullable(),
  documentId: z.string().uuid().nullable(),
  paperMarkId: z.string().uuid().nullable(),
  /** Кто отметил бумажную подпись или отметку «за работника». */
  recordedByName: z.string().nullable(),
});
export type BriefingJournalSignature = z.infer<typeof BriefingJournalSignatureSchema>;

/**
 * Графа 5: вид инструктажа. Целевой — по наряду (#531); первичный на рабочем
 * месте, повторный и внеплановый — без наряда, записью журнала (#543).
 */
export const BRIEFING_JOURNAL_KINDS = ['TARGET', 'WORKPLACE_INITIAL', 'REPEATED', 'UNSCHEDULED'] as const;
export const BriefingJournalKindSchema = z.enum(BRIEFING_JOURNAL_KINDS);
export type BriefingJournalKind = z.infer<typeof BriefingJournalKindSchema>;

export const BriefingJournalRowSchema = z.object({
  /** Графа 1: день инструктажа. */
  date: BriefingJournalDaySchema,
  /** Наряд целевого инструктажа; у инструктажа без наряда (#543) пусто. */
  workOrderId: z.string().uuid().nullable(),
  workOrderNumber: z.string().nullable(),
  /** Номер записи журнала у инструктажа без наряда (#543); у целевого пусто. */
  journalNumber: z.number().int().nullable(),
  subdivisionId: z.string().uuid().nullable(),
  briefedUserId: z.string().uuid(),
  /** Графа 2. */
  briefedName: z.string(),
  /** Графа 3: из карточки работника, ИИН не расшифровывается. */
  birthYear: z.number().int().nullable(),
  /** Графа 4: «профессия, должность» из того, что заполнено. */
  professionAndPosition: z.string().nullable(),
  /** Графа 5. */
  briefingKind: BriefingJournalKindSchema,
  /** Графа 6: причина внепланового (п. 66); у остальных пусто. */
  unscheduledReason: z.string().nullable(),
  conductorUserId: z.string().uuid().nullable(),
  /** Графа 7: «Фамилия И. О., должность». */
  conductorLabel: z.string().nullable(),
  /** Графа 8: `null` — отметка до #531, проверка знаний не записывалась. */
  knowledgePassed: z.boolean().nullable(),
  /** Графа 9: подпись инструктирующего. */
  conductorSignature: BriefingJournalSignatureSchema,
  /** Графа 10: подпись инструктируемого; `null` — не расписался. */
  workerSignature: BriefingJournalSignatureSchema.nullable(),
  /**
   * Стенд 30.09.2026: наряд выдан без ЭЦП выдающего, и подписи инструктажа
   * опираются на зафиксированный, но не подписанный документ выдачи. В
   * журнале — пометка «наряд выдан без ЭЦП». Нет поля (старый сервер) — нет
   * пометки.
   */
  issueUnsigned: z.boolean().optional(),
});
export type BriefingJournalRow = z.infer<typeof BriefingJournalRowSchema>;

/**
 * Фото бумажного журнала из строки журнала инструктажа — для открытия на
 * экране раздела. Страницы по порядку; первая — сама отметка.
 */
export const BriefingJournalPaperMarkSchema = z.object({
  id: z.string().uuid(),
  briefingDay: BriefingJournalDaySchema,
  uploadedAt: z.string().datetime(),
  uploadedByName: z.string().nullable(),
  pages: z.array(
    z.object({
      pageNumber: z.number().int().min(1),
      mimeType: z.string(),
      contentBase64: z.string(),
    }),
  ),
});
export type BriefingJournalPaperMark = z.infer<typeof BriefingJournalPaperMarkSchema>;

export const BriefingJournalResponseSchema = z.object({
  organizationName: z.string(),
  /** «Начат» — первый день периода. */
  from: BriefingJournalDaySchema,
  /** «Окончен» — последний день периода. */
  to: BriefingJournalDaySchema,
  rows: z.array(BriefingJournalRowSchema),
});
export type BriefingJournalResponse = z.infer<typeof BriefingJournalResponseSchema>;

// ─── Инструктаж без наряда (issue #543, ADR-008 §8a) ─────────────────────────
// Первичный на рабочем месте, повторный и внеплановый инструктаж проводит
// мастер или непосредственный руководитель работника в приложении. Запись
// журнала — один документ, две подписи ключом телефона: инструктирующего (с
// «прошёл / не прошёл») и работника. Работник без смартфона — фото бумажного
// журнала, как у целевого. Запись с «прошёл» сама создаёт или продлевает
// допуск: повторный — на шесть месяцев от даты инструктажа (п. 62), первичный
// — бессрочно, внеплановый — с причиной (п. 66).

export const WORKER_BRIEFING_KINDS = ['WORKPLACE_INITIAL', 'REPEATED', 'UNSCHEDULED'] as const;
export const WorkerBriefingKindSchema = z.enum(WORKER_BRIEFING_KINDS);
export type WorkerBriefingKind = z.infer<typeof WorkerBriefingKindSchema>;

/**
 * Проведение инструктажа одному работнику. Причину внепланового и её пустоту
 * бракует сервер — отказ приходит человеческой фразой, а не строкой
 * валидатора.
 */
export const CreateWorkerBriefingSchema = z.object({
  workerId: z.string().uuid(),
  kind: WorkerBriefingKindSchema,
  unscheduledReason: z.string().max(1000).nullable().optional(),
  knowledgeCheck: z.enum(['PASSED', 'FAILED']),
});
export type CreateWorkerBriefingDto = z.infer<typeof CreateWorkerBriefingSchema>;

/** Подпись ключом телефона над байтами, которые отдал сервер. */
export const SignWorkerBriefingSchema = z.object({
  deviceId: z.string().min(1),
  signature: z.string().min(1),
  unlockMethod: z.enum(['BIOMETRIC', 'DEVICE_CREDENTIAL']),
});
export type SignWorkerBriefingDto = z.infer<typeof SignWorkerBriefingSchema>;

/**
 * Состояние записи:
 * - `AWAITING_INSTRUCTOR` — заведена, инструктирующий ещё не подписал;
 * - `AWAITING_WORKER` — «прошёл», ждёт подписи работника или фото журнала;
 * - `COMPLETE` — «прошёл», обе подписи (или фото) есть, допуск создан;
 * - `FAILED_CHECK` — «не прошёл»: подписана инструктирующим, допуска нет.
 */
export const WorkerBriefingStateSchema = z.enum(['AWAITING_INSTRUCTOR', 'AWAITING_WORKER', 'COMPLETE', 'FAILED_CHECK']);
export type WorkerBriefingState = z.infer<typeof WorkerBriefingStateSchema>;

export const WorkerBriefingSchema = z.object({
  id: z.string().uuid(),
  /** Номер записи журнала; есть с подписи инструктирующего. */
  journalNumber: z.number().int().nullable(),
  kind: WorkerBriefingKindSchema,
  unscheduledReason: z.string().nullable(),
  /** День инструктажа, `YYYY-MM-DD`. От него считается срок допуска. */
  briefingDay: z.string(),
  knowledgePassed: z.boolean(),
  worker: z.object({
    userId: z.string().uuid(),
    name: z.string(),
    employeeNumber: z.string().nullable(),
    /** Профессия с разрядом и должность — графа 4 журнала; не заполнено — `null`. */
    position: z.string().nullable().optional(),
  }),
  conductor: z.object({ userId: z.string().uuid(), name: z.string() }),
  state: WorkerBriefingStateSchema,
  /** Подпись работника своим ключом; `null` — нет. */
  workerSignedAt: z.string().nullable(),
  /** Фото бумажного журнала вместо подписи работника. */
  paperMarkId: z.string().nullable(),
  /** Допуск, созданный или продлённый этой записью. */
  clearance: z
    .object({ id: z.string().uuid(), documentNumber: z.string(), expiresAt: z.string().nullable() })
    .nullable(),
});
export type WorkerBriefing = z.infer<typeof WorkerBriefingSchema>;

/** Что подписать: запись и её байты. */
export const WorkerBriefingSigningRequestSchema = z.object({
  briefing: WorkerBriefingSchema,
  documentId: z.string().uuid(),
  canonicalJson: z.string(),
  hash: z.string(),
});
export type WorkerBriefingSigningRequest = z.infer<typeof WorkerBriefingSigningRequestSchema>;

/** Кого актор вправе инструктировать без наряда. */
export const WorkerBriefingCandidateSchema = z.object({
  userId: z.string().uuid(),
  name: z.string(),
  employeeNumber: z.string().nullable(),
  subdivisionName: z.string().nullable(),
  /**
   * Есть ли у работника действующий ключ подписи на телефоне: «подпишет в
   * телефоне» или «нет телефона» — тогда фото бумажного журнала. Нет поля
   * (прежний сервер) — неизвестно, экран ничего не обещает.
   */
  hasSigningDevice: z.boolean().optional(),
});
export type WorkerBriefingCandidate = z.infer<typeof WorkerBriefingCandidateSchema>;

/**
 * «Кому пора»: работник охвата руководителя, у которого повторный инструктаж
 * просрочен или истекает в пределах порога вида «повторный». Срок — тем же
 * правилом, что у допуска (PR #586): последний повторный + 6 мес. либо
 * первичный + 6 мес., берётся самый поздний.
 */
export const WorkerBriefingDueSchema = WorkerBriefingCandidateSchema.extend({
  /** До какого дня действует повторный, `YYYY-MM-DD`. */
  dueDay: z.string(),
  /** Дней до срока; отрицательное — просрочен на столько дней. */
  daysLeft: z.number().int(),
  status: z.enum(['expired', 'expiring_soon']),
});
export type WorkerBriefingDue = z.infer<typeof WorkerBriefingDueSchema>;

/** Следующий повторный инструктаж самого работника — по тому же правилу. */
export const WorkerBriefingNextRepeatSchema = z.object({
  /** `YYYY-MM-DD`; `null` — повторного и первичного нет либо срок бессрочный. */
  dueDay: z.string().nullable(),
  daysLeft: z.number().int().nullable(),
  status: z.enum(['active', 'expiring_soon', 'expired', 'missing']),
  /** От чего считан срок: свой повторный или зачёт первичного. */
  basis: z.enum(['REPEATED', 'WORKPLACE_INITIAL']).nullable(),
});
export type WorkerBriefingNextRepeat = z.infer<typeof WorkerBriefingNextRepeatSchema>;

/** Счётчик вкладки «Инструктаж». */
export const WorkerBriefingSummarySchema = z.object({
  /** Записей, ждущих подписи самого человека. */
  pendingForMe: z.number().int(),
  /** Есть ли кому проводить инструктаж (правила PR #589). */
  canConduct: z.boolean(),
  /** Людей охвата, кому пора на повторный. */
  dueCount: z.number().int(),
});
export type WorkerBriefingSummary = z.infer<typeof WorkerBriefingSummarySchema>;

/**
 * Сколько страниц бумажного журнала уходит одной отметкой. Отметка — одним
 * запросом, и его тело ограничено транспортным потолком
 * (`SAFETY_APPROVAL_ATTACHMENT_REQUEST_MAX_BYTES`): снимок камеры — два-три
 * мегабайта в base64, пять страниц в потолок укладываются.
 */
export const BRIEFING_PAPER_MARK_MAX_PAGES = 5;

/**
 * Фото страницы бумажного журнала за одну или несколько записей.
 *
 * Страниц может быть несколько: работники одной отметки расписались на разных
 * страницах журнала. Первая страница — поля самой отметки, остальные —
 * `extraPages` по порядку съёмки. Все страницы — одним запросом: отметка либо
 * ложится со всеми страницами, либо не ложится вовсе.
 */
export const UploadWorkerBriefingPaperMarkSchema = UploadSafetyApprovalAttachmentSchema.extend({
  briefingIds: z.array(z.string().uuid()).min(1).max(50),
  extraPages: z.array(UploadSafetyApprovalAttachmentSchema).max(BRIEFING_PAPER_MARK_MAX_PAGES - 1).optional(),
});
export type UploadWorkerBriefingPaperMarkDto = z.infer<typeof UploadWorkerBriefingPaperMarkSchema>;

export const CreateWorkOrderSafetyApprovalSchema = z.object({
  /// С кем согласовано — наименование обслуживающей организации.
  organizationName: z.string().max(255),
  /// Кто согласовал: ФИО и должность. Должность обязательна наравне с
  /// фамилией — по пункту 10 согласовавший подписывает наряд, и при разборе
  /// спросят, чем подтверждены его полномочия.
  representativeName: z.string().max(255),
  representativePosition: z.string().max(255),
  /// ИИН согласовавшего (issue #471, карточка B-054). Необязателен: у
  /// согласований, записанных до этой задачи, его никто не спрашивал, и
  /// требовать его схемой значило бы перестать принимать наряды от
  /// установленных сборок.
  ///
  /// Без него подпись графы 8.1 собрать НЕЛЬЗЯ: ссылка, пересланная не тому
  /// человеку, дала бы чужую подпись под согласованием, и сервер отказывает
  /// словами в момент выдачи ссылки. Внести его можно и позже — пока графа 8.1
  /// не подписана.
  ///
  /// Наружу ИИН не отдаётся никогда и ни в каком виде: он служит только сверке
  /// подписанта (ADR-004 §12).
  representativeIin: z.string().max(40).nullable().optional(),
  /// Дата согласования в формате YYYY-MM-DD. Позже момента выписки она быть не
  /// может — по пункту 8 согласование ПРЕДВАРИТЕЛЬНОЕ; это проверяет сервер.
  approvedAt: z.string().max(40),
  /// Номер и дата письменного разрешения, если оно выдано. Необязательны, но
  /// ходят парой: «разрешение № 14» без даты и дата без номера — записи, по
  /// которым документ не найти.
  permitNumber: z.string().max(100).nullable().optional(),
  permitIssuedAt: z.string().max(40).nullable().optional(),
  /// Приложенные к этому согласованию файлы (issue #469): идентификаторы уже
  /// загруженных через `POST /api/safety-approval-attachments`. Земляные
  /// работы в охранной зоне без хотя бы одного приложения не выписываются —
  /// отказывает СЕРВЕР и отказывает словами; схема этого потребовать не может,
  /// потому что признак охранной зоны берётся из справочника видов работ, когда
  /// выдающий его не трогал.
  attachmentIds: z.array(z.string()).max(SAFETY_APPROVAL_ATTACHMENT_MAX_COUNT).optional(),
});
export type CreateWorkOrderSafetyApprovalDto = z.infer<typeof CreateWorkOrderSafetyApprovalSchema>;

export const CreateCoreWorkOrderSchema = z.object({
  // Номер ставит сервер при выдаче по порядку журнала организации (решение
  // владельца 25.09.2026) и присланный не читает. Поле оставлено
  // необязательным ради установленных сборок, которые его ещё присылают.
  number: z.string().min(1).optional(),
  workType: z.string().min(1),
  safetyMeasures: z.string().min(1),
  subdivisionId: z.string().uuid(),
  workSiteId: z.string().uuid(),
  workSiteDetail: z.string().optional(),
  requiredElectricalGroup: CoreElectricalGroupSchema.nullable().optional(),
  /// Issue #419 (карточка B-057): напряжение электроустановки. Опущено —
  /// берётся умолчание строки матрицы требований для выбранного вида работы;
  /// прислано — решает выдающий наряд. Ни то, ни другое не задано — наряд
  /// выписывается с пустым напряжением, ровно как до задачи.
  voltageClass: CoreInstallationVoltageClassSchema.nullable().optional(),
  // Issue #146: ссылка на справочник видов работ (#144). Опционально —
  // легаси-клиенты передают только `workType` (строку). Сервер проверяет,
  // что запись принадлежит организации actor'а.
  workTypeCatalogId: z.string().uuid().nullable().optional(),
  // ADR-003 (issue #176): нормативные роли наряда разведены. `supervisorId`
  // остаётся обязательным входом для совместимости с уже выпущенными
  // клиентами и трактуется как ответственный руководитель работ.
  //   responsibleUserId — ответственный руководитель работ; если не задан,
  //     берётся supervisorId.
  //   admittingUserId — допускающий; если не задан, по умолчанию совпадает с
  //     ответственным руководителем (решение владельца от 2026-09-02).
  responsibleUserId: z.string().uuid().optional(),
  admittingUserId: z.string().uuid().optional(),
  /// Issue #326 (план Э1-4): допускающие СВЕРХ названного в admittingUserId.
  /// Пункт 9 бланка № 344 числа допускающих не называет: работы на
  /// нескольких участках допускает несколько человек, каждый на своём.
  ///
  /// Опущено или пусто — наряд с одним допускающим, и он ведёт себя ровно
  /// как до задачи, включая версию 1 канона документа выдачи. Прислан хоть
  /// один — документ выдачи собирается версией 2, где вместо одного
  /// допускающего стоит их перечень.
  additionalAdmittingUserIds: z.array(z.string().uuid()).optional(),
  supervisorId: z.string().uuid(),
  producerId: z.string().uuid(),
  members: z.array(CreateCoreWorkOrderMemberSchema).min(1),
  /// Графы 5.1–5.10 бланка № 344. Необязательно: наряд прежней формы
  /// выписывается как сейчас. Незаполненные графы сюда не попадают — по
  /// пункту 5 Правил в них прочерк, а прочерк это отсутствие строки.
  safetyMeasureItems: z.array(CreateWorkOrderSafetyMeasureSchema).max(10).optional(),
  /// Пункт 8 Правил № 344. Опущено — берётся умолчание справочника видов
  /// работ; прислано — решает выдающий.
  requiresSafetyApproval: z.boolean().optional(),
  /// Реквизиты согласования по пункту 8 (issue #468). Наряд, которому
  /// согласование требуется, без единой записи не выписывается — отказывает
  /// СЕРВЕР, и здесь перечень необязателен намеренно: схема не знает, требуется
  /// ли согласование этому наряду, потому что признак берётся из справочника
  /// видов работ, когда выдающий его не трогал.
  safetyApprovals: z.array(CreateWorkOrderSafetyApprovalSchema).max(10).optional(),
  /// Пункт 300 Правил № 222 (issue #469): земляные работы в охранной зоне
  /// подземных коммуникаций. Опущено — берётся умолчание справочника видов
  /// работ; прислано — решает выдающий, и снятие предложенного записывается
  /// вместе с тем, кто снял.
  excavationInProtectedZone: z.boolean().optional(),
});
export type CreateCoreWorkOrderDto = z.infer<typeof CreateCoreWorkOrderSchema>;

// ─── Черновик выписки: форма выдающего → тело POST /work-orders ──────────────
//
// Наряд выписывают из двух мест — с телефона на месте работ и из веб-панели
// за столом (issue #446). Наряд от этого обязан получиться один и тот же,
// поэтому сборка тела запроса живёт здесь, в общих контрактах, а не по копии
// в каждом клиенте. Две копии разошлись бы при первой правке одной из них —
// и разошлись бы молча: оба экрана продолжали бы выписывать наряды, только
// немного разные.
//
// ЗДЕСЬ НЕТ НИ ОДНОЙ ПРОВЕРКИ ПО СУЩЕСТВУ. Требуемая группа, состав по
// матрице, наблюдающий, напряжение, согласование, полномочия названных лиц —
// считает и отвергает СЕРВЕР. Ниже только перевод черновика формы в поля
// запроса: что уходит, что опускается и в каком порядке.

/** Черновик одной графы пункта 5 в форме: текст и (необязательно) исполнитель. */
export type SafetyMeasureDraft = {
  content: string;
  executorUserId: string | null;
};

export type SafetyMeasureDrafts = Record<SafetyMeasureItemNumber, SafetyMeasureDraft>;

/** Пустой черновик на все десять граф: в бланке незаполненная графа — прочерк. */
export function emptySafetyMeasureDrafts(): SafetyMeasureDrafts {
  const out = {} as SafetyMeasureDrafts;
  for (const item of SAFETY_MEASURE_ITEMS) out[item.number] = { content: '', executorUserId: null };
  return out;
}

/**
 * Черновик → тело запроса.
 *
 * Пустые графы НЕ попадают: прочерк в бланке — это отсутствие мероприятия, а
 * не мероприятие с пустым текстом. Иначе пустая графа требовала бы подписи
 * исполнителя наравне с заполненной (пункт 7 бланка).
 *
 * Порядок — как в бланке (5.1 … 5.10), а не как пользователь их трогал:
 * перечень обязательных подписей собирается из содержания наряда, и порядок
 * граф в нём не должен зависеть от того, с какой начал выдающий.
 */
export function buildSafetyMeasureItems(drafts: SafetyMeasureDrafts): CreateWorkOrderSafetyMeasureDto[] {
  const out: CreateWorkOrderSafetyMeasureDto[] = [];
  for (const item of SAFETY_MEASURE_ITEMS) {
    const draft = drafts[item.number];
    const content = draft?.content.trim() ?? '';
    if (!content) continue;
    out.push({
      itemNumber: item.number,
      content,
      ...(draft.executorUserId ? { executorUserId: draft.executorUserId } : {}),
    });
  }
  return out;
}

/**
 * Что справочник предлагает по пункту 8 Правил № 344.
 *
 * Вид работы не выбран — справочник ничего не предлагает; это НЕ то же самое,
 * что предложенное «не требуется»: в первом случае снимать нечего.
 */
export function proposedSafetyApproval(
  workType: { requiresSafetyApprovalDefault: boolean } | null,
): boolean | null {
  return workType ? workType.requiresSafetyApprovalDefault : null;
}

/**
 * Значение галочки на экране: решение выдающего, если он его принял, иначе
 * предложение справочника, иначе «не требуется».
 */
export function effectiveSafetyApproval(decision: boolean | null, proposed: boolean | null): boolean {
  return decision ?? proposed ?? false;
}

/**
 * Выдающий снял то, что подставил справочник.
 *
 * Снятие не запрещается: по пункту 19 Правил меры безопасности определяет
 * выдающий наряд, и запрет переложил бы его ответственность на справочник.
 * Но оно показывается на экране и записывается на сервере вместе с тем, кто
 * снял и когда.
 */
export function safetyApprovalWaived(decision: boolean | null, proposed: boolean | null): boolean {
  return proposed === true && effectiveSafetyApproval(decision, proposed) === false;
}

/**
 * Что уходит в поле `requiresSafetyApproval` тела запроса.
 *
 * Выдающий не трогал галочку — поле опускается, и умолчание берёт сервер из
 * справочника. Экран не пересказывает серверу то, что сервер и так знает:
 * иначе между чтением справочника экраном и записью наряда появилось бы
 * второе место, где это умолчание может разойтись.
 */
export function safetyApprovalForRequest(decision: boolean | null): boolean | undefined {
  return decision ?? undefined;
}

// ─── Issue #469 (B-034, шаг 3): земляные работы в охранной зоне ──────────────
//
// Порядок ровно тот же, что у согласования выше, и функции здесь отдельные, а
// не общая с параметром: признаки живут рядом, но отвечают на разные вопросы, и
// общая функция однажды получила бы «ещё один флажок», после чего было бы не
// сказать, что именно она решает. Четыре строки дублирования дешевле этого.

/**
 * Что справочник предлагает по пункту 300 Правил № 222.
 *
 * Вид работы не выбран — справочник ничего не предлагает; это НЕ то же самое,
 * что предложенное «не требуется»: в первом случае снимать нечего.
 */
export function proposedExcavationInProtectedZone(
  workType: { excavationInProtectedZoneDefault?: boolean } | null,
): boolean | null {
  // Поля нет вовсе — справочник отвечает сервер, который про охранную зону ещё
  // не знает. Это «не предлагал ничего», а не «предложил нет»: разница
  // существенная — от неё зависит, есть ли что снимать.
  return workType?.excavationInProtectedZoneDefault ?? null;
}

/** Значение галочки на экране: решение выдающего, иначе предложение, иначе «нет». */
export function effectiveExcavationInProtectedZone(
  decision: boolean | null,
  proposed: boolean | null,
): boolean {
  return decision ?? proposed ?? false;
}

/**
 * Выдающий снял то, что подставил справочник.
 *
 * Снятие не запрещается: справочник знает вид работ, но не место их ведения —
 * та же траншея вне охранной зоны письменного разрешения не требует. Но оно
 * показывается на экране и записывается на сервере вместе с тем, кто снял.
 */
export function excavationInProtectedZoneWaived(
  decision: boolean | null,
  proposed: boolean | null,
): boolean {
  return proposed === true && effectiveExcavationInProtectedZone(decision, proposed) === false;
}

/** Что уходит в поле `excavationInProtectedZone` тела запроса. */
export function excavationInProtectedZoneForRequest(decision: boolean | null): boolean | undefined {
  return decision ?? undefined;
}

// ─── Issue #468 (карточка B-034): реквизиты согласования в форме ─────────────
//
// Признак выше отвечает на вопрос «требуется ли согласование», эти строки — на
// вопрос «с кем оно состоялось». Блок появляется в форме при включённом
// признаке и собирается ОБЩИМИ функциями: наряд, выписанный за столом, обязан
// получиться тем же нарядом, что и выписанный в поле.
//
// ПРОВЕРОК ПО СУЩЕСТВУ ЗДЕСЬ НЕТ. Требуются ли реквизиты этому наряду, полны
// ли они и не позже ли дата момента выписки — решает и отвергает СЕРВЕР
// (решение владельца 25.09.2026). Ниже только перевод черновика формы в поля
// запроса.

/** Черновик одной строки согласования. Все поля — строки формы, как введены. */
export type SafetyApprovalDraft = {
  organizationName: string;
  representativeName: string;
  representativePosition: string;
  /**
   * ИИН согласовавшего (issue #471); пустая строка — не введён. Без него
   * подпись графы 8.1 не собрать, но выписке наряда он не мешает: внести его
   * можно и позже, пока графа 8.1 не подписана.
   */
  representativeIin?: string;
  /** Дата в формате YYYY-MM-DD; пустая строка — дата не введена. */
  approvedAt: string;
  permitNumber: string;
  permitIssuedAt: string;
  /**
   * Уже загруженные приложения (issue #469): скан письменного разрешения и
   * схема. Хранятся реквизитами, а не байтами: файл уходит на сервер сразу при
   * выборе, и форма держит только то, что показывает человеку.
   */
  attachments: SafetyApprovalAttachment[];
};

/** Пустая строка согласования: с неё начинается блок в форме. */
export function emptySafetyApprovalDraft(): SafetyApprovalDraft {
  return {
    organizationName: '',
    representativeName: '',
    representativePosition: '',
    // Issue #471: ИИН согласовавшего — без него подпись графы 8.1 не собрать.
    representativeIin: '',
    approvedAt: '',
    permitNumber: '',
    permitIssuedAt: '',
    attachments: [],
  };
}

/**
 * Совсем пустая строка — та, в которой не введено ничего.
 *
 * Такие в запрос не уходят: блок начинается с одной пустой строки, и выдающий,
 * который её не заполнил, отправил бы иначе согласование без единого реквизита.
 * Это НЕ проверка полноты — наполовину заполненная строка уходит на сервер и
 * получает от него отказ словами.
 */
export function safetyApprovalDraftIsEmpty(draft: SafetyApprovalDraft): boolean {
  return (
    draft.organizationName.trim() === '' &&
    draft.representativeName.trim() === '' &&
    draft.representativePosition.trim() === '' &&
    // Issue #471: введённый ИИН — тоже введённое. Строка, где назван только он,
    // пустой не является: выбросив её, форма отправила бы наряд без того
    // согласования, к которому человек уже назвал подписанта.
    (draft.representativeIin ?? '').trim() === '' &&
    draft.approvedAt.trim() === '' &&
    draft.permitNumber.trim() === '' &&
    draft.permitIssuedAt.trim() === '' &&
    // Issue #469: приложенный файл — тоже введённое. Строка, в которой человек
    // не написал ничего, но приложил разрешение, пустой не является: выбросив
    // её, форма отправила бы наряд без того самого приложения, ради которого
    // он и заполняется.
    draft.attachments.length === 0
  );
}

/**
 * Черновики → поле `safetyApprovals` тела запроса.
 *
 * Порядок — тот, в котором строки стоят в форме. Каноном он не является:
 * документ выдачи упорядочивает согласования сам, по наименованию организации.
 *
 * Номер и дата разрешения не введены — поля опускаются: пустая строка была бы
 * названным пустым значением, а разрешения просто нет.
 */
export function buildSafetyApprovals(
  drafts: readonly SafetyApprovalDraft[],
): CreateWorkOrderSafetyApprovalDto[] {
  return drafts
    .filter((draft) => !safetyApprovalDraftIsEmpty(draft))
    .map((draft) => ({
      organizationName: draft.organizationName.trim(),
      representativeName: draft.representativeName.trim(),
      representativePosition: draft.representativePosition.trim(),
      approvedAt: draft.approvedAt.trim(),
      // Issue #471: ИИН не введён — поля нет вовсе. Пустая строка была бы
      // названным пустым ИИН, а его просто не назвали.
      ...((draft.representativeIin ?? '').trim()
        ? { representativeIin: (draft.representativeIin ?? '').trim() }
        : {}),
      ...(draft.permitNumber.trim() ? { permitNumber: draft.permitNumber.trim() } : {}),
      ...(draft.permitIssuedAt.trim() ? { permitIssuedAt: draft.permitIssuedAt.trim() } : {}),
      // Issue #469: приложений нет — поля нет вовсе. Пустой массив был бы
      // названным пустым значением, а приложений просто не прикладывали.
      ...(draft.attachments.length > 0
        ? { attachmentIds: draft.attachments.map((attachment) => attachment.id) }
        : {}),
    }));
}

// ─── Issue #463 (карточка B-075): напряжение электроустановки в форме ────────
//
// Порядок тот же, что у согласования по пункту 8 выше: строка матрицы
// ПРЕДЛАГАЕТ, выдающий подтверждает или называет другое. Разница ровно одна и
// она в теле запроса — согласование, которого выдающий не трогал, из тела
// опускается, а напряжение уходит ВСЕГДА, включая пустое.
//
// Почему так. Выдающий удостоверяет своей ЭЦП документ выдачи, в котором
// напряжение записано (канон версии 3, issue #423), а в канон не входит то,
// чего подписант не видел на экране подписи (ADR-004 §13). Опущенное поле
// сервер заменяет умолчанием строки матрицы — и в наряде оказалось бы
// напряжение, которого в форме не стояло. Поэтому серверу уходит ровно
// показанное: выбранное — значением, «не указано» — явным null.
//
// Решением выдающего считается только то, что он трогал сам: `undefined` —
// не трогал, `null` — снял названное. Сведи их в одно, и снятое умолчание
// молча вернулось бы обратно.

/** Решение выдающего о напряжении. `undefined` — не трогал поле. */
export type VoltageClassDecision = CoreInstallationVoltageClass | null | undefined;

/**
 * Что предлагает вид работы. `null` — не предлагает ничего: строки матрицы
 * нет, вид работы из справочника не выбран либо напряжение у строки не задано.
 */
export function proposedVoltageClass(
  row: { voltageClass: CoreInstallationVoltageClass | null } | null | undefined,
): CoreInstallationVoltageClass | null {
  return row?.voltageClass ?? null;
}

/**
 * Что стоит в поле на экране — и ровно оно же уходит в тело запроса.
 *
 * Выбор ниже предложенного вида работ не действует (решение владельца
 * 25.09.2026): «до 1000 В», выбранное до вида работ «выше 1000 В», уступает
 * виду работ — иначе форма показывала бы значение, которое сервер отвергнет.
 */
export function effectiveVoltageClass(
  decision: VoltageClassDecision,
  proposed: CoreInstallationVoltageClass | null,
): CoreInstallationVoltageClass | null {
  if (decision === undefined) return proposed;
  if (decision !== null && !allowedVoltageClasses(proposed).includes(decision)) return proposed;
  return decision;
}

/**
 * Показанное значение подставлено видом работ, а не выбрано выдающим.
 *
 * Этим отличием подписывается поле на экране («по виду работ»): без него
 * выдающий не отличит подставленное от выбранного им самим, а удостоверяет он
 * подписью и то, и другое одинаково.
 */
export function voltageClassFromWorkType(
  decision: VoltageClassDecision,
  proposed: CoreInstallationVoltageClass | null,
): boolean {
  if (proposed === null) return false;
  // Не трогал — подставлено. Выбрал ниже вида работ — выбор не действует, и в
  // поле снова стоит подставленное.
  return decision === undefined || (decision !== null && !allowedVoltageClasses(proposed).includes(decision));
}

/**
 * Какое напряжение выдающий может выбрать. Вид работ задаёт нижнюю планку
 * (решение владельца 25.09.2026, как с группой по электробезопасности):
 * «выше 1000 В» по виду работ понизить нельзя, «до 1000 В» — можно повысить.
 * Сервер отвергает понижение сам; здесь — только чтобы форма его не
 * предлагала.
 */
export function allowedVoltageClasses(
  proposed: CoreInstallationVoltageClass | null,
): CoreInstallationVoltageClass[] {
  return proposed === 'ABOVE_1000V' ? ['ABOVE_1000V'] : ['ABOVE_1000V', 'UP_TO_1000V'];
}

// Issue #147 (по решению владельца в #146): наблюдающий — один из членов
// бригады. В форме он выбирается из уже выбранной бригады; здесь выбор
// превращается в роли для тела запроса. Сервер отдельно проверяет, что при
// `requiresObserver` в матрице есть член с ролью OBSERVER.

export type CrewMemberInput = {
  workerId: string;
  role: z.infer<typeof CoreWorkOrderMemberRoleSchema>;
};

export function buildCrewMembers(
  memberIds: readonly string[],
  observerId: string | null,
): CrewMemberInput[] {
  return memberIds.map((workerId) => ({
    workerId,
    role: workerId === observerId ? 'OBSERVER' : 'MEMBER',
  }));
}

/** Наблюдающий, выпавший из состава бригады, сбрасывается. */
export function reconcileObserver(memberIds: readonly string[], observerId: string | null): string | null {
  return observerId && memberIds.includes(observerId) ? observerId : null;
}

/**
 * Можно ли отправлять форму с точки зрения наблюдающего.
 *
 * Это НЕ проверка нормы, а вежливость экрана: сервер откажет сам, и его
 * отказ называет вид работы. Здесь — только чтобы не отправлять заведомо
 * неполную форму и не заставлять человека заполнять её заново.
 */
export function observerRequirementSatisfied(requiresObserver: boolean, observerId: string | null): boolean {
  return !requiresObserver || observerId !== null;
}

/** Роль в наряде, на которую выбирают человека из списка ответственных. */
export type AssignableRole = 'RESPONSIBLE' | 'ADMITTING' | 'PRODUCER' | 'OBSERVER';

/**
 * Работник списка подразделения в объёме, который нужен отбору: поле
 * `assignableAs` из `GET /users/subdivision-members` (см. `SubdivisionMember`).
 */
export type AssignableCandidate = {
  id: string;
  assignableAs?: Record<AssignableRole, { upTo1000V: boolean; above1000V: boolean }>;
};

/**
 * Кого показывать в списке на роль — в форме выписки за столом и в телефоне.
 *
 * Правил здесь нет: проходит ли человек — полномочие роли и порог группы по
 * напряжению — ответил сервер тем же расчётом, которым он проверяет выписку.
 * Здесь только выбирается столбец по напряжению из формы; напряжение не
 * выбрано — порог «до 1000 В», как на сервере.
 *
 * Сервер без поля (старая сборка) — показываются все, как прежде: скрыть
 * людей по отсутствующему ответу значило бы оставить пустой список.
 */
export function assignablePeople<T extends AssignableCandidate>(
  people: readonly T[],
  role: AssignableRole,
  voltage: CoreInstallationVoltageClass | null,
): T[] {
  const column = voltage === 'ABOVE_1000V' ? 'above1000V' : 'upTo1000V';
  return people.filter((person) => person.assignableAs === undefined || person.assignableAs[role][column]);
}

/**
 * Выбранный человек, которого на роль больше назначить нельзя (сменилось
 * напряжение или состав бригады), сбрасывается: иначе в форме стоял бы
 * человек, которого в списке уже нет, и отказ пришёл бы только после отправки.
 */
export function keepAssignable(
  selectedId: string | null,
  assignable: readonly AssignableCandidate[],
): string | null {
  if (selectedId === null) return null;
  return assignable.some((person) => person.id === selectedId) ? selectedId : null;
}

// ─── Правила № 344, пункты 25 и 27: приостановка работ ───────────────────────
//
// Два основания, оба за допускающим, и путать их нельзя — последствия разные.
//
// Пункт 25: допускающий ПРЕКРАЩАЕТ работы, ИЗЫМАЕТ наряд-допуск, оформляет его
// вновь и производит допуск к работе заново — при аварийной ситуации,
// несоответствии фактических условий требованиям безопасности, угрозе на
// смежных объектах или необходимости подключения действующего оборудования.
// Работы по ЭТОМУ наряду не возобновляются вовсе: их возобновляет новый наряд.
//
// Пункт 27: ВРЕМЕННОЕ прекращение по указанию допускающего — производитель
// работ удаляет бригаду с места работы и возвращает наряд допускающему;
// возобновление по его разрешению после проверки первоначальных мероприятий.
//
// Ни то ни другое не есть закрытие: закрытый наряд исчерпан, приостановленный
// возобновляется или заменяется новым. Слить их значило бы потерять
// возможность отличить одно от другого при разборе происшествия (ADR-005 §11).
export const WorkOrderSuspensionGroundSchema = z.enum(['TEMPORARY_HALT', 'WITHDRAWAL']);
export type WorkOrderSuspensionGround = z.infer<typeof WorkOrderSuspensionGroundSchema>;

/// Участник приостановки — тот же состав полей, что у прочих людей наряда.
const SuspensionPersonSchema = z.object({
  userId: z.string(),
  name: z.string().nullable(),
  employeeNumber: z.string().nullable(),
});

export const CoreWorkOrderSuspensionSchema = z.object({
  id: z.string(),
  workOrderId: z.string(),
  ground: WorkOrderSuspensionGroundSchema,
  /// Что именно произошло — словами того, кто прекратил работы. Пункт 25
  /// перечисляет четыре основания, пункт 27 говорит «по указанию
  /// допускающего»: в обоих случаях причину знает человек, а не система.
  reason: z.string(),
  suspendedAt: z.string(),
  suspendedBy: SuspensionPersonSchema,
  /// Пусто — приостановка действует. Заполнено — работы возобновлены
  /// разрешением допускающего (пункт 27). У изъятого наряда (пункт 25)
  /// остаётся пустым навсегда: возобновления у него нет.
  resumedAt: z.string().nullable(),
  resumedBy: SuspensionPersonSchema.nullable(),
});
export type CoreWorkOrderSuspension = z.infer<typeof CoreWorkOrderSuspensionSchema>;

/// Чем допускающий прекращает работы. Причина обязательна: запись
/// «работы прекращены», не говорящая почему, при разборе не отвечает ни на
/// один вопрос.
export const SuspendWorkOrderSchema = z.object({
  ground: WorkOrderSuspensionGroundSchema,
  reason: z.string().min(1).max(2000),
});
export type SuspendWorkOrderDto = z.infer<typeof SuspendWorkOrderSchema>;

export const CoreWorkOrderSchema = z.object({
  id: z.string().uuid(),
  number: z.string(),
  workForm: CoreWorkFormSchema,
  workType: z.string(),
  safetyMeasures: z.string(),
  requiredElectricalGroup: CoreElectricalGroupSchema.nullable().optional(),
  /// Issue #419 (карточка B-057): напряжение электроустановки по этому наряду.
  /// null — не названо; это НЕ «до 1000 В» (у легаси-нарядов поля не было).
  voltageClass: CoreInstallationVoltageClassSchema.nullable().optional(),
  // Issue #146: связь со справочником видов работ; null для легаси-нарядов.
  workTypeCatalogId: z.string().nullable().optional(),
  workTypeCatalogName: z.string().nullable().optional(),
  status: CoreWorkOrderStatusSchema,
  workSiteId: z.string().nullable(),
  workSiteDetail: z.string().nullable().optional(),
  subdivisionId: z.string(),
  issuerId: z.string(),
  supervisorId: z.string(),
  producerId: z.string(),
  workSiteName: z.string().nullable().optional(),
  subdivisionName: z.string().nullable().optional(),
  issuerName: z.string().nullable().optional(),
  issuerEmployeeNumber: z.string().nullable().optional(),
  supervisorName: z.string().nullable().optional(),
  supervisorEmployeeNumber: z.string().nullable().optional(),
  producerName: z.string().nullable().optional(),
  // ADR-003: разведённые нормативные роли. nullable — исторические наряды
  // не имеют допускающего (см. миграцию 20260905120000).
  responsibleUserId: z.string().nullable().optional(),
  responsibleName: z.string().nullable().optional(),
  responsibleEmployeeNumber: z.string().nullable().optional(),
  admittingUserId: z.string().nullable().optional(),
  admittingName: z.string().nullable().optional(),
  admittingEmployeeNumber: z.string().nullable().optional(),
  /// Issue #326: ВСЕ допускающие наряда, включая названного первым, — по
  /// табельному номеру, тем же порядком, что в каноне документа выдачи.
  ///
  /// Отдаётся только у наряда, где их несколько: у наряда с одним
  /// допускающим поля нет, и карточка показывает его прежними тремя полями
  /// выше. Аддитивно — выпущенные сборки о нём не знают и не ломаются.
  admitters: z
    .array(
      z.object({
        userId: z.string(),
        name: z.string().nullable().optional(),
        employeeNumber: z.string().nullable().optional(),
      }),
    )
    .optional(),
  producerEmployeeNumber: z.string().nullable().optional(),
  issuedAt: z.string(),
  admittedAt: z.string().nullable().optional(),
  startedAt: z.string().nullable().optional(),
  /// ADR-005 §7: рабочее место сдано производителем работ; приёмка
  /// допускающим ставит closedAt. Аддитивно — старые клиенты не читают.
  closureHandoverAt: z.string().nullable().optional(),
  /// Кто сдал рабочее место. Без него карточка знает, что место сдано, и не
  /// знает кем — а второй акт закрытия подписывает другой человек, и ему
  /// нужно видеть, чью сдачу он принимает (№ 344 бланк, пункт 13).
  closureHandoverById: z.string().nullable().optional(),
  closureHandoverName: z.string().nullable().optional(),
  closureHandoverEmployeeNumber: z.string().nullable().optional(),
  /// Issue #327, ADR-005 §7: ждёт ли сдача со-подписи ответственного
  /// руководителя работ. Включается профилем организации (бланк № 253
  /// содержит вторую подписную строку закрывающего блока, бланк № 344 — нет)
  /// и только там, где руководитель работ в наряде назначен: роль
  /// необязательна (№ 253 пункт 42).
  ///
  /// Поле обязано ехать рядом со временем со-подписи: пустое время само по
  /// себе не отвечает, ждут ли ещё чего-то, и карточка иначе предложила бы
  /// приёмку под сдачей, удостоверенной наполовину.
  closureCoSignatureRequired: z.boolean().optional(),
  /// Когда и кем сдача со-подписана. NULL при включённой настройке — сдача
  /// не закончена; NULL при выключенной — со-подписи и не ждут.
  closureHandoverCoSignedAt: z.string().nullable().optional(),
  closureHandoverCoSignedById: z.string().nullable().optional(),
  closureHandoverCoSignedName: z.string().nullable().optional(),
  closureHandoverCoSignedEmployeeNumber: z.string().nullable().optional(),
  closedAt: z.string().nullable().optional(),
  /// Действующая приостановка работ (№ 344, пункты 25 и 27). NULL — работы не
  /// приостановлены.
  ///
  /// Поле обязано быть в самом наряде, а не только в отдельном перечне:
  /// приостановленный наряд стоит в статусе ADMITTED, и без этого поля он
  /// неотличим от наряда, который допустили и вот-вот начнут. Отличать их
  /// приходится не из любопытства — по первому работать нельзя.
  suspension: CoreWorkOrderSuspensionSchema.nullable().optional(),
  createdAt: z.string(),
  updatedAt: z.string(),
});
export type CoreWorkOrder = z.infer<typeof CoreWorkOrderSchema>;

export type CoreWorkOrderCreateInput = Omit<CreateCoreWorkOrderDto, 'workSiteId'> & {
  // Temporary compatibility invariant: old RailHub requests may omit location.
  workSiteId?: string | null;
};

export type CoreStartReasonCode =
  | 'PERMANENT_CLEARANCE_FAILED'
  | 'GROUP_BELOW_REQUIRED'
  | 'TARGET_BRIEFING_NOT_TODAY'
  // Инструктаж проведён, но подтверждения от работника нет: ни его подписи,
  // ни отметки за него. Пункт 11 приложения 1 Правил № 344 — в наряде
  // расписываются оба, и одной отметки проводившего для старта мало.
  | 'TARGET_BRIEFING_NOT_ACKNOWLEDGED'
  // Issue #531: проверка знаний после сегодняшнего инструктажа не пройдена
  // (Правила № 1019, п. 71) — нужен повторный инструктаж.
  | 'TARGET_BRIEFING_KNOWLEDGE_FAILED'
  | 'WORK_ORDER_NOT_ADMITTED';

// Issue #146: источник требований, по которым посчитаны ворота.
//   MATRIX — строка матрицы требований для вида работы наряда;
//   LEGACY — прежняя логика (WorkOrder.requiredElectricalGroup + все
//            обязательные типы допуска организации).
export type CoreRequirementSource = 'MATRIX' | 'LEGACY';

export type CoreMemberGateResult = {
  workerId: string;
  canStart: boolean;
  reasons: CoreStartReasonCode[];
  // Issue #146: человекочитаемые формулировки причин. В матричном режиме
  // называют вид работы и требование («для работы „…“ требуется группа III,
  // у работника II»). Аддитивно — старые клиенты читают только `reasons`.
  messages?: string[];
  // Те же причины в разобранном виде: код и подстановки, без готового текста.
  // Формулировки выше собираются на сервере по-русски, и на казахском
  // интерфейсе они оставались русскими — это объяснение системы, а не
  // введённые пользователем данные. Текст собирает тот, кто показывает.
  messageDetails?: CoreStartReasonDetail[];
  requirementSource?: CoreRequirementSource;
};

export type CoreStartReasonDetail = {
  code: string;
  params: Record<string, string>;
};

// ─── Issue #431: отказ по готовности бригады — поимённо ───────────────────────
//
// Отказ называл одну общую фразу «бригада не готова, проверьте допуски и
// инструктаж», хотя ворота по каждому работнику сервер уже посчитал. При
// бригаде из троих, где заперты двое и по разным основаниям, производитель
// работ стоял на месте работ и не знал ни кого не пускают, ни что делать, —
// перебирал бригаду вручную по другим экранам.
//
// ФОРМАТ ОДИН на все такие отказы: старт работ здесь, выписка наряда и допуск —
// issue #434. Второй формат того же отказа развёл бы формулировки, которые
// человек читает в одном и том же наряде.
export const CoreCrewReadinessRefusalMemberSchema = z.object({
  workerId: z.string().uuid(),
  /// ФИО на момент отказа. NULL — имени в записи наряда не нашлось; строка
  /// всё равно остаётся: причина без имени полезнее, чем молчание.
  workerName: z.string().nullable(),
  /// Коды причин, как их выдали ворота. Строками, а не перечислением: сервер
  /// новее клиента не должен ломать разбор ответа целиком.
  reasons: z.array(z.string()),
  /// Те же причины разобранными — код и подстановки, без готового текста. Имя
  /// поля то же, что у ворот члена бригады (`MemberGateResult.messageDetails`):
  /// текст собирает один и тот же код на стороне, которая показывает, и
  /// готовое русское предложение не осталось бы русским на казахском
  /// интерфейсе.
  messageDetails: z.array(z.object({ code: z.string(), params: z.record(z.string()) })),
});
export type CoreCrewReadinessRefusalMember = z.infer<typeof CoreCrewReadinessRefusalMemberSchema>;

export const CoreCrewReadinessRefusalSchema = z.object({
  /// Только те, кто не прошёл ворота: отказ отвечает на вопрос «кого не
  /// пускают», и готовые в нём не перечисляются.
  members: z.array(CoreCrewReadinessRefusalMemberSchema),
});
export type CoreCrewReadinessRefusal = z.infer<typeof CoreCrewReadinessRefusalSchema>;

// Issue #146: диагностика требований по наряду в целом (не по члену).
// `matrixMissing` = наряд ссылается на вид работы из справочника, но строки
// матрицы для него нет → движок работает по LEGACY, факт виден отдельно
// (предупреждение, не блок). `requiresObserver` собирается из матрицы;
// сама проверка наличия наблюдающего — на создании наряда.
export type CoreWorkOrderReadiness = {
  source: CoreRequirementSource;
  workTypeCatalogId: string | null;
  workTypeName: string | null;
  matrixMissing: boolean;
  requiredElectricalGroup: 'II' | 'III' | 'IV' | 'V' | null;
  requiredBriefingTypes: CoreBriefingType[];
  medicalExaminationRequired: boolean;
  requiresObserver: boolean;
  observerAssigned: boolean;
  /**
   * Вердикт по каждому требованию вида работы — проходит ли по нему бригада.
   *
   * Аддитивно: поле необязательное, установленные сборки его не читают и
   * продолжают работать. Клиент без вердикта показывает требование
   * нейтральным кружком — так же, как до появления поля.
   *
   * Вердикт НЕ считает ничего нового: он раскладывает по требованиям то, что
   * уже посчитали ворота допуска. Правила блокировки от этого не зависят.
   */
  requirements?: CoreRequirementVerdict[];
};

/** Требование вида работы, по которому бывает вердикт. */
export type CoreRequirementKind = 'GROUP' | 'BRIEFINGS' | 'MEDICAL';

/**
 * Исход проверки требования по ВСЕЙ бригаде.
 *
 * `unknown` — проверки не было: наряд идёт по базовой логике без матрицы
 * либо требование не с чем сопоставить. Это отдельное состояние, а не
 * «наверное, выполнено»: зелёная галочка утверждала бы проверку, которой не
 * было, на документе, по которому людей допускают к работе под напряжением.
 *
 * Состояния «ожидается действие» здесь нет намеренно. Любой непройденный
 * постоянный допуск закрывает ворота, то есть блокирует; ожидание бывает у
 * целевого инструктажа, и его показывает чек-лист работника, а не эта строка.
 */
export type CoreRequirementStatus = 'met' | 'blocked' | 'unknown';

export type CoreRequirementFailureReason =
  | 'GROUP_BELOW_REQUIRED'
  | 'GROUP_MISSING'
  | 'CLEARANCE_MISSING'
  | 'CLEARANCE_EXPIRED'
  | 'CLEARANCE_EXPIRING'
  /** Требование матрицы, под которое в организации не заведён тип допуска. */
  | 'CLEARANCE_NOT_CONFIGURED';

/**
 * Кто именно не проходит и почему.
 *
 * Работник назван идентификатором: ФИО у карточки уже есть в составе
 * бригады, и дублировать его в ответе — лишний повод им разойтись.
 */
export type CoreRequirementFailure = {
  workerId: string;
  reason: CoreRequirementFailureReason;
  /** Название типа допуска — для причин про допуск. */
  clearanceTypeName?: string | null;
  /** Когда истёк или истекает. */
  expiresAt?: string | null;
  /** Требуемая и фактическая группы — для причин про группу. */
  requiredGroup?: z.infer<typeof CoreElectricalGroupSchema> | null;
  workerGroup?: z.infer<typeof CoreElectricalGroupSchema> | null;
};

export type CoreRequirementVerdict = {
  kind: CoreRequirementKind;
  status: CoreRequirementStatus;
  failures: CoreRequirementFailure[];
};

/**
 * Чем подтверждено получение целевого инструктажа.
 *
 * `WORKER_DEVICE_KEY` — работник подписал сам ключом своего устройства.
 * `PROXY_VERBAL` — смартфона у работника нет, инструктаж проведён устно на
 * месте, отметку поставил производитель или проводивший.
 *
 * Два значения, а не флаг «подтверждено»: отметка за работника и подпись
 * работника — разные факты, и при разборе происшествия спросят именно про
 * разницу между ними.
 */
export const CORE_TARGET_BRIEFING_ACK_METHODS = ['WORKER_DEVICE_KEY', 'PROXY_VERBAL', 'PAPER_JOURNAL'] as const;
// Issue #531 (решение Штаба 29.09.2026):
//   `PAPER_JOURNAL` — работник без смартфона расписался в БУМАЖНОМ журнале,
//   в системе — отметка «подписано на бумаге» с фото страницы журнала.
//   `PROXY_VERBAL` отменён: новые отметки не ставятся, для допуска прежние не
//   считаются. Значение остаётся ради уже внесённых записей.
export type CoreTargetBriefingAckMethod = (typeof CORE_TARGET_BRIEFING_ACK_METHODS)[number];

/** Отметка целевого инструктажа за работника, у которого нет смартфона. */
export const ProxyTargetBriefingAckSchema = z.object({
  /// Единственный предусмотренный способ. Поле обязательно и закрыто
  /// перечнем: «отмечено, способ не указан» — запись, по которой при разборе
  /// нечего сказать.
  method: z.literal('PROXY_VERBAL'),
});
export type ProxyTargetBriefingAckDto = z.infer<typeof ProxyTargetBriefingAckSchema>;

export type CoreWorkOrderMemberWithGate = {
  id: string;
  workOrderId: string;
  workerId: string;
  workerName?: string | null;
  workerEmployeeNumber?: string | null;
  role: 'MEMBER' | 'OBSERVER';
  /// Отметка ПРОВОДИВШЕГО: инструктаж проведён. Подтверждением работника не
  /// является — по пункту 11 приложения 1 Правил № 344 расписываются оба.
  targetBriefingDoneAt?: string | null;
  targetBriefingById?: string | null;
  /// Подтверждение ПОЛУЧИВШЕГО — вторая подпись того же пункта.
  targetBriefingAckAt?: string | null;
  targetBriefingAckMethod?: CoreTargetBriefingAckMethod | null;
  /// Заполнено только у отметки за работника: кто её поставил.
  targetBriefingAckProxyById?: string | null;
  targetBriefingAckProxyName?: string | null;
  /// Issue #531: кто провёл последний инструктаж — ФИО.
  targetBriefingByName?: string | null;
  /// Issue #531: результат проверки знаний последнего инструктажа: true —
  /// прошёл, false — не прошёл (нужен повторный), null — проверки не было.
  targetBriefingKnowledgePassed?: boolean | null;
  /// Issue #531: подписанное проведение последнего инструктажа.
  targetBriefingConductDocumentId?: string | null;
  /// Issue #531: подпись инструктирующего — ЭЦП или нет. false — ключ
  /// устройства: в интерфейсе и выгрузках помечается «без ЭЦП» и за
  /// юридически значимую подпись не выдаётся (#534). null — подписи нет.
  targetBriefingConductQualified?: boolean | null;
  /// Issue #531: то же для подписи работника о получении.
  targetBriefingAckQualified?: boolean | null;
  /// Issue #531: отметка «подписано на бумаге» — фото страницы журнала.
  targetBriefingPaperMark?: {
    id: string;
    fileName: string;
    mimeType: string;
    uploadedAt: string;
    uploadedByName: string | null;
  } | null;
  // Issue #143: профессия по ЕТКС и разряд участника. Берутся из его
  // membership в организации наряда; у легаси-строк без membership читаются
  // как null. `professionProvisional` едет вместе с наименованием, потому
  // что читатель состава наряда не имеет доступа к справочнику профессий.
  professionName?: string | null;
  qualificationGrade?: number | null;
  professionProvisional?: boolean | null;
  gate: CoreMemberGateResult;
};

export type CoreWorkOrderListItem = CoreWorkOrder & { memberCount: number };
export type CoreWorkOrderListResponse = { workOrders: CoreWorkOrderListItem[] };
export type CoreWorkOrderDetailResponse = CoreWorkOrder & {
  members: CoreWorkOrderMemberWithGate[];
  // Issue #146: аддитивно; отсутствует только у ответов, собранных без
  // резолва требований (в текущем API — всегда присутствует в detail).
  readiness?: CoreWorkOrderReadiness;
  // № 344, пункты 25 и 27: ВСЕ приостановки наряда, включая снятые. Действующая
  // едет отдельным полем `suspension` самого наряда; здесь — история, потому
  // что работы по одному наряду прекращают не один раз, а пункт 15 требует
  // хранить наряд год со дня закрытия вместе с тем, что с ним происходило.
  suspensions?: CoreWorkOrderSuspension[];
};
export type CoreDeleteWorkOrderResponse = { id: string; deleted: true };

// ─── Формы допуска: создание и правка ────────────────────────────────────────
//
// Эти схемы живут ЗДЕСЬ, а не в корневом стволе, и перенесены сюда осознанно.
//
// Корневой ствол читает из CORE значения (`CoreBriefingTypeSchema`,
// `CreateWorkOrderSafetyMeasureSchema`), а CORE читал из ствола
// `CreateClearanceSchema` — получался цикл по РАНТАЙМУ, а не по типам.
// Node и Metro его переживают: там реэкспорт компилируется в ленивый геттер,
// и порядок вычисления модулей значения не имеет. Сборщик веба (Rollup, ESM)
// связывает импорты живыми привязками: когда точкой входа становится
// `core.ts`, тело ствола исполняется ПЕРВЫМ и обращается к ещё не
// инициализированным значениям CORE. Панель падала на этом целиком —
// `ReferenceError: Cannot access ... before initialization` до первой
// отрисовки, пустая страница вместо листа.
//
// Разрывать цикл нужно с одной стороны, и правильная сторона — эта: CORE не
// должен зависеть от ствола в рантайме вовсе. Обратное направление (ствол
// читает CORE) остаётся и безопасно.
//
// Для потребителей ничего не меняется: ствол реэкспортирует CORE целиком
// (`export * from './core'`), поэтому оба адреса импорта продолжают отдавать
// те же символы.

const CLEARANCE_DATE_RE = /^\d{4}-\d{2}-\d{2}$/;

// Выдающий допуск физлицо. Обязательное поле создания: снимок ФИО и
// табельного берётся из этой формы, а не из свободного текста issuedBy.
// INTERNAL_USER — активный член организации-выдающего (сервис проверит и
// возьмёт snapshot из membership'а); EXTERNAL_PERSON — внешний врач/комиссия
// без учётки, ФИО и идентификатор фиксируются в snapshot дословно.
export const IssuingPersonSchema = z.discriminatedUnion('kind', [
  z.object({
    kind: z.literal('INTERNAL_USER'),
    userId: z.string().uuid(),
  }),
  z.object({
    kind: z.literal('EXTERNAL_PERSON'),
    name: z.string().min(1).max(255),
    employeeNumber: z.string().min(1).max(50),
  }),
]);
export type IssuingPerson = z.infer<typeof IssuingPersonSchema>;

export const CreateClearanceSchema = z.object({
  employeeNumber: z.string().min(1), // табельный на входе, не workerId
  clearanceTypeId: z.string().uuid(),
  // Issue #523: номер документа-основания обязателен, но схема его пустоту не
  // бракует — это делает сервер (`clearance-entry-rules.ts`). Отказ по вводу
  // должен приходить человеку понятным текстом на русском, а не строкой
  // валидатора; поэтому ВСЕ правила ввода отвечают из одного места, и схема
  // остаётся формой поля, а не правилом.
  documentNumber: z.string().optional(),
  electricalSafetyGroup: z.number().int().min(2).max(5).nullable().optional(), // вход числовой 2..5 → маппится в enum II..V на сервере
  // Issue #537: дата документа-основания. Сервер записывает её и в дату
  // документа, и в начало действия. Обе формы поля необязательны в СХЕМЕ, но
  // не на деле: без даты документа сервер запись не примет — отказ приходит
  // человеческим текстом из `clearance-entry-rules.ts`, как и отказ по номеру.
  documentDate: z.string().regex(CLEARANCE_DATE_RE, 'Формат даты: YYYY-MM-DD').optional(),
  // Прежнее имя того же поля (до #537 дата документа называлась `issuedAt`).
  // Принимается, чтобы уже установленные сборки приложения не потеряли ввод
  // допуска до обновления; `documentDate`, если прислан, главнее.
  issuedAt: z.string().regex(CLEARANCE_DATE_RE, 'Формат даты: YYYY-MM-DD').optional(),
  expiresAt: z.string().regex(CLEARANCE_DATE_RE, 'Формат даты: YYYY-MM-DD').optional(),
  issuedBy: z.string().min(1), // организация/подразделение, выдавшее документ
  // #205: `issuingPerson` необязателен. Если не задан — сервер подставит
  // membership актора (типовой случай: HSE-специалист сам оформляет
  // допуск, который сам и выдал). Явное значение остаётся для
  // EXTERNAL_PERSON (медосмотр в больнице, обучение в УЦ и т.п.) и
  // для обратной совместимости старых клиентов, которые всегда
  // отправляли INTERNAL_USER.
  issuingPerson: IssuingPersonSchema.optional(),
  notes: z.string().optional(),
});
export type CreateClearanceDto = z.infer<typeof CreateClearanceSchema>;

export const UpdateClearanceSchema = z.object({
  documentNumber: z.string().nullable().optional(),
  electricalSafetyGroup: z.number().int().min(2).max(5).nullable().optional(),
  // Issue #537: дата документа-основания. `null` в схеме допустим затем, чтобы
  // попытка очистить дату получила понятный отказ сервера, а не строку
  // валидатора: исправлением дату документа не очищают.
  documentDate: z.string().regex(CLEARANCE_DATE_RE, 'Формат даты: YYYY-MM-DD').nullable().optional(),
  // Прежнее имя даты документа (до #537). Принимается от уже установленных
  // сборок; `documentDate`, если прислан, главнее.
  issuedAt: z.string().regex(CLEARANCE_DATE_RE, 'Формат даты: YYYY-MM-DD').optional(),
  expiresAt: z.string().regex(CLEARANCE_DATE_RE, 'Формат даты: YYYY-MM-DD').nullable().optional(),
  issuedBy: z.string().min(1).optional(),
  notes: z.string().nullable().optional(),
  // Issue #523: исправление — это правка уже удостоверенного документа, и
  // журнал обязан сказать, ПОЧЕМУ его исправили. Как и с номером документа,
  // пустоту бракует сервер, чтобы отказ был понятной фразой.
  reason: z.string().optional(),
});
export type UpdateClearanceDto = z.infer<typeof UpdateClearanceSchema>;

// Issue #523: аннулирование вместо удаления. Запись не исчезает — она
// перестаёт давать допуск и остаётся в истории с отметкой, кто и почему её
// аннулировал. Причина обязательна: аннулирование без причины ничем не
// отличается от опечатки оператора.
export const AnnulClearanceSchema = z.object({
  reason: z.string().optional(),
});
export type AnnulClearanceDto = z.infer<typeof AnnulClearanceSchema>;

// Issue #537: дозаполнение основания — номер и дата документа впервые
// вписываются в запись, внесённую до того, как они стали обязательными. Не
// исправление: окно дат организации здесь не действует, дата документа лишь
// не в будущем и не позже начала действия. Все три поля обязательны на деле;
// пустоту бракует сервер, чтобы отказ был понятной фразой.
export const CompleteClearanceBasisSchema = z.object({
  documentNumber: z.string().optional(),
  documentDate: z.string().regex(CLEARANCE_DATE_RE, 'Формат даты: YYYY-MM-DD').optional(),
  reason: z.string().optional(),
});
export type CompleteClearanceBasisDto = z.infer<typeof CompleteClearanceBasisSchema>;

// Type-only bridges for established CORE domains that still have their runtime
// compatibility schemas in the root barrel. They do not add RAIL vocabulary to CORE.
//
// Здесь остались ТОЛЬКО типы: они стираются при сборке и цикла не создают.
export type {
  ClearanceKind,
  ClearanceCategory,
  ClearanceStatus,
  OverallStatus,
  WorkerClearancesResponse,
  ClearanceWithStatus,
  // Issue #523: журнал допусков работника — ввод, продление, исправление,
  // аннулирование.
  ClearanceHistoryChange,
  ClearanceHistoryEvent,
  ClearanceHistoryRecord,
  ClearanceHistoryResponse,
  ResetPasswordDto,
  ResetPasswordResponse,
} from './index';

// ─── Stage 1-1: Additive Tenant Foundation (CORE) ─────────────────────────────
// Contracts mirror ADR-001 tenant model: Organization is the tenant root, User
// remains the global identity, employment/tenant access lives on
// OrganizationMembership, Team is optional and belongs to a Subdivision.
// Stage 1-1 exposes the shapes only; tenant request context, runtime guards,
// and product authorization belong to later Stages.

export const ProductSchema = z.enum(['RAIL', 'CITY']);
export type Product = z.infer<typeof ProductSchema>;

export const OrganizationSchema = z.object({
  id: z.string().uuid(),
  code: z.string().min(1).max(50),
  name: z.string().min(1).max(255),
  isActive: z.boolean(),
  createdAt: z.string().datetime().optional(),
  updatedAt: z.string().datetime().optional(),
});
export type Organization = z.infer<typeof OrganizationSchema>;

export const CreateOrganizationSchema = z.object({
  code: z.string().trim().min(1).max(50),
  name: z.string().trim().min(1).max(255),
});
export type CreateOrganizationDto = z.infer<typeof CreateOrganizationSchema>;

export const UpdateOrganizationSchema = z.object({
  code: z.string().trim().min(1).max(50).optional(),
  name: z.string().trim().min(1).max(255).optional(),
  isActive: z.boolean().optional(),
});
export type UpdateOrganizationDto = z.infer<typeof UpdateOrganizationSchema>;

export const AdminUpdateOrganizationSchema = z.object({
  name: z.string().trim().min(1).max(255).optional(),
  isActive: z.boolean().optional(),
  /// Issue #327, ADR-005 §7: требует ли организация со-подписи ответственного
  /// руководителя работ под сдачей рабочего места. Настройка профиля, а не
  /// правило схемы: вторая подписная строка есть в закрывающем блоке бланка
  /// № 253 и отсутствует в бланке № 344, а какой из актов применим, решает
  /// работодатель.
  closureHandoverRequiresManagerCoSignature: z.boolean().optional(),
  /// Issue #445: требует ли организация оперативного согласования наряда
  /// перед допуском бригады. Настройка организации, а не правило в коде: у
  /// одного клиента согласует энергодиспетчер, у другого — кто-то ещё, у
  /// третьего никто. Умолчание false — включённая настройка ДОБАВЛЯЕТ
  /// требование, и организация, о порядке которой ничего не сказано, работает
  /// ровно как до задачи.
  workOrderRequiresApproval: z.boolean().optional(),
  /// Issue #523: насколько старым может быть документ-основание, который
  /// сегодня вносят в допуски. Настройка организации, а не правило в коде:
  /// сколько времени бумага идёт от врача или учебного центра до того, кто
  /// вносит её в систему, знает работодатель. Умолчание — 30 дней; 0 значит
  /// «только сегодняшней датой», 365 — верхняя граница, документ старше года
  /// вносят не опечаткой, а разбирательством.
  clearanceDocumentMaxAgeDays: z.number().int().min(0).max(365).optional(),
}).refine((value) => Object.keys(value).length > 0, 'At least one change is required');
export type AdminUpdateOrganizationDto = z.infer<typeof AdminUpdateOrganizationSchema>;

export const AdminSubdivisionSchema = z.object({
  id: z.string().uuid(),
  organizationId: z.string().uuid(),
  code: z.string().min(1).max(50),
  name: z.string().min(1).max(255),
  parentId: z.string().uuid().nullable().optional(),
  isActive: z.boolean(),
});
export const AdminCreateSubdivisionSchema = z.object({
  code: z.string().trim().min(1).max(50),
  name: z.string().trim().min(1).max(255),
  parentId: z.string().uuid().nullable().optional(),
});
export const AdminUpdateSubdivisionSchema = z.object({
  name: z.string().trim().min(1).max(255).optional(),
  isActive: z.boolean().optional(),
}).refine((value) => Object.keys(value).length > 0, 'At least one change is required');

export const AdminCreateUserSchema = z.object({
  employeeNumber: z.string().trim().min(1).max(50),
  name: z.string().trim().min(1).max(255),
  password: z.string().min(8).max(128),
  subdivisionId: z.string().uuid().nullable().optional(),
});
export const AdminAttachUserSchema = z.object({
  userId: z.string().uuid(),
  employeeNumber: z.string().trim().min(1).max(50).nullable().optional(),
  subdivisionId: z.string().uuid().nullable().optional(),
});
export const AdminUpdateMembershipSchema = z.object({
  employeeNumber: z.string().trim().min(1).max(50).nullable().optional(),
  subdivisionId: z.string().uuid().nullable().optional(),
  isActive: z.boolean().optional(),
  // Issue #531: должность сотрудника — графа 4 журнала инструктажа вместе с
  // профессией по ЕТКС. Пустая строка снимает должность, как null.
  position: z
    .string()
    .trim()
    .max(255)
    .nullable()
    .optional()
    .transform((value) => (value === '' ? null : value)),
}).refine((value) => Object.keys(value).length > 0, 'At least one change is required');
export const AdminProductAccessInputSchema = z.object({ product: ProductSchema });

export const OrganizationProductSchema = z.object({
  id: z.string().uuid(),
  organizationId: z.string().uuid(),
  product: ProductSchema,
  isActive: z.boolean(),
  createdAt: z.string().datetime().optional(),
  updatedAt: z.string().datetime().optional(),
});
export type OrganizationProduct = z.infer<typeof OrganizationProductSchema>;

export const CreateOrganizationProductSchema = z.object({
  product: ProductSchema,
});
export type CreateOrganizationProductDto = z.infer<typeof CreateOrganizationProductSchema>;

export const OrganizationMembershipSchema = z.object({
  id: z.string().uuid(),
  organizationId: z.string().uuid(),
  userId: z.string().uuid(),
  employeeNumber: z.string().max(50).nullable().optional(),
  subdivisionId: z.string().uuid().nullable().optional(),
  isActive: z.boolean(),
  createdAt: z.string().datetime().optional(),
  updatedAt: z.string().datetime().optional(),
});
export type OrganizationMembership = z.infer<typeof OrganizationMembershipSchema>;

export const CreateOrganizationMembershipSchema = z.object({
  userId: z.string().uuid(),
  employeeNumber: z.string().trim().min(1).max(50).nullable().optional(),
  subdivisionId: z.string().uuid().nullable().optional(),
});
export type CreateOrganizationMembershipDto = z.infer<typeof CreateOrganizationMembershipSchema>;

export const UpdateOrganizationMembershipSchema = z.object({
  employeeNumber: z.string().trim().min(1).max(50).nullable().optional(),
  subdivisionId: z.string().uuid().nullable().optional(),
  isActive: z.boolean().optional(),
});
export type UpdateOrganizationMembershipDto = z.infer<typeof UpdateOrganizationMembershipSchema>;

export const TeamSchema = z.object({
  id: z.string().uuid(),
  subdivisionId: z.string().uuid(),
  name: z.string().min(1).max(255),
  isActive: z.boolean(),
  createdAt: z.string().datetime().optional(),
  updatedAt: z.string().datetime().optional(),
});
export type Team = z.infer<typeof TeamSchema>;

export const CreateTeamSchema = z.object({
  subdivisionId: z.string().uuid(),
  name: z.string().trim().min(1).max(255),
});
export type CreateTeamDto = z.infer<typeof CreateTeamSchema>;

export const UpdateTeamSchema = z.object({
  name: z.string().trim().min(1).max(255).optional(),
  isActive: z.boolean().optional(),
});
export type UpdateTeamDto = z.infer<typeof UpdateTeamSchema>;

export const TeamMembershipSchema = z.object({
  id: z.string().uuid(),
  teamId: z.string().uuid(),
  organizationMembershipId: z.string().uuid(),
  createdAt: z.string().datetime().optional(),
});
export type TeamMembership = z.infer<typeof TeamMembershipSchema>;

export const CreateTeamMembershipSchema = z.object({
  organizationMembershipId: z.string().uuid(),
});
export type CreateTeamMembershipDto = z.infer<typeof CreateTeamMembershipSchema>;

// ─── Stage 1-3: Tenant Request Context and Product Access (CORE) ──────────────
// Explicit membership access to server-enforced products, plus the tenant
// request context / auth options endpoint contracts. Runtime enforcement lives
// in apps/api Nest guards; here we only fix the response/DTO shapes so RAIL and
// CITY clients converge on a single CORE contract for tenant context.

export const MembershipProductAccessSchema = z.object({
  id: z.string().uuid(),
  organizationMembershipId: z.string().uuid(),
  product: ProductSchema,
  isActive: z.boolean(),
  createdAt: z.string().datetime().optional(),
  updatedAt: z.string().datetime().optional(),
});
export type MembershipProductAccess = z.infer<typeof MembershipProductAccessSchema>;

// ADR-003 / #176: нормативные роли наряда — разные люди в реальной бригаде.
// Ответственный руководитель работ — начальник участка, отвечает за работу в
// целом и закрывает наряд. Допускающий — оперативный персонал на объекте,
// часто тот же человек, что и производитель работ. Пока профиль был один,
// право закрыть наряд лежало на допускающем, что противоречит и ADR-003, и
// практике.
// Issue #445: WORK_ORDER_APPROVER — согласующий наряд. Внутренний работник
// самой дистанции, дающий оперативное разрешение на работы в своей сети (у
// первого клиента это энергодиспетчер за пультом). Профиль назван по
// ДЕЙСТВИЮ, а не по должности: у другого клиента согласует кто-то ещё или не
// согласует никто, и проверка по названию должности была бы правилом в коде
// вместо настройки организации.
//
// НЕ ПУТАТЬ с внешним согласованием по пункту 8 Правил № 344 — графой 8.1
// бланка (роль подписанта SAFETY_APPROVER, ADR-007 §11): там представитель
// ЧУЖОЙ организации, обслуживающей коммуникации, подписывающий по ссылке без
// учётной записи. Здесь — штатный работник со своим членством, и его подпись
// стоит в графе 8 (решение владельца 25.09.2026, issue #467).
export const RoleProfileCodeSchema = z.enum([
  'WORKER', 'WORK_PRODUCER', 'ADMITTING_PERSON', 'RESPONSIBLE_MANAGER',
  'WORK_ORDER_ISSUER', 'WORK_ORDER_APPROVER',
  'UNIT_MANAGER', 'HSE_SPECIALIST', 'ORGANIZATION_ADMIN', 'AUDITOR',
  // Issue #509 (решение владельца 27.09.2026): руководитель дистанции или его
  // заместитель — «Утверждаю к оплате» маршрутного листа.
  'DISTANCE_HEAD',
  // Issue #523 (решения Штаба 28.09.2026): ответственный за допуски — тот, кому
  // организация доверила вносить документы-основания. Право вносить даёт РОЛЬ,
  // а не должность: у одного клиента это инженер по охране труда, у другого
  // кадровик, и у инженера по ОТ роль можно снять, не перестав быть инженером.
  'CLEARANCE_OFFICER',
  // Решение Штаба 30.09.2026: читатель журнала регистрации инструктажа —
  // роль ровно с одним правом (BRIEFING_JOURNAL_READ). По умолчанию право
  // дают профили тех, кому журнал нужен по работе; эта роль — для того, кому
  // организация выдаёт журнал отдельно (например, администратору организации,
  // которому по решению #440 наряды не открыты).
  'BRIEFING_JOURNAL_READER',
  // Issue #510, ADR-008 §10: бухгалтерия — читает и выгружает утверждённые
  // маршрутные листы (право ROUTE_SHEET_READ). Роль, а не должность:
  // организация выдаёт её тому, кто считает зарплату, — или администратору,
  // которому содержимое листов иначе не открыто.
  'ACCOUNTANT',
  // БЭМ (доступы и роли в сборке bem-preview): бортовой электромеханик — тот,
  // кто выполняет осмотры и сдаёт СВОЙ рейс и отмечает свои маневровые работы.
  // Роль, а не признак «работник»: WORKER есть у каждого сотрудника, в том числе
  // у мастера и начальников, и право вести рейс от него бы им не принадлежало
  // по должности. Назначает руководитель на экране «Сотрудники»; без профессии
  // бортмеханика и действующих допусков роль не выдаётся.
  'ONBOARD_CREW',
  // БЭМ, журнал дежурства: представитель сторонней организации, которая
  // заправляет WG (у первого клиента — ТОО «Сұңқар»). Видит только WG —
  // номер, место, топливо, воду, состояние и историю — и записывает
  // заправку. Замечаний, смен и раздела мастера роль не открывает.
  'FUEL_SUPPLIER',
]);
export type RoleProfileCode = z.infer<typeof RoleProfileCodeSchema>;

export const AuthorizationScopeSchema = z.enum(['SELF', 'SUBDIVISION', 'ORGANIZATION']);
export type AuthorizationScope = z.infer<typeof AuthorizationScopeSchema>;

// Fixed capability catalog from STAGE-1-4.md. Adding a capability requires an
// explicit change here and in the server profile mapping.
// #145b: OHS_MATRIX_MANAGE — dedicated write capability for the
// work-requirement matrix (issue #145). Held by HSE_SPECIALIST; replaces
// the PERSONNEL_MANAGE placeholder that #145a used on matrix endpoints.
export const CapabilityCodeSchema = z.enum([
  'PERSONNEL_READ', 'PERSONNEL_MANAGE', 'STRUCTURE_MANAGE',
  'WORK_ORDER_READ', 'WORK_ORDER_ISSUE', 'WORK_ORDER_FORM_CREW',
  // ADR-003: старт наряда ведёт производитель работ (WORK_ORDER_OPERATE),
  // закрывает наряд ответственный руководитель (WORK_ORDER_CLOSE). Пока обе
  // обязанности покрывались одной capability, закрыть наряд не мог никто:
  // у производителя есть право, но сервер требует ответственного, а у
  // ответственного нет права — обе проверки одновременно не проходил никто.
  'WORK_ORDER_OPERATE', 'WORK_ORDER_CLOSE', 'WORK_ORDER_ADMIT', 'TARGET_BRIEFING_RECORD',
  'CLEARANCE_READ', 'CLEARANCE_EVIDENCE_MANAGE', 'CLEARANCE_CONFIG_MANAGE',
  'OHS_MATRIX_MANAGE',
  // #142: ведение справочника профессий ЕТКС. Отдельно от PERSONNEL_MANAGE:
  // кадровый администратор присваивает работнику профессию из справочника,
  // но сам справочник — нормативные формулировки ЕТКС — ведёт специалист по
  // охране труда, он же сверяет их с действующей редакцией.
  'PROFESSION_CATALOG_MANAGE',
  // Назначение маршрута и вызова работнику (контур ЭЧ/ШЧ: выезд с домашней
  // станции на обслуживание, вызов в выходной или в окно). Отдельно от
  // WORK_ORDER_ISSUE: наряд-допуск выписывает выдающий, а маршрут подчинённому
  // назначает его руководитель — это разные люди и разные документы. Пока
  // обязанности покрывались одной capability, начальник ЭЧК видел список своих
  // работников, но получал отказ при попытке назначить маршрут.
  'ROUTE_ASSIGN',
  // ADR-004: запрос подписи документа выдачи наряда. Отдельно от
  // WORK_ORDER_ISSUE: выдать наряд и удостоверить его квалифицированной ЭЦП —
  // разные действия, и второе может быть отключено (нет ключа, нет
  // провайдера) без потери первого.
  'WORK_ORDER_SIGN',
  // Issue #445: оперативное согласование наряда — разрешение на работы,
  // которое даёт согласующий своей организации (у первого клиента —
  // энергодиспетчер). Отдельно от WORK_ORDER_ADMIT: допускающий выводит
  // бригаду на место и отвечает за подготовку рабочего места, согласующий
  // отвечает за состояние сети и работ не ведёт. Одно право на двоих
  // означало бы, что каждый из них может сделать работу другого.
  'WORK_ORDER_APPROVE',
  // ADR-004 §8: отзыв ключа устройства ДРУГОГО работника — администратор
  // организации, 24 часа с момента, когда стало известно об утрате или
  // замене телефона. Свой ключ работник отзывает без capability.
  'SIGNING_DEVICE_REVOKE',
  // Issue #329 (Э3-3): замечания по пункту 26 Правил № 363 — поднять,
  // исправить срок, закрыть. Отдельно от OHS_MATRIX_MANAGE: матрицу требований
  // ведёт специалист по охране труда, а несоответствие видит тот, кто в эту
  // смену на месте, и его право сообщить о нём не должно зависеть от права
  // править нормативный справочник. Отдельно и от полномочий наряда: замечание
  // бывает и без наряда, по месту работ.
  'SAFETY_REMARK_MANAGE',
  // ADR-008 §7, §10 (issue #508): выдать работнику маршрутный лист ФРУ № 12 на
  // месяц и подписать выдачу ключом телефона — обязанность начальника цеха.
  //
  // Отдельно от ROUTE_ASSIGN: маршрут подчинённому назначает его руководитель, а
  // лист на месяц выдаёт начальник цеха, и это разные люди. Одно право на двоих
  // означало бы, что каждый может сделать работу другого.
  //
  // Отдельно и от WORK_ORDER_SIGN: то право — квалифицированная ЭЦП под
  // нарядом, а выдача листа подписывается ключом телефона. Читать лист это
  // право не даёт — чтение держится на WORK_ORDER_READ, как и у поездок.
  // Подтвердить и утвердить лист к оплате — свои права, их заводит issue #509.
  'ROUTE_SHEET_ISSUE',
  // ADR-008 §7, §10 (issue #509): «подтверждаю» под сданным листом — ключом
  // телефона. Право открывает дверь; ЧЕЙ лист можно подтвердить, решает
  // структура организации: только лист своего работника.
  'ROUTE_SHEET_CONFIRM',
  // ADR-008 §7, §10 (issue #509): «Утверждаю к оплате» — квалифицированной
  // ЭЦП, после подтверждения старшего. Отдельно от подтверждения: утверждает
  // не тот, кто подтвердил, и одно право на двоих позволило бы одному человеку
  // поставить обе подписи. Держит его только руководитель дистанции
  // (DISTANCE_HEAD, решение владельца 27.09.2026).
  'ROUTE_SHEET_APPROVE',
  // Issue #539 (решение Штаба 29.09.2026): чтение статистики времени по
  // поездкам. Отдельно от WORK_ORDER_READ: статистика считает поездки, а не
  // наряды, и администратору организации она нужна, тогда как наряды ему не
  // видны по отдельному решению владельца (#440). Пока статистику открывало
  // WORK_ORDER_READ, «дать администратору статистику» означало «дать ему
  // наряды» — то есть отменить то решение молча.
  //
  // Охват смотрящего определяет назначение, давшее это право: организация —
  // вся организация, подразделение — оно и вложенные участки, себя — только
  // свои поездки. Легаси-роль входа на охват не влияет.
  'ROUTE_STATS_READ',
  // Решение Штаба 30.09.2026: чтение журнала регистрации инструктажа
  // (приложение 17 Правил № 1019) — экран, печать и Excel. Отдельно от
  // PERSONNEL_READ: персонал видит и администратор организации, а журнал
  // несёт целевые инструктажи по нарядам, которые ему не открыты (#440).
  // Пока журнал держался на PERSONNEL_READ, «дать администратору персонал»
  // означало «дать ему журнал».
  //
  // Охват — по назначению, давшему право: организация — вся, подразделение —
  // оно и вложенные участки. Номер наряда в строке — только тем, кому виден
  // сам наряд (`work-order-visibility.ts`).
  'BRIEFING_JOURNAL_READ',
  // Issue #510, ADR-008 §10: «полномочие читать и выгружать утверждённые
  // листы (бухгалтерия)». Отдельно от WORK_ORDER_READ: оно есть у каждого
  // работника (свои поездки), и охват его — «сам». Это право открывает листы
  // ЧУЖИХ работников по охвату назначения: организация — все, подразделение —
  // оно и вложенные участки.
  'ROUTE_SHEET_READ',
  // Стенд 30.09.2026, Правила № 1019 п. 73: первичный на рабочем месте,
  // повторный и внеплановый инструктаж проводит мастер, начальник отдела или
  // цеха, непосредственный руководитель. Право — руководителю подразделения
  // по охвату (своих работников); непосредственного руководителя пускает
  // структура. Отдельно от TARGET_BRIEFING_RECORD: целевой по наряду проводят
  // лица наряда, а этот инструктаж — руководители работника.
  'WORKER_BRIEFING_CONDUCT',
  'ORGANIZATION_CONFIG_MANAGE', 'AUDIT_READ', 'AUDIT_EXPORT',
  // ─── БЭМ: доступы по должностям (сборка bem-preview) ───────────────────
  // Свои права у каждого экрана и действия сопровождения поезда; ROUTE_ASSIGN
  // и прочие права RailHub здесь не переиспользуются: у начальника ПТО он есть
  // ради маршрутов RailHub, а график рейсов БЭМ ему положен только на просмотр.
  // «READ» — смотрит; «MANAGE» — меняет (и читает). Меню и кнопки собираются из
  // этих прав, сервер проверяет те же.
  //
  // Осмотры и сдача своего рейса — бортмеханик.
  'ONBOARD_TRIP_RUN',
  // График рейсов, парк вагонов, QR-наклейки.
  'ONBOARD_SCHEDULE_READ', 'ONBOARD_SCHEDULE_MANAGE',
  // Маневровые работы: бортмеханик отмечает свои, мастер назначает и подтверждает.
  'ONBOARD_SHUNTING_OWN', 'ONBOARD_SHUNTING_READ', 'ONBOARD_SHUNTING_MANAGE',
  // Табель и переработки (свои часы бортмеханик видит без особого права).
  'ONBOARD_TIMESHEET_READ', 'ONBOARD_TIMESHEET_MANAGE',
  // Статистика: MANAGE — ещё и выгрузка в Excel, и порог «короткого осмотра».
  'ONBOARD_STATS_READ', 'ONBOARD_STATS_MANAGE',
  // Дефекты организации (свои дефекты рейса бортмеханик видит как участник рейса).
  'ONBOARD_DEFECT_READ', 'ONBOARD_DEFECT_MANAGE',
  // Сотрудники: список с ролями и допусками; MANAGE — назначение ролей наряда и рейсов.
  'ONBOARD_STAFF_READ', 'ONBOARD_STAFF_MANAGE',
  // Журнал дежурства (замечания по вагонам, дислокация, WG, события, сдача
  // смены): смотреть / вести; MANAGE — справочник вагонов, инвентарь, Excel.
  'ONBOARD_DUTY_READ', 'ONBOARD_DUTY_WRITE', 'ONBOARD_DUTY_MANAGE',
  // Топливо WG: смотреть WG и историю топлива / записать заправку. Отдельно
  // от журнала: заправщик из сторонней организации видит только WG.
  'ONBOARD_FUEL_READ', 'ONBOARD_FUEL_RECORD',
]);
export type CapabilityCode = z.infer<typeof CapabilityCodeSchema>;

// Explicit HTTP header for tenant selection. Header names are compared
// case-insensitively; this constant is the canonical spelling clients should
// send.
export const ORGANIZATION_CONTEXT_HEADER = 'X-Organization-Id';

// Перечень профилей здесь был выписан вторым списком и разошёлся с
// RoleProfileCodeSchema при добавлении RESPONSIBLE_MANAGER. Один источник:
// новый профиль попадает в контекст запроса сам, без правки в двух местах.
export const TenantContextRoleAssignmentSchema = z.object({
  profile: RoleProfileCodeSchema,
  scope: AuthorizationScopeSchema,
  subdivisionId: z.string().uuid().nullable().optional(),
});
export type TenantContextRoleAssignment = z.infer<typeof TenantContextRoleAssignmentSchema>;

export const TenantContextSchema = z.object({
  userId: z.string().uuid(),
  organizationId: z.string().uuid(),
  organizationMembershipId: z.string().uuid(),
  organizationCode: z.string(),
  organizationName: z.string(),
  organizationIsActive: z.boolean(),
  membershipIsActive: z.boolean(),
  membershipSubdivisionId: z.string().uuid().nullable().optional(),
  enabledProducts: z.array(ProductSchema),
  membershipProducts: z.array(ProductSchema),
  // Stage 1-4: active role assignments; capability guard consumes them.
  roleAssignments: z.array(TenantContextRoleAssignmentSchema),
});
export type TenantContext = z.infer<typeof TenantContextSchema>;

// #225: forward-compatible parse for `capabilities`. The server may add new
// capability codes at any time; the mobile bundle deployed on a device is
// pinned to whatever `CapabilityCodeSchema` looked like on the day it was
// built. Before this change the field was `z.array(CapabilityCodeSchema)`
// — a strict enum — so any unknown value made the whole /auth/context
// response fail parse and blocked login for every user whose role acquired
// the new capability. Now the schema accepts a bare string array, drops
// values the local bundle does not recognize, and keeps the return type
// as `CapabilityCode[]` so downstream consumers stay strictly typed. The
// filter is silent by design — new capabilities the client cannot use yet
// are functionally invisible to it, exactly what the strict enum used to
// guarantee, without the login-breaking parse failure.
const KNOWN_CAPABILITY_CODES: ReadonlySet<string> = new Set(
  CapabilityCodeSchema.options,
);

const ToleratedCapabilityListSchema = z
  .array(z.string())
  .default([])
  .transform((list) =>
    list.filter((cap): cap is CapabilityCode => KNOWN_CAPABILITY_CODES.has(cap)),
  );

export const AuthContextMembershipSchema = z.object({
  organizationMembershipId: z.string().uuid(),
  organizationId: z.string().uuid(),
  organizationCode: z.string(),
  organizationName: z.string(),
  organizationIsActive: z.boolean(),
  membershipIsActive: z.boolean(),
  /// Подразделение, к которому приписано ЭТО членство. `null` — не приписано.
  ///
  /// Issue #446: без него веб-панель не может выписать наряд. Подразделение
  /// наряда не выбирается — оно берётся у выдающего, ровно как в телефоне
  /// (там из профиля входа). Своего подразделения панель ниоткуда больше не
  /// узнаёт: справочник подразделений закрыт правом вести структуру, которого
  /// у выдающего нет и быть не должно.
  ///
  /// Аддитивно и необязательно: установленные сборки поля не читают и
  /// работают как прежде.
  membershipSubdivisionId: z.string().uuid().nullable().optional(),
  enabledProducts: z.array(ProductSchema),
  membershipProducts: z.array(ProductSchema),
  // Effective profile grants for capability-aware clients. Route guards still
  // evaluate assignment scope and remain the authorization source of truth.
  // See #225 — parse tolerates capability codes the local bundle does not
  // know and drops them.
  capabilities: ToleratedCapabilityListSchema,
  /// Показывать ли в панели раздел «Наряд-допуск» (стенд 30.09.2026).
  ///
  /// Право `WORK_ORDER_READ` есть у каждого по базовой роли «работник» — про
  /// его СОБСТВЕННЫЕ наряды. У администратора организации других прав на
  /// наряды нет (#440), и раздел у него стоял пустым. `true` — право шире
  /// «сам» или в организации есть наряд, который ему виден; `false` — читать
  /// в разделе нечего. Нет поля (старый сервер) — решает одно право, как было.
  workOrderSectionVisible: z.boolean().optional(),
});
export type AuthContextMembership = z.infer<typeof AuthContextMembershipSchema>;

// GET /auth/context — list every active membership the caller has so a client
// can present an Organization selector or auto-select the single-membership
// compatibility path.
export const AuthContextResponseSchema = z.object({
  userId: z.string().uuid(),
  memberships: z.array(AuthContextMembershipSchema),
});
export type AuthContextResponse = z.infer<typeof AuthContextResponseSchema>;

// ─── Stage 1-4: Capability and Personnel Authorization (CORE) ─────────────────
// Fixed system role profiles, authorization scopes, and the capability
// catalog that profiles map to. The mapping itself is code-defined and lives
// in the API (apps/api/src/tenant/capability-registry.ts); this file only
// fixes the identifiers so clients and tests never invent their own strings.

export const MembershipRoleAssignmentSchema = z.object({
  id: z.string().uuid(),
  organizationMembershipId: z.string().uuid(),
  profile: RoleProfileCodeSchema,
  scope: AuthorizationScopeSchema,
  subdivisionId: z.string().uuid().nullable().optional(),
  isActive: z.boolean(),
  createdAt: z.string().datetime().optional(),
  updatedAt: z.string().datetime().optional(),
});
export type MembershipRoleAssignment = z.infer<typeof MembershipRoleAssignmentSchema>;

// Refinement invariant that mirrors the SQL migration guard: SUBDIVISION
// scope requires subdivisionId; SELF / ORGANIZATION scope forbids it.
export function isAssignmentShapeValid(a: {
  scope: AuthorizationScope;
  subdivisionId?: string | null;
}): boolean {
  if (a.scope === 'SUBDIVISION') return !!a.subdivisionId;
  return !a.subdivisionId;
}

export const AdminRoleAssignmentInputSchema = z.object({
  profile: RoleProfileCodeSchema,
  scope: AuthorizationScopeSchema,
  subdivisionId: z.string().uuid().nullable().optional(),
}).refine(isAssignmentShapeValid, 'Subdivision scope requires subdivisionId and other scopes forbid it');

// ─── Stage 1-5: Clearance / Audit Ownership (CORE) ───────────────────────────
// Two-level Clearance model + first-class Audit ownership per ADR-001. Runtime
// enforcement lives in the API guards; here we only fix the response shapes so
// clients converge on a single CORE contract.

export const ClearanceTypeTemplateSchema = z.object({
  id: z.string().uuid(),
  code: z.string().min(1).max(100),
  name: z.string().min(1).max(255),
  category: z.enum(['BRIEFING', 'ELECTRICAL_SAFETY', 'MEDICAL']),
  kind: z.enum(['PERIODIC', 'ONE_TIME', 'EVENT']),
  createdAt: z.string().datetime().optional(),
  updatedAt: z.string().datetime().optional(),
});
export type ClearanceTypeTemplate = z.infer<typeof ClearanceTypeTemplateSchema>;

export const OrganizationClearanceTypeSchema = z.object({
  id: z.string().uuid(),
  organizationId: z.string().uuid(),
  templateId: z.string().uuid(),
  localCode: z.string().min(1).max(100),
  localName: z.string().min(1).max(255),
  renewalPeriodDays: z.number().int().nullable().optional(),
  warningThresholdDays: z.number().int(),
  isRequired: z.boolean(),
  isActive: z.boolean(),
  requiredProduct: ProductSchema.nullable().optional(),
  // Issue #146: вид инструктажа для связки с матрицей требований; null =
  // тип не участвует в матричной связке (только isRequired / legacy путь).
  briefingType: CoreBriefingTypeSchema.nullable().optional(),
  createdAt: z.string().datetime().optional(),
  updatedAt: z.string().datetime().optional(),
});
export type OrganizationClearanceType = z.infer<typeof OrganizationClearanceTypeSchema>;

export const CoreClearanceOwnershipSchema = z.object({
  organizationId: z.string().uuid().nullable().optional(),
  organizationMembershipId: z.string().uuid().nullable().optional(),
  organizationClearanceTypeId: z.string().uuid().nullable().optional(),
});
export type CoreClearanceOwnership = z.infer<typeof CoreClearanceOwnershipSchema>;

export const CoreAuditEventOwnershipSchema = z.object({
  organizationId: z.string().uuid().nullable().optional(),
  actorUserId: z.string().uuid().nullable().optional(),
  actorMembershipId: z.string().uuid().nullable().optional(),
  product: ProductSchema.nullable().optional(),
  entityType: z.string().max(80).nullable().optional(),
  entityId: z.string().max(80).nullable().optional(),
});
export type CoreAuditEventOwnership = z.infer<typeof CoreAuditEventOwnershipSchema>;

// ─── Stage 1-6 additive CORE work ownership contracts ─────────────────────
// Every WorkSite/WorkOrder eventually resolves to an Organization + Product;
// membership references coexist with legacy User ids until Stage 1-9. All
// fields are optional at this Stage; final NOT NULL enforcement belongs to
// Stage 1-9. Nothing here rewrites the existing WorkSite/WorkOrder response
// projections — those keep working through Stage 1-9.

export const CoreWorkSiteOwnershipSchema = z.object({
  organizationId: z.string().uuid().nullable().optional(),
  product: ProductSchema.nullable().optional(),
});
export type CoreWorkSiteOwnership = z.infer<typeof CoreWorkSiteOwnershipSchema>;

export const CoreWorkOrderOwnershipSchema = z.object({
  organizationId: z.string().uuid().nullable().optional(),
  product: ProductSchema.nullable().optional(),
  issuerMembershipId: z.string().uuid().nullable().optional(),
  supervisorMembershipId: z.string().uuid().nullable().optional(),
  producerMembershipId: z.string().uuid().nullable().optional(),
});
export type CoreWorkOrderOwnership = z.infer<typeof CoreWorkOrderOwnershipSchema>;

export const CoreWorkOrderMemberOwnershipSchema = z.object({
  workerMembershipId: z.string().uuid().nullable().optional(),
  targetBriefingByMembershipId: z.string().uuid().nullable().optional(),
});
export type CoreWorkOrderMemberOwnership = z.infer<typeof CoreWorkOrderMemberOwnershipSchema>;

// ─── Issue #145 (part 2a): API contracts for the work-requirement matrix ─────
// Three tenant-scoped resources built on top of #144's schema:
//   OrganizationWorkType, OrganizationRiskFactor, WorkRequirementMatrix.
// The matrix uses composite tenant-guard FKs from #219, so cross-tenant
// linkage is blocked at the DB level; server-side owner-first filtering also
// throws before hitting the FK to give clients a readable 404/400.
//
// Placeholder capability on write-endpoints is PERSONNEL_MANAGE — will be
// replaced by a dedicated OHS_MATRIX_MANAGE in part 2b (#145b).

const ElectricalGroupSchema = z.enum(['II', 'III', 'IV', 'V']);
export type CoreElectricalGroup = z.infer<typeof ElectricalGroupSchema>;

// Issue #146: единый enum объявлен выше (CoreBriefingTypeSchema) — здесь
// только локальный алиас, чтобы не дублировать перечень.
const BriefingTypeSchema = CoreBriefingTypeSchema;
export type CoreBriefingType = z.infer<typeof BriefingTypeSchema>;

const MedicalExaminationRequirementSchema = z.enum(['NOT_REQUIRED', 'PERIODIC']);
export type CoreMedicalExaminationRequirement = z.infer<typeof MedicalExaminationRequirementSchema>;

// ── OrganizationWorkType ─────────────────────────────────────────────────────

export const OrganizationWorkTypeSchema = z.object({
  id: z.string().uuid(),
  organizationId: z.string().uuid(),
  name: z.string(),
  code: z.string(),
  provisional: z.boolean(),
  /// Пункт 8 Правил № 344: работы вблизи действующих железнодорожных и
  /// автомобильных дорог, ЛЭП, газо- и теплопроводов, иных действующих
  /// коммуникаций, а также ВСЕ земляные работы предварительно согласовываются
  /// с организациями, обслуживающими эти объекты.
  ///
  /// Это УМОЛЧАНИЕ, которое справочник предлагает выдающему при выписке. Само
  /// требование живёт на наряде (`WorkOrder.requiresSafetyApproval`) и там
  /// замирает: выдающий подтверждает или снимает предложенное, и правка
  /// справочника уже выписанный наряд не трогает.
  requiresSafetyApprovalDefault: z.boolean(),
  /// Пункт 300 Правил № 222 (issue #469): относится ли этот вид работ к
  /// земляным работам в охранных зонах подземных коммуникаций.
  ///
  /// Тоже УМОЛЧАНИЕ, и по более сильному доводу, чем у согласования выше:
  /// справочник знает вид работ, но не место их ведения — та же траншея вне
  /// охранной зоны письменного разрешения не требует.
  ///
  /// Необязательно в схеме: установленная панель и установленный телефон
  /// читают справочник без этого поля, и отказ разбора оставил бы их без
  /// списка видов работ вовсе.
  excavationInProtectedZoneDefault: z.boolean().optional(),
  createdAt: z.string().datetime(),
  updatedAt: z.string().datetime(),
});
export type OrganizationWorkType = z.infer<typeof OrganizationWorkTypeSchema>;

export const CreateOrganizationWorkTypeSchema = z.object({
  name: z.string().trim().min(1).max(255),
  code: z.string().trim().min(1).max(50),
  provisional: z.boolean().optional(),
  /// Умолчание согласования по пункту 8 Правил № 344. Необязательно: вид
  /// работы, заведённый без него, ничего не предлагает — то же, что сегодня.
  requiresSafetyApprovalDefault: z.boolean().optional(),
  /// Умолчание признака земляных работ в охранной зоне (issue #469).
  /// Необязательно: вид работы, заведённый без него, ничего не предлагает.
  excavationInProtectedZoneDefault: z.boolean().optional(),
});
export type CreateOrganizationWorkTypeDto = z.infer<typeof CreateOrganizationWorkTypeSchema>;

export const UpdateOrganizationWorkTypeSchema = z.object({
  name: z.string().trim().min(1).max(255).optional(),
  code: z.string().trim().min(1).max(50).optional(),
  provisional: z.boolean().optional(),
  requiresSafetyApprovalDefault: z.boolean().optional(),
  excavationInProtectedZoneDefault: z.boolean().optional(),
});
export type UpdateOrganizationWorkTypeDto = z.infer<typeof UpdateOrganizationWorkTypeSchema>;

// ── OrganizationRiskFactor ───────────────────────────────────────────────────

export const OrganizationRiskFactorSchema = z.object({
  id: z.string().uuid(),
  organizationId: z.string().uuid(),
  name: z.string(),
  code: z.string(),
  provisional: z.boolean(),
  createdAt: z.string().datetime(),
  updatedAt: z.string().datetime(),
});
export type OrganizationRiskFactor = z.infer<typeof OrganizationRiskFactorSchema>;

export const CreateOrganizationRiskFactorSchema = z.object({
  name: z.string().trim().min(1).max(255),
  code: z.string().trim().min(1).max(50),
  provisional: z.boolean().optional(),
});
export type CreateOrganizationRiskFactorDto = z.infer<typeof CreateOrganizationRiskFactorSchema>;

export const UpdateOrganizationRiskFactorSchema = z.object({
  name: z.string().trim().min(1).max(255).optional(),
  code: z.string().trim().min(1).max(50).optional(),
  provisional: z.boolean().optional(),
});
export type UpdateOrganizationRiskFactorDto = z.infer<typeof UpdateOrganizationRiskFactorSchema>;

// ── WorkRequirementMatrix ────────────────────────────────────────────────────
//
// One row per (organizationId, workTypeId) — enforced by DB unique constraint.
// The row edits in place: PUT replaces the whole payload including the
// risk-factor association set. `riskFactorIds` MUST all belong to the same
// organization; sever-side check throws before the composite FK does, so the
// client sees a 400 with a readable message rather than an FK violation.

export const WorkRequirementMatrixRowSchema = z.object({
  id: z.string().uuid(),
  organizationId: z.string().uuid(),
  workTypeId: z.string().uuid(),
  requiredElectricalGroup: ElectricalGroupSchema.nullable(),
  /// Issue #419 (карточка B-057): напряжение электроустановки, на которой этот
  /// вид работы ведётся обычно. Стоит рядом с требуемой группой намеренно —
  /// именно напряжение задаёт её порог по пунктам 33, 35, 37 и 38 Правил
  /// № 222. Умолчание: при выписке наряда оно предлагается выдающему.
  voltageClass: CoreInstallationVoltageClassSchema.nullable(),
  requiredBriefingTypes: z.array(BriefingTypeSchema),
  medicalExaminationRequirement: MedicalExaminationRequirementSchema.nullable(),
  requiresObserver: z.boolean(),
  /// Issue #470 (карточка B-089): требуется ли ПРИСУТСТВИЕ представителя
  /// обслуживающей организации на месте работ.
  ///
  /// Это НЕ согласование пункта 8 Правил № 344 (карточка B-034): согласование
  /// происходит до выписки, за столом, и удостоверяется подписью выдающего.
  /// Здесь — человек из другой организации, физически стоящий у места работ,
  /// и требуют этого отдельные пункты Правил № 222: 302 (контрольное вскрытие
  /// КЛ под надзором персонала владельца линии), 431 (пересечение ВЛ с
  /// транспортными магистралями — представитель службы движения), 527
  /// (шунтирующие перемычки при прокладке трубопроводов у путей —
  /// представитель дистанции электроснабжения или СЦБ), 537 (пересечение
  /// проводов ВЛС с контактной сетью — представитель района контактной сети).
  ///
  /// Требуется НЕ ВСЕГДА, и поэтому это признак вида работ, а не свойство
  /// наряда: норма называет присутствие по видам работ поимённо.
  ///
  /// Необязательно в схеме по тому же доводу, что `excavationInProtectedZoneDefault`
  /// у справочника видов работ: установленная панель и установленный телефон
  /// читают строку матрицы без этого поля, и отказ разбора оставил бы их без
  /// требований вовсе.
  requiresServiceOrgPresence: z.boolean().optional(),
  /// ЧЕЙ представитель — в родительном падеже, как он встанет в мероприятие:
  /// «владельца кабельной линии», «района контактной сети», «службы
  /// движения». Норма называет разных представителей в разных пунктах, и
  /// признак без этого текста не говорит бригаде, кого вызывать.
  ///
  /// ФИО представителя здесь нет намеренно: норма его записывать не требует,
  /// а на момент заполнения справочника оно и не известно — справочник
  /// описывает вид работ, а не конкретный наряд.
  serviceOrgPresenceParty: z.string().nullable().optional(),
  riskFactorIds: z.array(z.string().uuid()),
  provisional: z.boolean(),
  createdAt: z.string().datetime(),
  updatedAt: z.string().datetime(),
});
export type WorkRequirementMatrixRow = z.infer<typeof WorkRequirementMatrixRowSchema>;

export const UpsertWorkRequirementMatrixSchema = z.object({
  requiredElectricalGroup: ElectricalGroupSchema.nullable().optional(),
  /// Issue #419 (карточка B-057). Опущено — напряжение у строки снимается:
  /// PUT перезаписывает строку целиком, и опущенное поле здесь означает
  /// «не задано», а не «оставить как было».
  voltageClass: CoreInstallationVoltageClassSchema.nullable().optional(),
  requiredBriefingTypes: z.array(BriefingTypeSchema).default([]),
  medicalExaminationRequirement: MedicalExaminationRequirementSchema.nullable().optional(),
  requiresObserver: z.boolean().optional(),
  /// Issue #470 (карточка B-089). Опущено — признак снимается: PUT
  /// перезаписывает строку целиком.
  requiresServiceOrgPresence: z.boolean().optional(),
  /// Чей представитель. Пустая строка не принимается: признак без ответа на
  /// вопрос «кого вызывать» превращает мероприятие в графе 5.10 в
  /// незаполнимое предписание. Сочетание «признак есть, текста нет»
  /// отвергается сервисом, а не схемой, — человеку нужен ответ словами, а не
  /// разбор ошибки схемы.
  serviceOrgPresenceParty: z.string().trim().min(1).max(255).nullable().optional(),
  riskFactorIds: z.array(z.string().uuid()).default([]),
  provisional: z.boolean().optional(),
});
export type UpsertWorkRequirementMatrixDto = z.infer<typeof UpsertWorkRequirementMatrixSchema>;

// ─── Issue #470 (карточка B-089): присутствие представителя на месте работ ────
//
// Признак живёт в строке матрицы (выше), а в наряд он попадает МЕРОПРИЯТИЕМ
// графы 5.10 бланка № 344 («дополнительные мероприятия»). Почему так, а не
// новой колонкой наряда: присутствие представителя — это то, что кто-то
// обязан обеспечить ДО допуска, и под графой 5.10 уже стоит подпись графы 7
// («Мероприятия выполнил(и)»). Заведи колонку — пришлось бы строить второй
// путь к той же подписи.
//
// Текст собирается здесь, а не в экране и не в сервисе, по тому же доводу, что
// и перечень граф `SAFETY_MEASURE_ITEMS`: его подставляет сервер при выписке, а
// показывают выдающему оба клиента, и два набора слов на одно мероприятие
// означали бы, что человек видит не то, что подписывает.

/** Графа бланка, в которую встаёт мероприятие: «дополнительные мероприятия». */
export const SERVICE_ORG_PRESENCE_ITEM = '5.10' as const;

/**
 * Текст мероприятия о присутствии представителя.
 *
 * Норма названа в самом тексте («Правила № 222»), потому что мероприятие
 * читают на месте работ, и вопрос «на каком основании нас ждут представителя»
 * задают там же. Пункт не называется: он зависит от вида работ, а
 * справочник ведёт организация — назвать в тексте чужой пункт хуже, чем не
 * назвать никакого.
 */
export function serviceOrgPresenceMeasure(party: string): string {
  return `Присутствие на месте работ представителя ${party.trim()} — Правила № 222`;
}

/**
 * Требует ли строка матрицы присутствия представителя — и чьего.
 *
 * `null` — не требует, либо строки нет, либо она прочитана старой выборкой без
 * этих полей. Признак без текста тоже даёт `null`: такую строку сервер
 * записать не даёт, а если она уже лежит в базе с прошлых времён, то
 * мероприятие «Присутствие представителя  — Правила № 222» не сказало бы
 * бригаде ничего.
 */
export function serviceOrgPresenceRequirement(
  row:
    | { requiresServiceOrgPresence?: boolean | null; serviceOrgPresenceParty?: string | null }
    | null
    | undefined,
): { party: string } | null {
  if (!row?.requiresServiceOrgPresence) return null;
  const party = row.serviceOrgPresenceParty?.trim();
  return party ? { party } : null;
}

/**
 * Текст графы 5.10 после смены требования: было `previousParty`, стало
 * `nextParty` (любое из них `null` — требования не было или не стало).
 *
 * Одна операция на оба случая намеренно. Форма выписки подставляет мероприятие
 * заранее — иначе выдающему нечего назначать исполнителя и нечего подписывать,
 * — а вид работ он меняет столько раз, сколько нужно. Две отдельные операции
 * («вставить» и «убрать») оставляли бы после смены вида работ мероприятие о
 * представителе, которого эта работа не требует, и выдающему пришлось бы
 * замечать и стирать его вручную.
 *
 * ЧТО СОХРАНЯЕТСЯ. Всё, что вписал человек: убирается РОВНО строка прежнего
 * мероприятия, слово в слово. Сравнение построчное, а не подстрокой — иначе
 * своей считался бы текст, лишь начинающийся теми же словами.
 *
 * ЧТО НЕ ДУБЛИРУЕТСЯ. Мероприятие, уже стоящее в тексте, второй раз не
 * дописывается: сервер вызывает то же правило при выписке, и повторная
 * подстановка дала бы графу с двумя одинаковыми строками.
 */
export function reconcileServiceOrgPresenceText(
  content: string,
  previousParty: string | null,
  nextParty: string | null,
): string {
  const removed = previousParty === null
    ? content
    : dropLine(content, serviceOrgPresenceMeasure(previousParty));
  if (nextParty === null) return removed;

  const next = serviceOrgPresenceMeasure(nextParty);
  const kept = removed.trim();
  if (kept.length === 0) return next;
  if (kept.split('\n').some((line) => line.trim() === next)) return kept;
  return `${kept}\n${next}`;
}

/** То же для единственного случая «требование есть»: подстановка при выписке. */
export function withServiceOrgPresenceText(content: string, party: string): string {
  return reconcileServiceOrgPresenceText(content, null, party);
}

function dropLine(content: string, line: string): string {
  return content
    .split('\n')
    .filter((candidate) => candidate.trim() !== line)
    .join('\n');
}

// ─── Issue #329 (Э3-3): замечания по пункту 26 Правил № 363 ──────────────────
//
// Пункт 26 — мониторинг: наблюдение за опасностями, выявление новых и принятие
// мер по устранению выявленных несоответствий. Замечание отвечает на три
// вопроса: кто заметил, к какому сроку устранить, устранено ли.
//
// ГРАНИЦА, КОТОРУЮ ДЕРЖИМ СТРОГО. Это не План корректирующих мер: тот
// разрабатывается в ЕЦС, согласуется со специализированной организацией и
// утверждается ЭЦП (пункты 23, 25). Юридическая сила — у документа в ЕЦС, и
// вторая его версия здесь была бы заведомо слабее. Замечание может стать
// основанием для строки Плана и этим ограничивается.
//
// Это и не мероприятие пункта 5 бланка № 344: то — предписание выдающего до
// начала работы и часть подписанного документа наряда.

export const SafetyRemarkSchema = z.object({
  id: z.string().uuid(),
  organizationId: z.string().uuid(),
  /// К чему привязано. Хотя бы одно из двух заполнено всегда — замечание,
  /// не привязанное ни к наряду, ни к месту работ, некому адресовать.
  workOrderId: z.string().uuid().nullable(),
  workOrderNumber: z.string().nullable(),
  workSiteId: z.string().uuid().nullable(),
  workSiteName: z.string().nullable(),
  description: z.string(),
  raisedByUserId: z.string().uuid(),
  raisedByName: z.string().nullable(),
  raisedAt: z.string().datetime(),
  dueDate: z.string().datetime(),
  /// Пусто — замечание открыто. Заполнено — устранено, и тогда заполнены все
  /// три поля закрытия сразу.
  closedAt: z.string().datetime().nullable(),
  closedByUserId: z.string().uuid().nullable(),
  closedByName: z.string().nullable(),
  closureNote: z.string().nullable(),
  /// Производное: открыто и срок прошёл. Считает сервер, а не клиент, — иначе
  /// просроченность зависела бы от часов телефона.
  overdue: z.boolean(),
  createdAt: z.string().datetime(),
  updatedAt: z.string().datetime(),
});
export type SafetyRemark = z.infer<typeof SafetyRemarkSchema>;

export const CreateSafetyRemarkSchema = z
  .object({
    workOrderId: z.string().uuid().nullable().optional(),
    workSiteId: z.string().uuid().nullable().optional(),
    description: z.string().trim().min(1).max(2000),
    /// Срок устранения. Обязателен: замечание без срока не отличимо от заметки
    /// на память — по нему нельзя сказать, просрочено оно или нет.
    dueDate: z.string().datetime(),
  })
  .refine((dto) => Boolean(dto.workOrderId) || Boolean(dto.workSiteId), {
    message: 'Замечание привязывается к наряду или к месту работ',
    path: ['workOrderId'],
  });
export type CreateSafetyRemarkDto = z.infer<typeof CreateSafetyRemarkSchema>;

/// Правка открытого замечания. Привязка и автор не меняются: замечание —
/// наблюдение конкретного человека в конкретном месте, и подмена того или
/// другого сделала бы из него другое замечание.
export const UpdateSafetyRemarkSchema = z.object({
  description: z.string().trim().min(1).max(2000).optional(),
  dueDate: z.string().datetime().optional(),
});
export type UpdateSafetyRemarkDto = z.infer<typeof UpdateSafetyRemarkSchema>;

/// Закрытие. Чем устранено — обязательно: отметка «устранено» без ответа на
/// вопрос «чем» ничего не удостоверяет.
export const CloseSafetyRemarkSchema = z.object({
  closureNote: z.string().trim().min(1).max(2000),
});
export type CloseSafetyRemarkDto = z.infer<typeof CloseSafetyRemarkSchema>;

// ─── Issue #142 (2/3): справочник профессий ЕТКС + привязка к работнику ──────
//
// OrganizationProfession — tenant-scoped справочник профессий по ЕТКС
// (схема заведена в #141). Ключ записи внутри организации — пара
// (выпуск ЕТКС, номер записи в выпуске). Диапазон квалификационных разрядов
// [min, max] берётся из самой ЕТКС; присвоенный работнику разряд обязан
// лежать внутри него — это проверяет сервер, а не клиент.
//
// `provisional` = формулировка/код/диапазон ещё не сверены с действующей
// редакцией ЕТКС РК и требуют проверки специалистом по охране труда.
// Обоснование по каждой демо-записи — docs/reference/etks-issue-9-professions.md.

export const OrganizationProfessionSchema = z.object({
  id: z.string().uuid(),
  organizationId: z.string().uuid(),
  name: z.string(),
  etksIssueNumber: z.number().int(),
  etksEntryNumber: z.string(),
  minQualificationGrade: z.number().int(),
  maxQualificationGrade: z.number().int(),
  occupationClassifierGroup: z.string().nullable(),
  provisional: z.boolean(),
  createdAt: z.string().datetime(),
  updatedAt: z.string().datetime(),
});
export type OrganizationProfession = z.infer<typeof OrganizationProfessionSchema>;

const professionGradeBounds = {
  min: z.number().int().positive().max(99),
  max: z.number().int().positive().max(99),
};

export const CreateOrganizationProfessionSchema = z
  .object({
    name: z.string().trim().min(1).max(255),
    etksIssueNumber: z.number().int().positive(),
    etksEntryNumber: z.string().trim().min(1).max(20),
    minQualificationGrade: professionGradeBounds.min,
    maxQualificationGrade: professionGradeBounds.max,
    occupationClassifierGroup: z.string().trim().max(50).nullable().optional(),
    provisional: z.boolean().optional(),
  })
  .refine((dto) => dto.minQualificationGrade <= dto.maxQualificationGrade, {
    message: 'Минимальный разряд не может быть больше максимального',
    path: ['minQualificationGrade'],
  });
export type CreateOrganizationProfessionDto = z.infer<typeof CreateOrganizationProfessionSchema>;

// PATCH: любое подмножество полей. Пара min/max проверяется здесь только
// когда пришли оба значения; при частичном обновлении итоговый диапазон
// сверяется на сервере с уже сохранённой записью.
export const UpdateOrganizationProfessionSchema = z
  .object({
    name: z.string().trim().min(1).max(255).optional(),
    etksIssueNumber: z.number().int().positive().optional(),
    etksEntryNumber: z.string().trim().min(1).max(20).optional(),
    minQualificationGrade: professionGradeBounds.min.optional(),
    maxQualificationGrade: professionGradeBounds.max.optional(),
    occupationClassifierGroup: z.string().trim().max(50).nullable().optional(),
    provisional: z.boolean().optional(),
  })
  .refine(
    (dto) =>
      dto.minQualificationGrade === undefined ||
      dto.maxQualificationGrade === undefined ||
      dto.minQualificationGrade <= dto.maxQualificationGrade,
    {
      message: 'Минимальный разряд не может быть больше максимального',
      path: ['minQualificationGrade'],
    },
  );
export type UpdateOrganizationProfessionDto = z.infer<typeof UpdateOrganizationProfessionSchema>;

// Привязка профессии к работнику (OrganizationMembership). Разряд без
// профессии не принимается ни схемой, ни БД (CHECK в миграции #142).
export const MembershipProfessionAssignmentSchema = z
  .object({
    professionId: z.string().uuid().nullable().optional(),
    qualificationGrade: z.number().int().positive().max(99).nullable().optional(),
  })
  .refine(
    (dto) => !(dto.qualificationGrade !== null && dto.qualificationGrade !== undefined && dto.professionId === null),
    {
      message: 'Квалификационный разряд нельзя задать без профессии',
      path: ['qualificationGrade'],
    },
  );
export type MembershipProfessionAssignmentDto = z.infer<typeof MembershipProfessionAssignmentSchema>;

// ADR-004 §12: внесение ИИН работника. Пустая строка или null снимают ИИН.
// Значение принимается «как из удостоверения» — пробелы и дефисы сервер
// уберёт сам; проверка формата (12 цифр) на сервере, не в форме, потому что
// форма — не граница доверия.
export const SetUserIinSchema = z.object({
  iin: z.string().max(20).nullable(),
});
export type SetUserIinDto = z.infer<typeof SetUserIinSchema>;

// Ответ никогда не содержит ИИН целиком: только признак «внесён» и маска с
// последними четырьмя цифрами — этого достаточно, чтобы кадровик убедился,
// что внёс того человека.
export const UserIinStateSchema = z.object({
  iinSet: z.boolean(),
  iinMasked: z.string().nullable(),
});
export type UserIinState = z.infer<typeof UserIinStateSchema>;

// ─── ADR-004: подписанные документы ───────────────────────────────────────────

/// Уровень подтверждения. Оба используют один канон, хеш и хранилище;
/// различаются ключ и юридический вес (ADR-004 §2).
export const SignatureLevelSchema = z.enum(['QUALIFIED_ECP', 'INTERNAL_DEVICE']);
export type SignatureLevel = z.infer<typeof SignatureLevelSchema>;

/// Способ, которым работник получил целевой инструктаж, когда подтверждение
/// ставит не он сам. Входит в канон TARGET_BRIEFING_PROXY_ACK: подпись
/// отметившего покрывает и то, КАК инструктаж был получен, иначе отметка
/// утверждала бы только факт, а при разборе спрашивают про способ.
export const TargetBriefingProxyMethodSchema = z.enum(['PROXY_VERBAL']);
export type TargetBriefingProxyMethod = z.infer<typeof TargetBriefingProxyMethodSchema>;

/// Подписывается конкретное действие, а не «наряд вообще» (ADR-004 §3).
export const SignedDocumentKindSchema = z.enum([
  'WORK_ORDER_ISSUE',
  'WORK_ORDER_ADMISSION',
  /// ADR-005 §9: кратен сменам — производитель работ назначается на каждую
  /// смену (Правила № 344, пункты 24 и 29).
  'WORK_ORDER_START',
  'TARGET_BRIEFING_ACK',
  /// ADR-004 §4: отметка за работника, у которого нет смартфона. Актор —
  /// отметивший (производитель работ или ответственный руководитель работ),
  /// субъект — работник, способ отметки входит в канон. Отдельный вид, а не
  /// расширение TARGET_BRIEFING_ACK: там подписант обязан быть самим членом
  /// бригады, и подписать за другого его ключом — ровно та подделка, которую
  /// контур предотвращает.
  'TARGET_BRIEFING_PROXY_ACK',
  /// Issue #531: проведение целевого инструктажа — подпись инструктирующего
  /// по каждому работнику, с результатом проверки знаний.
  'TARGET_BRIEFING_CONDUCT',
  /// @deprecated ADR-005 §7: заменён на CLOSURE_HANDOVER и CLOSURE_ACCEPT.
  /// Значение сохранено — на нём стоят уже выпущенные подписи, и они обязаны
  /// остаться проверяемыми. Новые документы этого вида не выпускаются.
  'WORK_ORDER_CLOSURE',
  'WORK_ORDER_ANNULMENT',
  /// ADR-004 §3a (issue #445): согласование наряда согласующим САМОЙ
  /// организации — разрешение на работы в её сети. Графа 8 бланка № 344
  /// (решение владельца 25.09.2026, issue #467). Уровень QUALIFIED_ECP, как у
  /// выдачи: согласующий работает за пультом при связи.
  ///
  /// НЕ ПУТАТЬ с ролью подписанта SAFETY_APPROVER: та — графа 8.1, внешнее
  /// согласование по пункту 8 Правил № 344 представителем ЧУЖОЙ организации, и
  /// живёт оно подписью под документом выдачи, а не своим документом.
  'WORK_ORDER_APPROVAL',
  /// ADR-005 §3: Лист ежесменной оценки рисков (Правила № 363, пункт 11).
  'SHIFT_RISK_ASSESSMENT',
  /// ADR-005 §1: «допуск разрешаю» допускающего в смене (№ 344, пункт 24).
  'SHIFT_EXTENSION',
  /// ADR-005 §7: сдача рабочего места производителем работ.
  'CLOSURE_HANDOVER',
  /// ADR-005 §7: приёмка рабочего места допускающим, иначе выдающим наряд.
  'CLOSURE_ACCEPT',
  /// ADR-005 §11: прекращение работ допускающим (№ 344, пункты 25 и 27).
  /// Кратность — по числу приостановок: работы по одному наряду прекращают
  /// и возобновляют не один раз, и каждое прекращение — свой документ со
  /// своим временем и своей причиной.
  'SUSPENSION',
  /// ADR-008 §7 (issue #507): «Выдал начальник цеха» — отметка о выдаче
  /// маршрутного листа ФРУ № 12. Канон — шапка листа (номер, работник, месяц,
  /// приписка), подпись одна, ключом телефона.
  ///
  /// Отдельный документ, а не первая подпись под листом: выдача ставится в
  /// начале месяца, когда строк ещё нет, а по ADR-007 новая подпись не может
  /// менять то, что проверяет прежняя.
  ///
  /// Основание документа — МАРШРУТНЫЙ ЛИСТ, а не наряд (§8). Канон и приём —
  /// issue #508.
  'ROUTE_SHEET_ISSUE',
  /// ADR-008 §7 (issue #507): сам маршрутный лист за календарный месяц. Канон
  /// — шапка, хеш документа выдачи и все строки; три подписи (работник
  /// «сдал», старший «подтверждаю», начальник ШЧ «Утверждаю к оплате») стоят
  /// над ОДНИМИ И ТЕМИ ЖЕ байтами, то есть тремя подписями под одним
  /// документом, а не тремя документами.
  ///
  /// Кратность — по числу сборок листа: возвращённый на исправление экземпляр
  /// остаётся в истории, а собранный заново — новый документ (решение
  /// владельца В9). Канон, приём и проверка — issue #509.
  'ROUTE_SHEET',
  /// Issue #543 (ADR-008 §8a): запись журнала регистрации инструктажа по
  /// работнику без наряда — первичный на рабочем месте, повторный,
  /// внеплановый. Один документ, две подписи ключом телефона: инструктирующего
  /// (`BRIEFING_INSTRUCTOR`) и инструктируемого (`BRIEFING_RECIPIENT`).
  /// Основание — запись инструктажа, не наряд.
  'WORKER_BRIEFING',
]);
export type SignedDocumentKind = z.infer<typeof SignedDocumentKindSchema>;

/// ВНИМАНИЕ: перечень НЕ расширяется значением PARTIALLY_SIGNED, хотя в базе
/// такое состояние есть (ADR-007 §4).
///
/// Установленные сборки сравнивают статус с известными им значениями:
/// `status === 'SIGNED'`, иначе `status === 'PENDING'`, иначе «документа
/// нет». Незнакомое третье значение прочиталось бы как «документ не
/// запрашивали» — и человек нажал бы «запросить подпись» под документом,
/// который уже подписан наполовину.
///
/// Поэтому наружу частично подписанный документ едет как PENDING (это
/// правда: подпись не полна), а точное состояние — отдельным полем
/// `signatureState` ниже. Замена делается в `toSignedDocumentSummary`.
export const SignedDocumentStatusSchema = z.enum([
  'PENDING',
  'SIGNED',
  'FAILED',
  'EXPIRED',
  'REJECTED',
]);
export type SignedDocumentStatus = z.infer<typeof SignedDocumentStatusSchema>;

/// Точное состояние подписанности документа (ADR-007 §5). Поле
/// необязательное: старые сборки его не читают, новые читают вместо status.
export const DocumentSignatureStateSchema = z.enum([
  'unsigned', // канон зафиксирован, подписей нет
  'partially_signed', // есть подписи, но не все обязательные роли
  'signed', // подписи всех обязательных ролей собраны
  'failed', // подписание не состоялось
]);
export type DocumentSignatureState = z.infer<typeof DocumentSignatureStateSchema>;

/// Чем подтверждена конкретная подпись на момент приёма (ADR-007 §7).
/// Хранится и отдаётся по каждой подписи своё: одна подпись, не
/// подтверждённая провайдером, не делает недействительными остальные.
export const DocumentSignatureVerificationSchema = z.enum([
  'VERIFIED',
  'DEVICE_KEY_VERIFIED',
  'UNVERIFIED',
  'FAILED',
]);
export type DocumentSignatureVerification = z.infer<typeof DocumentSignatureVerificationSchema>;

/// В каком нормативном качестве подписано (ADR-003 §6). Не выводится из
/// RoleProfileCode: системный профиль и нормативная роль — разные вещи.
export const SignerNormativeRoleSchema = z.enum([
  'WORK_ORDER_ISSUER',
  'RESPONSIBLE_MANAGER',
  'ADMITTING_PERSON',
  'WORK_PRODUCER',
  'CREW_MEMBER',
  // Бланк № 344, пункт 7: «Мероприятия выполнил(и)». Подпись привязана к
  // номеру мероприятия, поэтому у неё есть `mitigationItemNumber`.
  'MITIGATION_EXECUTOR',
  // Бланк № 344, ГРАФА 8.1: открытая строка «Согласовано» без заданной
  // должности — представитель ВНЕШНЕЙ организации, обслуживающей коммуникации
  // (пункт 8 Правил). Графа 8 — не эта роль, а OPERATIONAL_APPROVER ниже
  // (решение владельца 25.09.2026, issue #467). Название значения оставлено
  // как есть: оно стоит в подписанных документах.
  'SAFETY_APPROVER',
  // Бланк № 344, ГРАФА 8: «Согласовано — начальник смены (участка)», то есть
  // согласующий САМОЙ организации, дающий разрешение на работы в её сети
  // (ADR-004 §3a, issue #445). Единственная роль подписанта, которую наряд не
  // называет: согласующий объявляется в момент, когда сам запрашивает подпись
  // под документом согласования.
  'OPERATIONAL_APPROVER',
  // ADR-008 §7 (issue #508): «Выдал начальник цеха» — тот, кто выдал работнику
  // маршрутный лист ФРУ № 12 на месяц и подписал выдачу ключом телефона.
  //
  // Своя роль, а не одна из нарядных: наряда у этого документа нет вовсе
  // (основание — маршрутный лист, ADR-008 §8), и назвать начальника цеха
  // «выдающим наряд» значило бы приписать ему чужой документ. Роли сдачи,
  // подтверждения и утверждения листа заводит issue #509 — вместе с приёмом
  // тех подписей, а не заранее.
  'ROUTE_SHEET_ISSUER',
  // ADR-008 §7 (issue #509): три подписи над одними байтами листа — «сдал»
  // работник и «подтверждаю» старший ключом телефона, «Утверждаю к оплате»
  // квалифицированной ЭЦП.
  'ROUTE_SHEET_SUBMITTER',
  'ROUTE_SHEET_CONFIRMER',
  'ROUTE_SHEET_APPROVER',
  // Issue #543 (ADR-008 §8a): две подписи записи журнала инструктажа без
  // наряда — графа 9 (инструктирующий) и графа 10 (инструктируемый).
  'BRIEFING_INSTRUCTOR',
  'BRIEFING_RECIPIENT',
]);

/**
 * Роли, которые допустимы в поле `signerNormativeRole` САМОГО ДОКУМЕНТА.
 *
 * Установленная сегодня сборка разбирает перечень документов наряда через
 * `SignedDocumentSummarySchema.parse` — `parse`, не `safeParse`, — и роль
 * документа описана у неё перечислением из пяти значений. Незнакомое шестое
 * уронило бы разбор ВСЕГО перечня, а не одной строки: человек увидел бы
 * пустую карточку наряда вместо документа.
 *
 * Поэтому роли пункта 7 и графы 8.1 живут только у подписи (`signatures[]`) и
 * в `requiredSignerRoles` — оба поля добавлены вместе с мультиподписью, старая
 * сборка их не объявляет, а zod отбрасывает необъявленные ключи, не проверяя.
 *
 * Роль документа — это роль, ПОД КОТОРУЮ документ заведён, и ни выполнивший
 * мероприятие, ни согласовавший ПО ПУНКТУ 8 Правил документом не владеют: они
 * расписываются под документом выдачи, заведённым на выдающего. Список
 * закрепляет это правило там, где его можно проверить, а не оставляет на
 * добрую волю вызывающего.
 *
 * ПОЧЕМУ OPERATIONAL_APPROVER ВСЁ-ТАКИ ЗДЕСЬ (issue #445, решение владельца
 * 24.09.2026). Согласующий своей организации документом ВЛАДЕЕТ: согласование
 * — отдельный подписанный документ WORK_ORDER_APPROVAL, заведённый на него, и
 * назвать его роль одним из прежних пяти значений значило бы солгать о том,
 * кто разрешил работы.
 *
 * Цена совместимости при этом не растёт. Установленная сборка разбирает
 * перечень документов через `z.array(SignedDocumentSummarySchema).parse` и
 * спотыкается уже на САМОМ ВИДЕ документа: WORK_ORDER_APPROVAL ей незнаком
 * ровно так же, как незнакома шестая роль. То есть новый вид документа и без
 * того требует обновления приложения на телефонах, и шестое значение роли
 * ничего к этому требованию не добавляет.
 *
 * ПОСЛЕДСТВИЕ, КОТОРОЕ НАДО ЗНАТЬ: включать организации настройку
 * «наряд согласуют перед допуском» можно только после того, как приложение на
 * телефонах обновлено. До обновления карточка наряда, у которого появился
 * документ согласования, откажется разбираться целиком.
 *
 * ПОЧЕМУ ЗДЕСЬ ЖЕ ROUTE_SHEET_ISSUER (issue #508). По тому же доводу, что
 * согласующий: начальник цеха ВЛАДЕЕТ документом `ROUTE_SHEET_ISSUE`,
 * заведённым на него. Совместимости это не стоит ничего, и по причине более
 * сильной, чем у графы 8: документ выдачи листа не привязан к наряду вовсе
 * (ADR-008 §8), а установленная сборка читает перечень документов НАРЯДА — она
 * не увидит его ни в одном ответе, который умеет запрашивать.
 */
export const DOCUMENT_LEVEL_SIGNER_ROLES = [
  'WORK_ORDER_ISSUER',
  'RESPONSIBLE_MANAGER',
  'ADMITTING_PERSON',
  'WORK_PRODUCER',
  'CREW_MEMBER',
  'OPERATIONAL_APPROVER',
  'ROUTE_SHEET_ISSUER',
  // Issue #509: документ листа заведён на работника — «сдал» ставит его
  // владелец. Подтверждающий и утверждающий расписываются под документом
  // работника и владельцами его не становятся, поэтому их ролей здесь нет.
  'ROUTE_SHEET_SUBMITTER',
  // Issue #543: документ записи инструктажа заведён на инструктирующего —
  // его подпись первая. Работник расписывается под ним и владельцем не
  // становится. Совместимости это не стоит: установленная сборка читает
  // перечень документов наряда, а у этого документа наряда нет.
  'BRIEFING_INSTRUCTOR',
] as const;
export const DocumentLevelSignerRoleSchema = z.enum(DOCUMENT_LEVEL_SIGNER_ROLES);
export type DocumentLevelSignerRole = z.infer<typeof DocumentLevelSignerRoleSchema>;
export type SignerNormativeRole = z.infer<typeof SignerNormativeRoleSchema>;

/// Чем разблокирован ключ устройства. Собственного ПИН-кода приложения нет:
/// ключ в аппаратном хранилище закрывается только системным способом.
export const DeviceUnlockMethodSchema = z.enum(['BIOMETRIC', 'DEVICE_CREDENTIAL']);
export type DeviceUnlockMethod = z.infer<typeof DeviceUnlockMethodSchema>;

/// Строка перечня подписей наряда. Ни канона, ни подписи здесь нет — они
/// отдаются отдельными ручками (ADR-004 §11).
/// ADR-007 §1: одна подпись под документом.
///
/// Ни контейнера подписи, ни сертификата целиком здесь нет: перечень читает
/// карточка наряда, а байты и контейнер отдаются отдельными ручками — тем же
/// правилом, что и канон (ADR-004 §11).
export const DocumentSignatureSummarySchema = z.object({
  id: z.string(),
  signerNormativeRole: SignerNormativeRoleSchema,
  /// Графа пункта 5 бланка, под которой стоит подпись, — только у роли
  /// MITIGATION_EXECUTOR. Без неё перечень подписей показывал бы несколько
  /// одинаковых строк «мероприятие выполнил», и по такому перечню нельзя
  /// сказать, какая графа кем закрыта, — то есть ровно то, за чем его
  /// читают при разборе.
  mitigationItemNumber: z.string().nullable().optional(),
  signatureLevel: SignatureLevelSchema,
  /// Чем работник подтвердил подпись ключом устройства: биометрией или кодом
  /// разблокировки телефона. `null` у ЭЦП — там подтверждает провайдер.
  ///
  /// Это единственный признак, отличающий «работник приложил свой палец» от
  /// «кто-то ввёл код общего телефона», и при разборе спрашивают именно про
  /// это. Способ берёт операционная система по факту подтверждения; при
  /// неизвестном исходе заявляется более слабый (ADR-007 §13).
  unlockMethod: DeviceUnlockMethodSchema.nullable().optional(),
  /// Отпечаток ключа устройства, которым подписано. `null` у ЭЦП.
  ///
  /// Не доказывает личность, но показывает, с одного телефона закрыты графы
  /// или с разных, и сверяется глазами с отпечатком в профиле работника.
  deviceThumbprint: z.string().nullable().optional(),
  /// Работник, поставивший подпись. ПУСТАЯ СТРОКА у подписи графы 8.1 бланка
  /// (issue #471): её ставит представитель внешней обслуживающей организации, у
  /// которого учётной записи нет, и идентификатора работника у такой подписи не
  /// бывает вовсе.
  ///
  /// Тип оставлен строкой намеренно, а не переведён в `nullable`. Установленная
  /// сборка разбирает перечень документов наряда через `parse`, и `null` в
  /// объявленном строкой поле уронил бы разбор ВСЕГО перечня — человек увидел
  /// бы пустую карточку наряда вместо документов. Это ровно та цена, от которой
  /// уводит совместимость статуса (ADR-007 §5). Пустая строка ложью при этом не
  /// является: с идентификатором работника она не совпадёт ни при каком
  /// сравнении, а кто подписал — сказано в `signedByNameSnapshot` и
  /// `externalSigner` ниже.
  signedByUserId: z.string(),
  signedByNameSnapshot: z.string(),
  /// Табельный номер подписанта. ПУСТАЯ СТРОКА у подписи графы 8.1: у
  /// постороннего табельного номера не бывает — это не «номер неизвестен».
  signedByEmployeeNumberSnapshot: z.string(),
  /// Подписант без учётной записи (issue #471): должность и организация, от
  /// имени которой он расписался в графе 8.1. `null` или отсутствует — подпись
  /// работника нашей организации.
  ///
  /// Ф.И.О. здесь нет: оно в `signedByNameSnapshot`, там же, где у работника.
  /// Вопрос «кто подписал» один, и два поля под него читались бы по-разному в
  /// зависимости от подписанта.
  externalSigner: z
    .object({
      position: z.string(),
      organizationName: z.string(),
    })
    .nullable()
    .optional(),
  verificationStatus: DocumentSignatureVerificationSchema,
  signedAt: z.string(),
  claimedAt: z.string().nullable().optional(),
  recordedAt: z.string().nullable().optional(),
});
export type DocumentSignatureSummary = z.infer<typeof DocumentSignatureSummarySchema>;

export const SignedDocumentSummarySchema = z.object({
  id: z.string(),
  documentKind: SignedDocumentKindSchema,
  signatureLevel: SignatureLevelSchema,
  status: SignedDocumentStatusSchema,
  /// Роль, ПОД КОТОРУЮ заведён документ, — намеренно суженный перечень.
  /// Роли пункта 7 и графы 8.1 бланка сюда не попадают никогда: ни
  /// выполнивший мероприятие, ни согласовавший по пункту 8 Правил документом
  /// не владеют, они расписываются под документом выдачи. См.
  /// DOCUMENT_LEVEL_SIGNER_ROLES — там же разобрано, почему согласующий своей
  /// организации, графа 8 (issue #445), в перечень входит, а эти две роли нет.
  signerNormativeRole: DocumentLevelSignerRoleSchema,
  signedByUserId: z.string(),
  signedByNameSnapshot: z.string(),
  signedByEmployeeNumberSnapshot: z.string(),
  /// Смена, к которой относится документ. Нужен, чтобы найти продление ИМЕННО
  /// той смены: по ADR-005 §12 основание старта смены N ≥ 2 — SHIFT_EXTENSION
  /// этой смены, и без номера его не отличить от продления соседней.
  shiftNumber: z.number().int().nullable().optional(),
  /// День инструктажа, к которому относится подтверждение (issue #338),
  /// `YYYY-MM-DD`. Заполнен у TARGET_BRIEFING_ACK и TARGET_BRIEFING_PROXY_ACK,
  /// у остальных видов — null.
  ///
  /// Нужен карточке: целевой инструктаж проводится заново каждый день, и без
  /// дня подтверждение за вчера выглядело бы как подтверждение за сегодня —
  /// кнопка подписи исчезала бы в день, когда подпись как раз требуется.
  briefingDay: z.string().nullable().optional(),
  /// Issue #531: у проведения инструктажа — кого инструктировали, у прежней
  /// отметки «за работника» — за кого; и результат проверки знаний у
  /// проведения. У прочих видов полей нет.
  onBehalfOfUserId: z.string().optional(),
  briefingKnowledgePassed: z.boolean().optional(),
  hash: z.string(),
  claimedAt: z.string().nullable().optional(),
  signedAt: z.string().nullable().optional(),
  recordedAt: z.string().nullable().optional(),
  requestedAt: z.string(),
  syncFlags: z.array(z.string()).default([]),

  // ─── ADR-007: мультиподпись. Поля аддитивные ──────────────────────────────
  // Все три необязательны намеренно: установленная сборка их не читает и
  // обязана продолжать работать, а новая читает их вместо `status` и
  // одиночных полей подписанта.

  /// Точное состояние подписанности. `status` для частично подписанного
  /// документа отдаёт PENDING (см. SignedDocumentStatusSchema).
  signatureState: DocumentSignatureStateSchema.optional(),
  /// Роли, без подписей которых документ не полон (ADR-007 §4). Пустой
  /// массив у легаси-документов, заведённых до мультиподписи.
  requiredSignerRoles: z.array(SignerNormativeRoleSchema).optional(),
  /// Подписи документа. У легаси-документа ровно одна — та же, что названа
  /// одиночными полями `signedBy*` выше.
  signatures: z.array(DocumentSignatureSummarySchema).optional(),
});
export type SignedDocumentSummary = z.infer<typeof SignedDocumentSummarySchema>;

// ─── ADR-004 §2, §8: ключи устройств ──────────────────────────────────────────

/// Регистрация открытого ключа устройства. Ключ — SPKI (DER) в base64,
/// ECDSA P-256. `proof` — подпись доказательства владения приватным ключом
/// (см. registrationProofBytes на сервере), DER в base64. `deviceId` —
/// идентификатор пары ключей на этом телефоне: новая пара ключей — новый id.
export const RegisterSigningDeviceSchema = z.object({
  deviceId: z.string().min(8).max(128),
  publicKey: z.string().min(40).max(4096),
  algorithm: z.literal('ES256').default('ES256'),
  proof: z.string().min(40).max(1024),
});
export type RegisterSigningDeviceDto = z.infer<typeof RegisterSigningDeviceSchema>;

export const RevokeSigningDeviceSchema = z.object({
  reason: z.string().min(1).max(500),
});
export type RevokeSigningDeviceDto = z.infer<typeof RevokeSigningDeviceSchema>;

/// Ключ наружу не отдаётся — только отпечаток, чтобы сверить «это мой телефон».
export const SigningDeviceSummarySchema = z.object({
  id: z.string(),
  deviceId: z.string(),
  algorithm: z.string(),
  thumbprint: z.string(),
  registeredAt: z.string(),
  revokedAt: z.string().nullable(),
  revocationReason: z.string().nullable(),
});
export type SigningDeviceSummary = z.infer<typeof SigningDeviceSummarySchema>;

/// Та же запись в реестре организации: администратору нужен ещё и владелец —
/// иначе отпечаток ключа не с кем сопоставить, и отозвать нужный ключ он не
/// сможет. ИИН и другие персональные данные сюда не попадают: для опознания
/// достаточно имени и табельного номера.
export const OrganizationSigningDeviceSummarySchema = SigningDeviceSummarySchema.extend({
  userId: z.string(),
  ownerName: z.string(),
  ownerEmployeeNumber: z.string().nullable(),
  revokedByName: z.string().nullable(),
});
export type OrganizationSigningDeviceSummary = z.infer<typeof OrganizationSigningDeviceSummarySchema>;

// ─── ADR-004 §8: приём офлайн-актов ──────────────────────────────────────────

/// Акты уровня INTERNAL_DEVICE: собираются на устройстве без связи.
export const OfflineActKindSchema = z.enum([
  'WORK_ORDER_ADMISSION',
  'WORK_ORDER_START',
  'TARGET_BRIEFING_ACK',
  'TARGET_BRIEFING_PROXY_ACK',
  /// Issue #531: проведение целевого инструктажа по работнику.
  'TARGET_BRIEFING_CONDUCT',
  /// @deprecated ADR-005 §7 — см. SignedDocumentKindSchema. Остаётся в
  /// перечне ради установленных сборок: устройство с прежней версией всё ещё
  /// может прислать такой акт, и он обязан приниматься.
  'WORK_ORDER_CLOSURE',
  'SHIFT_RISK_ASSESSMENT',
  'SHIFT_EXTENSION',
  'CLOSURE_HANDOVER',
  'CLOSURE_ACCEPT',
  /// ADR-005 §11: прекращение работ допускающим (№ 344, пункты 25 и 27).
  'SUSPENSION',
]);
export type OfflineActKind = z.infer<typeof OfflineActKindSchema>;

/// Что присылает устройство при синхронизации. Сервер НЕ доверяет
/// `canonicalJson`: он пересобирает канон сам из документа выдачи и полей
/// акта и сравнивает байты; присланные байты нужны, чтобы проверить подпись
/// и, при отказе, сохранить как улику ровно то, что подписали.
/**
 * Строка Листа ежесменной оценки рисков по одному опасному фактору
 * (приложение 2 к Правилам № 363).
 *
 * `admissible` — решение ответственного руководителя о допустимости работы
 * ПРИ ЭТОМ ФАКТОРЕ в эту смену. Это НЕ квалификационный допуск работника и НЕ
 * допуск на рабочее место: ADR-002 запрещает называть разные нормативные
 * основания одним словом, поэтому ни `clearance`, ни `admission` в имени нет.
 */
export const ShiftRiskFactorAssessmentSchema = z.object({
  /// Наименование опасного фактора, как его видит подписант.
  factor: z.string().min(1).max(500),
  admissible: z.boolean(),
  /// Меры безопасности для контроля этого фактора. Могут предзаполняться из
  /// матрицы требований, но подтверждаются руководителем в каждую смену:
  /// пункт 11 требует ПРОВЕРКИ условий, а не воспроизведения записанного.
  controlMeasures: z.string().min(1).max(2000),
});
export type ShiftRiskFactorAssessment = z.infer<typeof ShiftRiskFactorAssessmentSchema>;

/**
 * Лист целиком. Шапка (наряд-задание, наряд-допуск, дата, подписант) в него не
 * входит: она проецируется из наряда и подписанта, отдельных полей форма не
 * требует (ADR-005 §3).
 *
 * Число факторов переменное. Четыре блока бумажного бланка — ограничение
 * формы, а не нормы.
 */
export const ShiftRiskAssessmentSheetSchema = z.object({
  factors: z.array(ShiftRiskFactorAssessmentSchema).min(1).max(50),
});
export type ShiftRiskAssessmentSheet = z.infer<typeof ShiftRiskAssessmentSheetSchema>;

const ShiftActPersonSchema = z.object({
  userId: z.string(),
  fullName: z.string(),
  employeeNumber: z.string(),
  membershipId: z.string().nullable().optional(),
});

/**
 * Подпись исполнителя под графой пункта 5 бланка № 344 (пункт 7 бланка).
 *
 * Это подпись НА СУЩЕСТВУЮЩЕМ документе выдачи, а не новый документ: по
 * пункту 10 Правил наряд подписывают несколько лиц, и все их подписи стоят
 * под одними и теми же байтами (ADR-007 §3). Поэтому здесь нет ни канона, ни
 * его хеша — подписываются байты документа выдачи, которые клиент получает
 * ручкой `GET /signed-documents/:id/canonical`, а сервер сверяет со своими.
 *
 * Уровень подписи — ключ устройства: мероприятия выполняют на рабочем месте,
 * где квалифицированной ЭЦП нет. Под одним документом это уживается с ЭЦП
 * выдающего — уровень есть свойство подписи, а не документа (ADR-007 §1).
 */
/**
 * Строка перечня мероприятий для того, кто их подписывает (бланк № 344, п. 7).
 *
 * Содержание графы здесь ОБЯЗАТЕЛЬНО, а не для красоты: подпись утверждает,
 * что выполнено именно это мероприятие. Человек, которому показали номер
 * «5.2» без текста, расписывается вслепую, и такая подпись не удостоверяет
 * ничего.
 */
export const MeasureSignatureItemSchema = z.object({
  itemNumber: SafetyMeasureItemNumberEnum,
  /// Текст, который вписал выдающий, — то самое, что подписывается.
  content: z.string(),
  /// Назначенный исполнитель. Назван — подписывает он и только он.
  executorUserId: z.string().nullable(),
  executorName: z.string().nullable(),
  /// Кем и когда графа уже подписана. `null` — ещё не подписана.
  signedByName: z.string().nullable(),
  signedAt: z.string().nullable(),
  /// Может ли ЭТОТ работник подписать графу сейчас.
  canSign: z.boolean(),
});
export type MeasureSignatureItem = z.infer<typeof MeasureSignatureItemSchema>;

export const MeasureSignatureStateSchema = z.object({
  /// Документ выдачи, на который лягут подписи. `null` — не зафиксирован, и
  /// подписывать пока нечего.
  documentId: z.string().nullable(),
  /// Байты документа подписываются целиком, поэтому их хеш нужен экрану, чтобы
  /// не подписать устаревшую копию канона из кеша.
  documentHash: z.string().nullable(),
  items: z.array(MeasureSignatureItemSchema),
});
export type MeasureSignatureState = z.infer<typeof MeasureSignatureStateSchema>;

export const SubmitMeasureSignatureSchema = z.object({
  /// Графа, под которой ставится подпись. Одна подпись — одна графа; чтобы
  /// подписать три, исполнитель подписывает трижды.
  itemNumber: SafetyMeasureItemNumberEnum,
  /// Подпись над байтами канона документа выдачи, base64.
  signature: z.string().min(1),
  deviceId: z.string().min(8).max(128),
  unlockMethod: DeviceUnlockMethodSchema,
});
export type SubmitMeasureSignatureDto = z.infer<typeof SubmitMeasureSignatureSchema>;

/**
 * Issue #327, ADR-005 §7: со-подпись ответственного руководителя работ под
 * сдачей рабочего места, оформленной подписанным актом.
 *
 * Номера графы здесь нет и быть не может: со-подпись относится к документу
 * целиком — это вторая подписная строка закрывающего блока бланка № 253, а не
 * подпись под одним из пунктов. Байты подписываются те же, что подписал
 * производитель работ (ADR-007 §3), и клиент их не присылает: он подписывает
 * ровно то, что получил ручкой канона.
 */
export const SubmitClosureCoSignatureSchema = z.object({
  /// Подпись над байтами канона документа сдачи, base64.
  signature: z.string().min(1),
  deviceId: z.string().min(8).max(128),
  unlockMethod: DeviceUnlockMethodSchema,
});
export type SubmitClosureCoSignatureDto = z.infer<typeof SubmitClosureCoSignatureSchema>;

export const SubmitOfflineActSchema = z.object({
  documentKind: OfflineActKindSchema,
  basisDocumentHash: z.string().regex(/^sha256:[0-9a-f]{64}$/),
  /// ADR-005 §5: номер смены. Обязателен для посменных видов
  /// (SHIFT_RISK_ASSESSMENT, SHIFT_EXTENSION, WORK_ORDER_START) и запрещён
  /// для остальных. Без него два Листа одного наряда за разные смены при
  /// совпавшем содержании дали бы одинаковый хеш, и второй был бы
  /// неотличим от повторной отправки первого.
  shiftNumber: z.number().int().min(1).optional(),
  /// ADR-004 §4: за кого поставлена отметка. Только для
  /// TARGET_BRIEFING_PROXY_ACK и только член бригады этого наряда.
  onBehalfOfUserId: z.string().optional(),
  /// Способ, которым работник получил инструктаж. Только для
  /// TARGET_BRIEFING_PROXY_ACK. Перечень закрыт и сейчас содержит одно
  /// значение: способ — это утверждение о том, как человек получил
  /// инструктаж, и заводить значения «на будущее» значит разрешить запись,
  /// за которой не стоит согласованного смысла.
  proxyMethod: TargetBriefingProxyMethodSchema.optional(),
  /// Issue #531: для TARGET_BRIEFING_CONDUCT — кого инструктировали (член
  /// бригады этого наряда) и результат проверки знаний. Для остальных видов
  /// недопустимы.
  briefedUserId: z.string().uuid().optional(),
  knowledgeCheck: z.enum(['PASSED', 'FAILED']).optional(),
  /// ADR-005 §12: факты продления, которые подписывает допускающий. Только
  /// для SHIFT_EXTENSION. Здесь в канон попадает производитель заступающей
  /// смены — единственный участник, которого документ выдачи не называет
  /// (пункт 29 Правил № 344); за него ручается подпись допускающего.
  shift: z
    .object({
      shiftNumber: z.number().int().min(2),
      conditionsUnchanged: z.boolean(),
      incomingCrewSize: z.number().int().min(1),
      handedOverBy: ShiftActPersonSchema,
      incomingProducer: ShiftActPersonSchema,
    })
    .optional(),
  /// ADR-005 §3: содержание Листа ежесменной оценки. Только для
  /// SHIFT_RISK_ASSESSMENT. Подписывается вместе с остальным каноном — иначе
  /// подписанный Лист не удостоверял бы никакой оценки.
  sheet: ShiftRiskAssessmentSheetSchema.optional(),
  /// № 344, пункты 25 и 27: основание и причина прекращения работ. Только для
  /// SUSPENSION. Оба поля — утверждение самого допускающего: серверного
  /// источника у причины нет, её знает человек на месте. Канон всё равно
  /// пересобирается сервером и сравнивается байт в байт, поэтому подменить
  /// подписанное этим нельзя.
  suspension: z
    .object({
      ground: WorkOrderSuspensionGroundSchema,
      reason: z.string().min(1).max(2000),
    })
    .optional(),
  /// Момент по часам устройства, RFC 3339 с зоной.
  claimedAt: z.string().datetime({ offset: true }),
  /// Для TARGET_BRIEFING_ACK — кто проводил инструктаж.
  briefingByUserId: z.string().uuid().nullable().optional(),
  deviceId: z.string().min(8).max(128),
  unlockMethod: DeviceUnlockMethodSchema,
  canonicalJson: z.string().min(2).max(262_144),
  /// ECDSA P-256 / SHA-256, DER, base64.
  signature: z.string().min(40).max(1024),
});
export type SubmitOfflineActDto = z.infer<typeof SubmitOfflineActSchema>;

/// Почему акт не принят. Записывается в syncFlags как `REJECTED_<причина>` и
/// возвращается клиенту: повторять отправку без разбирательства бессмысленно.
export const OfflineActRejectionReasonSchema = z.enum([
  'DEVICE_NOT_REGISTERED',
  'DEVICE_REVOKED',
  'BASIS_NOT_SIGNED',
  'CLAIMED_BEFORE_BASIS',
  'ACTOR_NOT_AUTHORIZED',
  'BRIEFING_BY_NOT_AUTHORIZED',
  'BRIEFING_BY_MISMATCH',
  /// ADR-004 §4: отметка целевого инструктажа за работника.
  /// Отметка не назвала, за кого она поставлена.
  'ON_BEHALF_OF_REQUIRED',
  /// Названный работник не входит в бригаду этого наряда.
  'ON_BEHALF_OF_NOT_IN_ORDER',
  /// Отметка за себя: подпись за себя в обход ключа устройства.
  'ACTOR_IS_SUBJECT',
  /// Инструктаж ещё не отмечен проводившим: подтверждать нечего.
  'BRIEFING_NOT_CONDUCTED',
  /// Работник уже подтвердил инструктаж собственной подписью. Она сильнее, и
  /// отметкой её не перекрывают.
  'ALREADY_ACKNOWLEDGED_BY_WORKER',
  'CANON_MISMATCH',
  'SIGNATURE_INVALID',
  'ALREADY_SIGNED',
  'STATUS_MISMATCH',
  'CREW_NOT_READY',
  /// ADR-005 ввёл виды документов, которые контур офлайн-актов ещё не
  /// обслуживает (сервис — задача П-6). Отдельная причина, а не
  /// ACTOR_NOT_AUTHORIZED: подписант вправе совершить акт, просто сервер
  /// пока не умеет его принять, и путать это с отказом в полномочии
  /// значило бы послать в поле неверное объяснение.
  'ACT_KIND_NOT_SERVED',
  /// ADR-005 §12: старт смены N ≥ 2 ссылается на смену, записи о которой
  /// нет. Продление — отдельный подписываемый акт, и пока он не принят,
  /// стартовать нечего. Само продление этой причины не получает: оно свою
  /// запись и создаёт (issue #323).
  'SHIFT_NOT_FOUND',
  /// Факты смены в присланном каноне не совпали с записью продления на
  /// сервере, либо номер смены не тот, либо продление не прошло правила
  /// (№ 344, пункты 6, 22, 24). Сервер пересобирает канон сам (§8).
  'SHIFT_MISMATCH',
  /// ADR-005 §3: Лист прислан без оценки, либо оценка пришла с видом
  /// документа, который её не несёт.
  'SHEET_INVALID',
  /// № 344, пункты 25 и 27: прекращение работ прислано без основания или
  /// причины, либо они пришли с видом документа, который их не несёт.
  'SUSPENSION_INVALID',
  /// Работы по наряду прекращены допускающим и не возобновлены. Отдельная
  /// причина, а не STATUS_MISMATCH: статус наряда при этом обычный
  /// (ADMITTED), и «не тот статус» отправило бы человека искать не то.
  'WORK_ORDER_SUSPENDED',
  /// Issue #531: отметка «за работника» для инструктажа отменена решением
  /// Штаба 29.09.2026. Работник расписывается сам или в бумажном журнале.
  'PROXY_ACK_WITHDRAWN',
  /// Проведение инструктажа не назвало работника или назвало не из бригады.
  'BRIEFED_NOT_IN_ORDER',
  /// Проведение инструктажа без результата проверки знаний.
  'KNOWLEDGE_CHECK_REQUIRED',
  /// Сегодня работник уже прошёл инструктаж с проверкой знаний.
  'BRIEFING_ALREADY_PASSED',
  /// Последний инструктаж работника — «не прошёл»: подтверждать нечего,
  /// нужен повторный инструктаж.
  'BRIEFING_KNOWLEDGE_FAILED',
]);
export type OfflineActRejectionReason = z.infer<typeof OfflineActRejectionReasonSchema>;

export const OfflineActResultSchema = z.object({
  document: SignedDocumentSummarySchema,
  /// true — акт принят (SIGNED) и переход наряда выполнен.
  accepted: z.boolean(),
  /// true — тот же акт уже был принят раньше; повторная отправка ничего не меняет.
  existing: z.boolean(),
  rejectionReason: OfflineActRejectionReasonSchema.nullable(),
  /// Отказ словами — тем же текстом, каким объяснял бы себя онлайн-путь.
  /// Код причины остаётся машинным признаком, но человеку в поле он говорит
  /// немного: SHIFT_MISMATCH одинаково покрывает «смена не та по номеру» и
  /// «Лист ежесменной оценки за эту смену не подписан», а действия у них
  /// разные. NULL — объяснения сверх кода нет.
  rejectionMessage: z.string().max(500).nullable(),
  /// Кто из бригады не готов и почему — по строке на работника (issue #431).
  /// Заполнено у отказа CREW_NOT_READY; у прочих отказов и у принятого акта —
  /// NULL. Необязательное: установленные сборки поля не читают и показывают
  /// прежнюю общую фразу.
  rejectionCrew: CoreCrewReadinessRefusalSchema.nullish(),
});
export type OfflineActResult = z.infer<typeof OfflineActResultSchema>;

// ─── ADR-005 §1: продление наряда по сменам ──────────────────────────────────
// Правила № 344, пункт 6: наряд выдаётся на смену либо на весь период
// непрерывных работ с продлением для каждой смены; продление оформляет
// допускающий перед началом каждой смены. Бланк, пункт 12 — состав записи.
// Первая смена продлением не оформляется: она проходит первичным допуском
// (бланк, пункты 9-11), поэтому shiftNumber всегда ≥ 2.

export const CreateShiftExtensionSchema = z.object({
  /// Номер заступающей смены. Минимум 2 — см. выше.
  shiftNumber: z.number().int().min(2),
  /// «Условия не изменились, смену сдал» — констатация производителя работ
  /// сдающей смены. Ложь допустима и осмысленна: изменившиеся условия по
  /// пункту 25 Правил № 344 — основание выдать наряд заново, и запись об
  /// этом должна остаться в продлении, а не исчезнуть.
  conditionsUnchanged: z.boolean(),
  /// Кто сдал смену. Не задан — производитель работ предыдущей смены.
  handedOverByUserId: z.string().uuid().optional(),
  /// Численность заступающей бригады (бланк, пункт 12).
  incomingCrewSize: z.number().int().min(1),
  /// Производитель работ заступающей смены. По пункту 29 назначается на
  /// каждую смену отдельно, поэтому передаётся явно.
  incomingProducerId: z.string().uuid(),
});
export type CreateShiftExtensionDto = z.infer<typeof CreateShiftExtensionSchema>;

/**
 * Что именно будет подписано под продлением (issue #323).
 *
 * Продление — подписываемый документ (ADR-005 §1), и порядок обязан быть:
 * собрали факты → человек подписал → запись и документ появились одной
 * транзакцией. Чтобы телефон собрал ТЕ ЖЕ байты, что соберёт сервер, состав
 * фактов отдаёт сервер: фамилий, табельных и идентификаторов членства у
 * телефона нет, а канон сравнивается байт в байт (ADR-004 §8).
 *
 * Черновик ничего не пишет и ничего не бронирует. Изменись данные между
 * черновиком и приёмом акта — пересобранный сервером канон не сойдётся, и акт
 * будет отвергнут CANON_MISMATCH.
 */
export const ShiftExtensionDraftSchema = CreateShiftExtensionSchema.omit({ handedOverByUserId: true });
export type ShiftExtensionDraftDto = z.infer<typeof ShiftExtensionDraftSchema>;

const ShiftExtensionDraftPersonSchema = z.object({
  userId: z.string(),
  fullName: z.string(),
  employeeNumber: z.string(),
  membershipId: z.string().nullable(),
});

export const ShiftExtensionDraftFactsSchema = z.object({
  shiftNumber: z.number().int().min(2),
  conditionsUnchanged: z.boolean(),
  incomingCrewSize: z.number().int().min(1),
  /// Кого сервер считает сдающим смену. Телефон его не назначает: правило
  /// одно (производитель предыдущей смены, для второй — производитель наряда),
  /// и держать его в двух местах значило бы однажды их развести.
  handedOverBy: ShiftExtensionDraftPersonSchema,
  incomingProducer: ShiftExtensionDraftPersonSchema,
});
export type ShiftExtensionDraftFacts = z.infer<typeof ShiftExtensionDraftFactsSchema>;

const ShiftExtensionPersonSchema = z.object({
  userId: z.string(),
  name: z.string().nullable(),
  employeeNumber: z.string().nullable(),
});

export const CoreShiftExtensionSchema = z.object({
  id: z.string(),
  workOrderId: z.string(),
  shiftNumber: z.number().int(),
  extendedAt: z.string(),
  conditionsUnchanged: z.boolean(),
  handedOverBy: ShiftExtensionPersonSchema,
  incomingCrewSize: z.number().int(),
  incomingProducer: ShiftExtensionPersonSchema,
  /// Допускающий в смене. Его «допуск разрешаю» — подписываемый документ
  /// SHIFT_EXTENSION: по пункту 24 именно оно отпирает начало работы.
  admitting: ShiftExtensionPersonSchema,
  /// «Смену принял»: когда заступающий производитель начал работу в этой
  /// смене. Пусто — смена продлена, но ещё не начата.
  ///
  /// Со второй смены статус наряда не меняется, и без этого поля продлённую
  /// смену не отличить от начатой: карточка предлагала сдавать рабочее место
  /// вместо того, чтобы начать на нём работу (ADR-005 §10).
  startedAt: z.string().nullable().optional(),
  startedBy: ShiftExtensionPersonSchema.nullable().optional(),
});
export type CoreShiftExtension = z.infer<typeof CoreShiftExtensionSchema>;

/**
 * Наряд-допуск, собранный по структуре бланка (приложение 1 к Правилам № 344).
 *
 * ЗАЧЕМ ОТДЕЛЬНЫЙ ОТВЕТ. Наряд живёт тремя документами — выдача, допуск,
 * закрытие, — и до сих пор читался четырьмя обращениями: сам наряд, перечень
 * подписанных документов, продления и мероприятия. Для экрана этого хватало,
 * для ЛИСТА — нет: четыре запроса это четыре момента времени, и на лист,
 * который подшивается в материалы расследования, попал бы документ, не
 * существовавший ни в одну секунду.
 *
 * Новых источников правды здесь нет. Это те же строки той же базы, которые
 * уже уходят в мобильное приложение; новое — только порядок бланка и то, что
 * снимок один.
 */
export const Form344PersonSchema = z.object({
  name: z.string(),
  employeeNumber: z.string(),
});
export type Form344Person = z.infer<typeof Form344PersonSchema>;

/** Заполненная графа пункта 5. Незаполненных здесь нет — в бланке прочерк. */
export const Form344MeasureSchema = z.object({
  itemNumber: SafetyMeasureItemNumberEnum,
  content: z.string(),
  executor: Form344PersonSchema.nullable(),
  /// Issue #329 (Э3-2): уровень меры по пункту 24 Правил № 363. Графы под него
  /// в бланке № 344 нет, и лист его не печатает — это признак для разбора
  /// работодателя, а не строка документа. Необязательно: у нарядов, выписанных
  /// до задачи, уровень не назван.
  hierarchyLevel: RiskControlHierarchyLevelSchema.nullable().optional(),
});
export type Form344Measure = z.infer<typeof Form344MeasureSchema>;

/**
 * Графа 8.1 бланка: согласование с ВНЕШНЕЙ организацией, обслуживающей
 * коммуникации, по пункту 8 Правил № 344 (дороги, ЛЭП, газо- и теплопроводы,
 * все земляные работы). Подписывает её представитель — у него нет учётной
 * записи, и подпись собирается по ссылке (карточка B-054).
 *
 * Внутреннее согласование своей организации — не здесь, а в
 * `Form344OperationalApprovalSchema` ниже: это графа 8 (решение владельца
 * 25.09.2026, issue #467).
 */
/**
 * Подпись представителя обслуживающей организации в графе 8.1 (issue #471,
 * карточка B-054).
 *
 * Стоит под документом выдачи, на тех же байтах, что подпись выдающего
 * (ADR-007 §3). Учётной записи у подписанта нет, поэтому здесь нет ни
 * идентификатора работника, ни табельного номера: их не бывает.
 *
 * Ф.И.О. и должность — снимок на момент подписи, а не текущие реквизиты
 * согласования: подпись обязана читаться так, как она поставлена.
 */
export const Form344SafetyApprovalSignatureSchema = z.object({
  name: z.string(),
  position: z.string(),
  signedAt: z.string(),
  /// Чем подтверждена подпись на момент приёма (ADR-007 §7). У подписи по
  /// ссылке — VERIFIED: провайдер подтвердил подпись, и ИИН сошёлся с тем,
  /// который выдающий назвал в реквизитах.
  verificationStatus: DocumentSignatureVerificationSchema,
});
export type Form344SafetyApprovalSignature = z.infer<
  typeof Form344SafetyApprovalSignatureSchema
>;

/**
 * Одна запись согласования в графе 8.1 (issue #468, карточка B-034): с какой
 * обслуживающей организацией согласованы работы, кто согласовал и когда.
 *
 * Реквизиты — то, что выдающий удостоверяет СВОЕЙ подписью под документом
 * выдачи: он утверждает, что согласовал. Подпись самого представителя —
 * отдельный факт, поле `signature` ниже (issue #471).
 */
export const Form344SafetyApprovalSchema = z.object({
  organizationName: z.string(),
  representativeName: z.string(),
  representativePosition: z.string(),
  approvedAt: z.string(),
  /// Письменное разрешение, если оно выдавалось. `null` в обоих полях —
  /// согласование устное, и на листе в этой строке прочерк.
  permitNumber: z.string().nullable(),
  permitIssuedAt: z.string().nullable(),
  /// Приложенные к этому согласованию документы (issue #469): скан письменного
  /// разрешения владельца коммуникаций и схема с размещением и глубиной
  /// заложения. Пусто — не прикладывали, и на листе в этой строке прочерк.
  ///
  /// Порядок — по контрольной сумме, тот же, что в каноне документа выдачи: лист
  /// и подписанный документ обязаны перечислять одно и то же одинаково.
  attachments: z.array(SafetyApprovalAttachmentSchema),
  /// Подпись представителя этой организации в графе 8.1 (issue #471). `null` —
  /// не подписана, и на листе в подписной строке прочерк.
  ///
  /// Необязательно: поле аддитивное, установленная сборка его не читает.
  signature: Form344SafetyApprovalSignatureSchema.nullable().optional(),
  /// Внесён ли ИИН согласовавшего. САМ ИИН наружу не отдаётся — ни целиком, ни
  /// маской (ADR-004 §12): он нужен серверу для сверки с сертификатом, а
  /// интерфейсу нужен ответ на другой вопрос — можно ли уже выдать ссылку на
  /// подпись или сперва надо внести ИИН.
  representativeIinRecorded: z.boolean().optional(),
});
export type Form344SafetyApproval = z.infer<typeof Form344SafetyApprovalSchema>;

export const Form344ApprovalSchema = z.object({
  /// Требуется ли согласование по пункту 8 Правил. Значение наряда, а не
  /// справочника.
  required: z.boolean(),
  /// Что предлагал справочник видов работ. `null` — не предлагал ничего.
  proposed: z.boolean().nullable(),
  /// Кто и когда снял подставленное справочником. Снятие не запрещено
  /// (пункт 19), но оно записано и на листе видно.
  waivedBy: Form344PersonSchema.nullable(),
  waivedAt: z.string().nullable(),
  /// С кем именно согласованы работы (issue #468). Пусто — согласований не
  /// записано, и лист ставит в графе прочерк. У наряда с требованием
  /// согласования пустым этот перечень не бывает: без единой записи такой
  /// наряд не выписывается.
  ///
  /// Порядок — по наименованию организации, тот же, что в каноне документа
  /// выдачи: лист и подписанный документ обязаны перечислять одно и то же
  /// одинаково, иначе при сверке они читаются как два разных перечня.
  records: z.array(Form344SafetyApprovalSchema),
  /// Земляные работы в охранной зоне подземных коммуникаций — пункт 300 Правил
  /// № 222 (issue #469). Значение наряда, а не справочника: он только
  /// предлагает. `true` — наряд выписан только потому, что к согласованию
  /// приложено письменное разрешение владельца коммуникаций.
  excavationInProtectedZone: z.boolean(),
  /// Что предлагал справочник видов работ. `null` — не предлагал ничего.
  excavationProposed: z.boolean().nullable(),
  /// Кто и когда снял предложенный признак. Снятие не запрещено — справочник
  /// знает вид работ, но не место их ведения, — но записано и на листе видно.
  excavationWaivedBy: Form344PersonSchema.nullable(),
  excavationWaivedAt: z.string().nullable(),
  /// Ждёт ли документ выдачи подписи в графе 8.1 (issue #471). `true` —
  /// подпись обязательна, и пока её нет, документ выдачи остаётся подписанным
  /// не полностью. Считается по перечню обязательных подписантов самого
  /// документа, а не по признаку наряда: перечень заморожен первой подписью
  /// (ADR-007 §2), и наряд, документ которого заведён раньше решения, ждёт
  /// ровно то, что записано в нём. Документа ещё нет — отвечает признак
  /// наряда: подпись потребуется, как только документ будет зафиксирован.
  ///
  /// Необязательно: поле аддитивное, установленная сборка его не читает.
  signatureRequired: z.boolean().optional(),
  /// Подписана ли графа 8.1 — хоть одним из названных представителей
  /// (issue #471). Кем именно, видно в `records[].signature`.
  signatureCollected: z.boolean().optional(),
});

/**
 * Графа 8 бланка: согласование наряда согласующим СВОЕЙ организации
 * (issue #445).
 *
 * ГРАФА 8, А НЕ ДОПОЛНЕНИЕ К БЛАНКУ (решение владельца 25.09.2026, сверено с
 * подлинным приложением 1 к Правилам № 344; issue #467). Графу 8 «Согласовано»
 * подписывает начальник смены (участка), где идут работы, — работник самой
 * организации с учётной записью. Внешнее согласование с обслуживающей
 * коммуникации организацией — это графа 8.1, и она живёт в
 * `Form344ApprovalSchema` рядом.
 *
 * До issue #467 схема называла эту позицию не предусмотренной приложением 1, а
 * лист печатал её вне бланковой части. ADR-004 §3a и ADR-007 §11 пока говорят
 * то же самое — расхождение с решением владельца названо в PR issue #467,
 * правка ADR идёт отдельным PR с явного одобрения владельца.
 *
 * ПОДПИСАННЫЙ ДОКУМЕНТ, А НЕ ОТМЕТКА (решение владельца 24.09.2026).
 * Согласование — разрешение на работы, и при разборе происшествия проверяют
 * именно его. Поэтому «кто согласовал» и «когда» берутся не из колонок наряда
 * — их нет, — а из подписи под документом `WORK_ORDER_APPROVAL` со статусом
 * SIGNED. Пока подпись не поставлена, поля пусты: запрошенная, но не
 * поставленная подпись согласованием не является.
 */
export const Form344OperationalApprovalSchema = z.object({
  /// Требует ли ЭТОТ наряд согласования. Снимок настройки организации на
  /// момент выписки: последующая её правка уже выписанный наряд не трогает —
  /// тот же приём, что у требования по пункту 8 Правил. `false` — на листе в
  /// графе 8 стоит ПРОЧЕРК, а не пустое место (пункт 5 Правил).
  required: z.boolean(),
  /// Кто согласовал — из подписи под документом согласования. `null`, пока
  /// подписи нет (или согласование не требовалось вовсе).
  approvedBy: Form344PersonSchema.nullable(),
  approvedAt: z.string().nullable(),
  /// Документ согласования, если он уже заведён: по нему панель продолжает
  /// незаконченное подписание, не заводя второго документа на ту же работу.
  documentId: z.string().nullable(),
});
export type Form344OperationalApproval = z.infer<typeof Form344OperationalApprovalSchema>;

/**
 * Ответ на запрос согласования наряда (issue #445).
 *
 * Согласование не совершается одним нажатием: нажатие ФИКСИРУЕТ канон и хеш
 * документа и заводит его в статусе PENDING, а согласованным наряд становится
 * после того, как под этот хеш придёт квалифицированная подпись (ADR-004 §7 —
 * то же правило, по которому наряд считается выданным только после SIGNED).
 *
 * Поэтому ответ отдаёт ДОКУМЕНТ, а не «согласовал такой-то, тогда-то»:
 * фамилия и время появятся у подписи, а до неё их нет и показывать их было бы
 * утверждением разрешения, которого никто не дал.
 */
export const WorkOrderApprovalRequestSchema = z.object({
  workOrderId: z.string().uuid(),
  /// Требует ли наряд согласования. Ответ отдаёт его вместе с документом,
  /// чтобы панель не перечитывала лист ради одного признака.
  required: z.boolean(),
  document: SignedDocumentSummarySchema,
  /// true — документ был заведён раньше и возвращён как есть. Повторное
  /// нажатие не заводит второго документа на ту же работу: два разных хеша
  /// под одним разрешением означали бы два разных разрешения.
  existing: z.boolean(),
});
export type WorkOrderApprovalRequest = z.infer<typeof WorkOrderApprovalRequestSchema>;

// ─── Issue #471 (карточка B-054): ссылка на подпись графы 8.1 ────────────────
//
// Представитель обслуживающей организации подписывает документ выдачи, не
// имея учётной записи: выдающий получает одноразовую ссылку с ограниченным
// сроком и передаёт её любым способом. Ссылка открывает подписание ровно этого
// документа и ничего больше — ни просмотра других нарядов, ни входа в систему.

/** Запрос ссылки. Выдаёт её выдающий наряд — он же по пункту 8 и согласовывал. */
export const CreateSafetyApprovalSigningLinkSchema = z.object({
  workOrderId: z.string().uuid(),
  /// Согласование, представитель которого подписывает. Наряд бывает согласован
  /// с несколькими организациями, и ссылка адресована одному человеку: чужая
  /// подпись под согласованием с другой организацией ничего не удостоверяет.
  safetyApprovalId: z.string().uuid(),
  /// ИИН согласовавшего — когда при выписке его не назвали. Сервер внесёт его в
  /// реквизиты и с ним же сверит сертификат подписи.
  ///
  /// Уже внесённый ИИН этим полем НЕ ПОДМЕНЯЕТСЯ: выдающий назвал подписанта
  /// при выписке, и молчаливая замена означала бы, что согласование
  /// переадресовали другому человеку после того, как наряд подписан.
  representativeIin: z.string().max(40).nullable().optional(),
});
export type CreateSafetyApprovalSigningLinkDto = z.infer<
  typeof CreateSafetyApprovalSigningLinkSchema
>;

/**
 * Выданная ссылка.
 *
 * Отдаётся один раз, в ответе на запрос: сам токен на сервере не хранится (в
 * базе лежит только его SHA-256), и показать ссылку второй раз нельзя —
 * утраченная не восстанавливается, а выдаётся заново.
 */
export const SafetyApprovalSigningLinkSchema = z.object({
  url: z.string(),
  expiresAt: z.string(),
  /// Кому она адресована — чтобы выдающий видел, кому отправляет, и не
  /// перепутал согласование при двух обслуживающих организациях.
  organizationName: z.string(),
  representativeName: z.string(),
  representativePosition: z.string(),
});
export type SafetyApprovalSigningLink = z.infer<typeof SafetyApprovalSigningLinkSchema>;

/**
 * Состояние графы 8.1 глазами карточки наряда.
 *
 * По строке на каждую обслуживающую организацию: кто подписывает, подписал ли
 * уже и можно ли прямо сейчас выдать ему ссылку. Последнее считает СЕРВЕР —
 * кнопка, которая приведёт к отказу, хуже, чем её отсутствие, а условий у
 * отказа несколько, и повторять их в двух клиентах значило бы развести ответы.
 *
 * ИИН здесь нет ни в каком виде: наружу уходит ответ на вопрос «внесён ли он»,
 * а не сам ИИН (ADR-004 §12).
 */
export const SafetyApprovalSigningStateSchema = z.object({
  safetyApprovalId: z.string(),
  organizationName: z.string(),
  representativeName: z.string(),
  representativePosition: z.string(),
  representativeIinRecorded: z.boolean(),
  /// Подписана ли графа 8.1 этим представителем и когда.
  signed: z.boolean(),
  signedAt: z.string().nullable(),
  /// Можно ли выдать ссылку прямо сейчас.
  canIssueLink: z.boolean(),
  /// Почему нельзя — словами, теми же, что скажет отказ на попытку. `null`,
  /// когда можно.
  refusal: z.string().nullable(),
});
export type SafetyApprovalSigningState = z.infer<typeof SafetyApprovalSigningStateSchema>;

export const WorkOrderForm344Schema = z.object({
  /// Бланк начинается с наименования организации.
  organization: z.object({ id: z.string(), name: z.string() }),
  subdivisionName: z.string().nullable(),
  workOrder: z.object({
    id: z.string().uuid(),
    number: z.string(),
    workForm: CoreWorkFormSchema,
    status: CoreWorkOrderStatusSchema,
    workType: z.string(),
    workTypeCatalogName: z.string().nullable(),
    /// Прежнее свободное поле мер безопасности. Остаётся: на его байтах стоят
    /// выпущенные подписи (ADR-004 §5), и на листе оно печатается как есть.
    safetyMeasures: z.string(),
    workSiteName: z.string().nullable(),
    workSiteDetail: z.string().nullable(),
    requiredElectricalGroup: CoreElectricalGroupSchema.nullable(),
    /// Issue #463 (карточка B-075): напряжение электроустановки. Стоит рядом с
    /// требуемой группой по тому же доводу, что и в каноне документа выдачи
    /// (issue #423): пункты 33, 35, 37 и 38 Правил № 222 выводят порог группы
    /// из напряжения, и лист, называющий группу без напряжения, печатает
    /// следствие без основания.
    ///
    /// `null` — напряжение у наряда не названо. Это НЕ «до 1000 В»: у нарядов,
    /// выписанных до issue #419, поля не было вовсе.
    voltageClass: CoreInstallationVoltageClassSchema.nullable(),
    issuedAt: z.string(),
    admittedAt: z.string().nullable(),
    startedAt: z.string().nullable(),
    closureHandoverAt: z.string().nullable(),
    closedAt: z.string().nullable(),
  }),
  /// Пункты 4, 9, 10 бланка. Ответственный руководитель назван, но наряд не
  /// подписывает — подписной строки у него в бланке нет.
  roles: z.object({
    issuer: Form344PersonSchema,
    responsible: Form344PersonSchema.nullable(),
    admitting: Form344PersonSchema.nullable(),
    producer: Form344PersonSchema,
  }),
  /// Пункт 5. Только ЗАПОЛНЕННЫЕ графы: незаполненная — прочерк, и рисует его
  /// лист, а не сервер. Десять позиций бланка постоянны и берутся из
  /// SAFETY_MEASURE_ITEMS.
  measures: z.array(Form344MeasureSchema),
  /// Графа 8.1: согласование с внешней организацией (пункт 8 Правил).
  approval: Form344ApprovalSchema,
  /// Графа 8: внутреннее согласование наряда (issue #445). Отдельным полем — и
  /// поле, и графа отдельные: в графах 8 и 8.1 расписываются разные люди.
  operationalApproval: Form344OperationalApprovalSchema,
  /// Состав бригады. Готовность и ворота сюда не входят: лист свидетельствует
  /// о том, что было, а не о том, можно ли начинать.
  crew: z.array(
    z.object({
      workerId: z.string(),
      name: z.string(),
      employeeNumber: z.string(),
      role: CoreWorkOrderMemberRoleSchema,
      professionName: z.string().nullable(),
      qualificationGrade: z.number().int().nullable(),
      targetBriefingDoneAt: z.string().nullable(),
      targetBriefingAckAt: z.string().nullable(),
      targetBriefingAckMethod: z.enum(CORE_TARGET_BRIEFING_ACK_METHODS).nullable(),
      targetBriefingAckProxyName: z.string().nullable(),
      /// Issue #531/#533: кто провёл, результат проверки знаний и фото
      /// бумажного журнала. Optional — прежние ответы читаются.
      targetBriefingByName: z.string().nullable().optional(),
      targetBriefingKnowledgePassed: z.boolean().nullable().optional(),
      targetBriefingPaperMark: z
        .object({ id: z.string(), uploadedAt: z.string(), uploadedByName: z.string().nullable() })
        .nullable()
        .optional(),
    }),
  ),
  /// Продления по сменам. Пункт 24: производитель назначается на каждую смену.
  shifts: z.array(CoreShiftExtensionSchema),
  /// ВСЕ подписанные документы наряда со всеми подписями: выдача, допуск,
  /// старт каждой смены, подтверждения инструктажа, продления, оба акта
  /// закрытия. Лист раскладывает их по позициям бланка.
  documents: z.array(SignedDocumentSummarySchema),
});
export type WorkOrderForm344 = z.infer<typeof WorkOrderForm344Schema>;


/**
 * Вход старта наряда. ADR-005 §9: старт кратен сменам, и номер смены
 * определяет, какое это действие — первичный старт (ADMITTED → IN_PROGRESS)
 * или «смену принял» заступающей смены (статус не меняется).
 *
 * Поле опционально: установленные сборки шлют пустой POST, и для них это
 * по-прежнему первая смена.
 */
export const StartWorkOrderSchema = z.object({
  shiftNumber: z.number().int().min(1).optional(),
});
export type StartWorkOrderDto = z.infer<typeof StartWorkOrderSchema>;

// ─── Д-3: ключи внешнего API для агента ──────────────────────────────────────

export const CreateApiKeySchema = z.object({
  /// Человекочитаемое имя: «агент HackAlem», «мониторинг». Нужно, чтобы
  /// администратор понимал, что он отзывает.
  name: z.string().min(1).max(120),
});
export type CreateApiKeyDto = z.infer<typeof CreateApiKeySchema>;

export const ApiKeySummarySchema = z.object({
  id: z.string(),
  name: z.string(),
  /// Открытый префикс. По нему ключ узнают в списке, не зная самого ключа.
  prefix: z.string(),
  createdAt: z.string(),
  lastUsedAt: z.string().nullable(),
  revokedAt: z.string().nullable(),
});
export type ApiKeySummaryResponse = z.infer<typeof ApiKeySummarySchema>;

export const IssuedApiKeySchema = z.object({
  id: z.string(),
  name: z.string(),
  prefix: z.string(),
  /// Полный ключ. Отдаётся ОДИН раз, при создании, и больше нигде не доступен:
  /// в базе лежит только его SHA-256.
  key: z.string(),
});
export type IssuedApiKeyResponse = z.infer<typeof IssuedApiKeySchema>;

/// Что внешний контур сообщает о себе предъявителю ключа. Ни работника, ни
/// роли здесь нет и быть не может: ключ принадлежит организации.
export const AgentIdentitySchema = z.object({
  organizationId: z.string(),
  organizationCode: z.string(),
  organizationName: z.string(),
  apiVersion: z.literal('v1'),
});
export type AgentIdentityResponse = z.infer<typeof AgentIdentitySchema>;

// ─── Д-4: чтение через внешний API для агента ────────────────────────────────

export const AgentWorkTypeSchema = z.object({
  id: z.string(),
  code: z.string(),
  name: z.string(),
  /// Работы повышенной опасности требуют Листа ежесменной оценки рисков
  /// (ADR-005 §4, пункт 11 Правил № 363).
  elevatedHazard: z.boolean(),
  /// Запись справочника не сверена специалистом по охране труда. Отдаётся
  /// наружу намеренно: потребитель обязан видеть, что основание не проверено.
  provisional: z.boolean(),
});
export type AgentWorkType = z.infer<typeof AgentWorkTypeSchema>;

export const AgentRequirementsSchema = z.object({
  workTypeId: z.string(),
  workTypeName: z.string().nullable(),
  /// MATRIX — требования из строки матрицы; LEGACY — прежняя логика.
  source: z.string(),
  /// Вид работы есть, а строки матрицы для него нет. Не отказ, а предупреждение.
  matrixMissing: z.boolean(),
  requiredElectricalGroup: z.string().nullable(),
  requiredBriefingTypes: z.array(z.string()),
  medicalExaminationRequired: z.boolean(),
  requiresObserver: z.boolean(),
  /// Типы допуска, по которым работнику нужен действующий документ.
  requiredClearanceTypes: z.array(z.object({ code: z.string(), name: z.string() })),
  /// Требования матрицы, под которые в организации не настроен тип допуска.
  /// Движок считает их отсутствующим допуском (fail-closed), поэтому наружу
  /// они видны отдельно от настроенных.
  unconfiguredRequirements: z.array(z.object({ code: z.string(), name: z.string() })),
});
export type AgentRequirements = z.infer<typeof AgentRequirementsSchema>;

export const AgentWorkerReadinessSchema = z.object({
  workerId: z.string(),
  employeeNumber: z.string().nullable(),
  workTypeId: z.string(),
  /**
   * Итог ворот готовности БЕЗ целевого инструктажа.
   *
   * Целевой инструктаж проводится по конкретному наряду и вне наряда не
   * существует, поэтому здесь он не проверяется и не может быть причиной
   * отказа. Вопрос, на который отвечает это поле: «мешает ли что-нибудь
   * включить работника в бригаду на такую работу» — не «можно ли ему прямо
   * сейчас приступить».
   */
  readyForCrew: z.boolean(),
  /// Коды причин, по которым работник не готов. Пусто, когда готов.
  reasons: z.array(z.string()),
});
export type AgentWorkerReadiness = z.infer<typeof AgentWorkerReadinessSchema>;

// ─── Дата журнала по времени организации ───────────────────────────────────

/**
 * Часовой пояс организации по умолчанию. Своего пояса у организации в модели
 * пока нет, поэтому дата журнала везде считается по нему (решение Штаба,
 * ночная проверка 21.10.2026).
 */
export const DEFAULT_ORGANIZATION_TIME_ZONE = 'Asia/Almaty';

/** Казахстан живёт по UTC+5 круглый год — запас на случай среды без поясов. */
const ALMATY_FALLBACK_OFFSET_MS = 5 * 3_600_000;

/**
 * Календарная дата момента по времени организации, `YYYY-MM-DD`.
 *
 * Это ДАТА ЖУРНАЛА — та, что стоит в журнале регистрации инструктажа, на
 * экране «Ждут вашей подписи», в сроке допуска и в отборе по периоду.
 * Повторный, проведённый 21.10.2026 в 02:45 по Астане, — инструктаж 21.10, а
 * не 20.10, как выходило по UTC: журнал предъявляют проверяющим.
 *
 * Не путать с `briefingDayOf`: та входит в подписанные байты актов наряда и
 * остаётся по UTC, чтобы прежние подписи сходились (ADR-004 §8).
 *
 * Среда без часовых поясов (старый движок телефона) получает UTC+5 — ровно
 * пояс Казахстана; для другого пояса такой подмены нет.
 */
export function localDayOf(at: Date | string, timeZone: string = DEFAULT_ORGANIZATION_TIME_ZONE): string {
  const moment = typeof at === 'string' ? new Date(at) : at;
  try {
    const parts = new Intl.DateTimeFormat('en-CA', {
      timeZone,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    }).formatToParts(moment);
    const part = (type: string) => parts.find((p) => p.type === type)?.value;
    const year = part('year');
    const month = part('month');
    const day = part('day');
    if (year && month && day) return `${year}-${month}-${day}`;
  } catch {
    // ниже — запасной путь
  }
  if (timeZone !== DEFAULT_ORGANIZATION_TIME_ZONE) {
    throw new Error(`Часовой пояс ${timeZone} не поддерживается средой`);
  }
  return new Date(moment.getTime() + ALMATY_FALLBACK_OFFSET_MS).toISOString().slice(0, 10);
}

// ─── Номер документа допуска на экране ─────────────────────────────────────

/**
 * Номер документа допуска для показа: «№ 123». У допуска, созданного записью
 * журнала инструктажа, номер уже читается целиком — «Запись журнала № 5 от
 * 21.10.2026», и второй «№» перед ним не ставится (ручная проверка PR #592).
 */
export function clearanceDocumentLabel(documentNumber: string): string {
  return documentNumber.startsWith('Запись журнала') ? documentNumber : `№ ${documentNumber}`;
}

// ─── Срок повторного инструктажа ───────────────────────────────────────────

/** Повторный инструктаж — не реже раза в шесть месяцев (Правила № 1019, п. 62). */
export const REPEAT_BRIEFING_MONTHS = 6;

/**
 * Срок повторного по дате «прошёл», `YYYY-MM-DD`: плюс шесть календарных
 * месяцев, тот же день; дня нет в месяце — последний день месяца (31.08 →
 * 28.02). Тем же правилом срок ставит журнал инструктажа на сервере
 * (`addCalendarMonths` в `worker-briefing-rules.ts`); панель подставляет его
 * при вводе повторного вместо «+180 суток» (ручная проверка PR #592).
 */
export function repeatBriefingDueDay(day: string): string {
  const [year, month, date] = day.split('-').map(Number);
  const targetMonthIndex = month - 1 + REPEAT_BRIEFING_MONTHS;
  const targetYear = year + Math.floor(targetMonthIndex / 12);
  const targetMonth = targetMonthIndex % 12;
  const lastDay = new Date(Date.UTC(targetYear, targetMonth + 1, 0)).getUTCDate();
  return `${targetYear}-${String(targetMonth + 1).padStart(2, '0')}-${String(Math.min(date, lastDay)).padStart(2, '0')}`;
}
